/*
 * Panier maison NAKED : composant <naked-cart>.
 *
 * Sommaire
 *   1. Moteur de cadeaux (fonctions pures, testées sous Node : NakedCartEngine)
 *   2. Outils (mémoire liée au panier, appels panier avec délai maximal)
 *   3. Composant : état, rendu, file d'exécution
 *   4. Détection des changements de panier (évènements du thème, fetch, XHR)
 *   5. Actions : quantités, cadeaux, code promo, recommandations, boîte cadeau, paiement
 *   6. Minuteur, confettis
 *   7. Compatibilité : window.NakedCart, évènements naked-cart:*
 *
 * Règles : aucun montant n'est formaté ici (tout vient du Liquid), à une exception près et documentée : le seuil
 * converti du panier VIDE (vague D, F8), entier, écrit dans le format appris d'un échantillon rendu par le Liquid ;
 * window.fetch est appelé au moment
 * de l'appel (les scripts de tests de prix et CRO l'enveloppent) ; quantités par cart/change.js avec la
 * clé de ligne ; jamais cart/clear.js ; jamais d'objet attributes complet ; le champ discount reprend
 * toujours les codes déjà posés, relus sur un panier frais.
 * Cadeaux : reconnus par identifiant de produit / variante (jamais par le type de produit, que Shopify
 * traduit) ; ajoutés par cart/update.js (quantité FIXÉE à 1, pas d'« ajout au panier » pour les pixels
 * ni pour le SDK CRO) ; jamais ajoutés ni gardés si leur prix n'est pas 0.
 */
(function (root) {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* 1. Moteur de cadeaux                                                 */
  /* ------------------------------------------------------------------ */

  var CORNER_PROP = '_corner_promo_code';

  /**
   * Seuil d'un palier, en centimes de la devise du panier.
   * Table du palier d'abord ; sinon seuil en euros converti au taux, arrondi à l'unité supérieure ;
   * sinon null. Un seuil nul, négatif ou illisible vaut null : palier non évaluable, jamais « 0 ».
   */
  function resolveThreshold(tier, currency, rate) {
    var table = (tier && tier.thresholds) || {};
    var listed = table[currency];
    if (typeof listed === 'number') return listed > 0 ? listed : null;
    if (typeof table.EUR === 'number' && table.EUR > 0 && typeof rate === 'number' && rate > 0) {
      return Math.ceil((table.EUR / 100) * rate) * 100;
    }
    return null;
  }

  function isCornerLine(item) {
    if (item.corner === true) return true;
    var props = item.properties;
    return !!(props && typeof props === 'object' && props[CORNER_PROP] != null && props[CORNER_PROP] !== '');
  }

  /**
   * Décide des ajouts et retraits de cadeaux. Fonction pure.
   *
   * @param {object} cart     panier (format /cart.js, ou « cart » de la configuration rendue par le Liquid) :
   *                          items[] { key, variant_id, product_id, quantity, final_line_price,
   *                          paid? (montant réellement payé, remises de commande comprises),
   *                          gift? (copie cadeau reconnue par le Liquid), corner? | properties },
   *                          currency, cart_level_discount_applications?
   * @param {object} config   { rate, tiers: [{ id, valid, available, productId, threshold?, converted?,
   *                          thresholds: {EUR, USD…}, variants: [{ id, available, price }] }] }
   * @param {string[]} declined  paliers dont la cliente a retiré le cadeau
   * @param {object} [chosen]    variante déjà choisie par palier (taille du body), { palier: id }
   * @returns {{base:number, reached:string[], hidden:string[], pending:string[], declined:string[],
   *            forget:string[], add:object[], remove:string[], setQuantity:object[]}}
   */
  function planGifts(cart, config, declined, chosen) {
    cart = cart || {};
    config = config || {};
    chosen = chosen || {};
    var items = cart.items || [];
    var currency = cart.currency || 'EUR';
    var declinedSet = {};
    (declined || []).forEach(function (id) { declinedSet[String(id)] = true; });

    var out = { base: 0, reached: [], hidden: [], pending: [], declined: [], forget: [], add: [], remove: [], setQuantity: [] };

    // Paliers. Un palier mal configuré (pas une copie cadeau, prix non nul, sans variante) est ignoré :
    // ses produits ne sont pas des cadeaux, ils se comportent comme des articles ordinaires.
    // « Gelé » (frozen) : seuil en euros saisi mais seuil local non résolu (taux manquant ou non fiable) :
    // le palier n'est pas évaluable, un cadeau déjà présent est GARDÉ. « Désactivé » (seuil vidé, table
    // fautive) : un cadeau présent est retiré.
    var tiers = (config.tiers || []).map(function (t) {
      var variants = (t.variants || []).filter(function (v) { return !(typeof v.price === 'number' && v.price !== 0); });
      var valid = t.valid !== false && variants.length > 0 && variants.length === (t.variants || []).length;
      var threshold = null;
      var frozen = t.frozen === true;
      if (valid) {
        if (typeof t.threshold === 'number') threshold = t.threshold > 0 ? t.threshold : null;
        else if (t.threshold === undefined) {
          threshold = resolveThreshold(t, currency, config.rate);
          var table = t.thresholds || {};
          if (threshold === null && typeof table.EUR === 'number' && table.EUR > 0 && typeof table[currency] !== 'number') frozen = true;
        }
      }
      return { id: String(t.id), tier: t, valid: valid, variants: variants, threshold: threshold, frozen: frozen };
    });
    var byVariant = {};
    var byProduct = {};
    tiers.forEach(function (x) {
      if (!x.valid) { out.hidden.push(x.id); return; }
      x.variants.forEach(function (v) { byVariant[String(v.id)] = x; });
      if (x.tier.productId != null) byProduct[String(x.tier.productId)] = x;
    });
    function tierOf(it) { return byVariant[String(it.variant_id)] || byProduct[String(it.product_id)] || null; }
    function isGift(it) { return it.gift === true || !!tierOf(it); }

    // Base : montant réellement payé pour les articles hors cadeaux (remises de ligne et de commande).
    // Protection colis (US, 16/10/2026) : jamais dans la base ni dans les pièces ; retirée hors US ou s'il ne reste qu'elle.
    var paidQty = 0;
    var hasPaid = false;
    var protect = config.protect || null;
    var protectKeys = [];
    var protectLines = [];
    items.forEach(function (it) {
      if (protect && String(it.product_id) === String(protect.pid)) { protectKeys.push(it.key); protectLines.push(it); return; }
      if (isCornerLine(it)) { out.remove.push(it.key); return; } // cadeau de l'ancienne app : remplacé
      if (isGift(it)) return;
      if (typeof it.paid === 'number') { out.base += it.paid; hasPaid = true; }
      else out.base += it.final_line_price;
      paidQty += it.quantity;
    });
    if (!hasPaid) {
      (cart.cart_level_discount_applications || []).forEach(function (d) {
        out.base -= Number(d.total_allocated_amount) || 0;
      });
    }
    if (out.base < 0) out.base = 0;
    if (protectKeys.length && (!protect.on || paidQty === 0)) protectKeys.forEach(function (key) { out.remove.push(key); });
    else if (protectLines.length > 1) {
      // Protection en 3 paliers (17/10/2026) : jamais deux lignes. La ligne du palier courant est gardée (sinon la première).
      var keep = protectLines.filter(function (it) { return protect.vid != null && String(it.variant_id) === String(protect.vid); })[0] || protectLines[0];
      protectLines.forEach(function (it) { if (it !== keep) out.remove.push(it.key); });
    }

    function isReached(x) { return paidQty > 0 && x.threshold !== null && out.base >= x.threshold; }

    // Lignes cadeaux présentes : retrait si prix non nul, cadeau indisponible, palier perdu, désactivé,
    // inconnu ou en double ; quantité 1. En double (deux tailles du body) : la taille choisie est gardée.
    var present = {};
    items.forEach(function (it) {
      if (isCornerLine(it) || !isGift(it)) return;
      var x = tierOf(it);
      if (!x) { out.remove.push(it.key); return; }
      if (it.final_line_price > 0) { out.remove.push(it.key); return; } // jamais un « cadeau » facturé
      var own = x.variants.filter(function (v) { return String(v.id) === String(it.variant_id); })[0];
      if (x.tier.available === false || (own && own.available === false)) { out.remove.push(it.key); return; }
      if (x.threshold === null) { if (!x.frozen || paidQty === 0) out.remove.push(it.key); return; }
      if (!isReached(x)) { out.remove.push(it.key); return; }
      var wanted = chosen[x.id] != null ? String(chosen[x.id]) : null;
      if (present[x.id]) {
        var kept = present[x.id];
        if (wanted && String(it.variant_id) === wanted && String(kept.variant_id) !== wanted) out.remove.push(kept.key);
        else { out.remove.push(it.key); return; }
      }
      present[x.id] = it;
    });
    Object.keys(present).forEach(function (id) {
      if (present[id].quantity !== 1) out.setQuantity.push({ key: present[id].key, quantity: 1 });
    });

    // Paliers atteints sans cadeau : ajout, sauf refus, taille à choisir ou cadeau indisponible.
    tiers.forEach(function (x) {
      if (!x.valid) return;
      if (x.threshold === null) return;
      var available = x.variants.filter(function (v) { return v.available !== false; });
      if (x.tier.available === false || !available.length) {
        if (!present[x.id]) { out.hidden.push(x.id); return; }
      }
      if (!isReached(x)) {
        if (declinedSet[x.id]) out.forget.push(x.id); // repasser sous le seuil efface le refus
        return;
      }
      out.reached.push(x.id);
      if (present[x.id]) return;
      if (declinedSet[x.id]) { out.declined.push(x.id); return; }
      var pick = available[0];
      if (x.variants.length > 1) {
        pick = available.filter(function (v) { return String(v.id) === String(chosen[x.id]); })[0];
        if (!pick) { out.pending.push(x.id); return; }
      }
      out.add.push({ id: pick.id, quantity: 1, tier: x.id });
    });

    return out;
  }

  /**
   * Protection colis en 3 paliers (Mathis, 17/10/2026 : « 4,90 $ de 120 à 160 $, 5,90 $ au-dessus ») : variante choisie sur le
   * TOTAL du panier hors protection, en cents (cart.total_price moins les lignes de protection). protect = { pid, vid, vids
   * (variantes disponibles triées par prix), cuts [12000, 16001] : seuils « à partir de », 160 $ pile reste à 4,90 $, D9 }. Même règle que le
   * Liquid. Renvoie l'id de variante, ou null.
   */
  function protectTier(cart, protect) {
    if (!protect) return null;
    var vids = protect.vids || [];
    if (!vids.length) return protect.vid != null ? Number(protect.vid) : null;
    var total = Number(cart && cart.total_price) || 0;
    ((cart && cart.items) || []).forEach(function (it) {
      if (String(it.product_id) === String(protect.pid)) total -= Number(it.final_line_price) || 0;
    });
    var index = 0;
    (protect.cuts || []).forEach(function (cut, i) { if (total >= Number(cut)) index = i + 1; });
    return Number(vids[Math.min(index, vids.length - 1)]);
  }

  /**
   * Variante de protection posée au départ (relecture du 17/10, D10) : le palier FIGÉ au clic quand la popup s'est ouverte (la cliente
   * paie le prix vu sur la ligne, même si la popup a fait grossir le panier) ; sinon le palier du total actuel ; sinon celle du rendu.
   */
  function protectChoice(frozen, cart, protect, shown) {
    if (frozen) return Number(frozen);
    if (protect && protect.vids && protect.vids.length && cart) return protectTier(cart, protect);
    return shown != null && shown !== '' ? Number(shown) : null;
  }

  /**
   * Départ vers le paiement : la variante du palier à 1, toute autre ligne de protection à 0, en UN cart/update.js. Avec la liste des
   * variantes (vids) : chaque variante par son id (1 pour celle du palier, 0 pour les autres), même si ce panier-ci ne la connaît pas
   * (QA du 17/10, lot 5 : un 2e onglet resté ouvert posait une 2e ligne ou n'en posait aucune). Sans liste : clés des lignes connues.
   * want false : tout à 0. lines [{ key, variant_id, quantity }].
   */
  function protectUpdates(lines, vid, want, vids) {
    var updates = {};
    if (vids && vids.length) {
      var known = {};
      vids.forEach(function (v) { known[String(v)] = true; updates[String(v)] = want && vid != null && String(v) === String(vid) ? 1 : 0; });
      (lines || []).forEach(function (line) { if (!known[String(line.variant_id)]) updates[line.key] = 0; });
      return updates;
    }
    var kept = false;
    (lines || []).forEach(function (line) {
      if (want && !kept && vid != null && String(line.variant_id) === String(vid)) {
        kept = true;
        if (Number(line.quantity) !== 1) updates[line.key] = 1;
        return;
      }
      updates[line.key] = 0;
    });
    if (want && !kept && vid != null) updates[String(vid)] = 1;
    return updates;
  }

  /**
   * Popup avant paiement (US, Mathis 17/10/2026 : « le but c'est lui faire dépenser le plus d'argent possible »). Fonction pure.
   * s = { on (US, interrupteur ou aperçu), group ('A' | 'B'), seen (déjà montrée, ou rappel cadeau montré, dans la session),
   * paid (pièces payées), pieces (pièces proposables dans sa taille), boxOn, pouchOn (en vente), boxIn, pouchIn (au panier) }.
   * 1 pièce payée : écran Pièces (une pièce PAYÉE de plus), puis Emballage. 2 pièces ou plus : jamais d'écran Pièces (une 3e pièce
   * offerte ne rapporte rien) ; seulement l'écran Emballage s'il manque la boîte ou le pochon. Rien à proposer : pas de popup.
   * Renvoie { show, pieces: 0 | 1, pack: '' | 'full' | 'box' | 'pouch' } ('box' : il manque la boîte au pack).
   */
  function planUpsell(s) {
    s = s || {};
    var out = { show: false, pieces: 0, tier: 0, pack: '' };
    if (!s.on || s.group !== 'B' || s.seen || !(s.paid > 0)) return out;
    if (s.paid === 1 && s.pieces > 0) out.pieces = 1;
    if (s.paid >= 2 && s.tier) out.tier = 1; // écran Palier (une pièce suffit au palier suivant, jamais une pièce que GLOW offrirait)
    if (s.boxOn && s.pouchOn) {
      if (!s.boxIn && !s.pouchIn) out.pack = 'full';
      else if (!s.boxIn) out.pack = 'box';
      else if (!s.pouchIn) out.pack = 'pouch';
    }
    out.show = out.pieces > 0 || out.tier > 0 || out.pack !== '';
    return out;
  }

  var SIZE_LIKE = /^(XXS|XS|S|M|L|XL|XXL|XXXL|\d+[A-Z]?|[A-Z]{1,3})$/i;

  /**
   * Pièces de l'écran Pièces (« Tap to add in your size ») : la variante disponible dans la taille de la cliente, toutes ses options
   * de taille égales à sa taille (ensemble : les deux). Taille inconnue : aucune pièce (rien n'est proposé sans sa taille).
   * D'abord celles à [min] cents et plus (le panier passe le palier du harnais), dans l'ordre de la collection. Au plus [max].
   * candidates [{ id, price, variants: [{ id, o: [], t }] }] ; renvoie [{ product, variant }].
   */
  function pickPieces(candidates, size, min, max) {
    var want = size ? String(size).trim().toUpperCase() : '';
    if (!want) return [];
    var out = [];
    (candidates || []).forEach(function (c) {
      var fit = (c.variants || []).filter(function (v) {
        var sized = (v && v.o || []).filter(function (x) { return SIZE_LIKE.test(String(x).trim()); });
        return v && v.id && sized.length > 0 && sized.every(function (x) { return String(x).trim().toUpperCase() === want; });
      })[0];
      if (fit) out.push({ product: c, variant: fit });
    });
    var high = function (x) { return min > 0 && Number(x.product.price) >= min; };
    out = out.filter(high).concat(out.filter(function (x) { return !high(x); }));
    return out.slice(0, max || 4);
  }

  /**
   * Cadeau gagné en ajoutant UNE pièce payée ([add] cents) : le palier le plus haut que la base atteint alors et que la cliente n'a pas
   * déjà, avec son nom et sa valeur. Sinon null. tiers [{ threshold (cents), reached, name, worth }].
   */
  function nextGift(tiers, base, add) {
    var after = Number(base) + Number(add);
    var best = null;
    (tiers || []).forEach(function (t) {
      if (!t || !(t.threshold > 0) || t.reached || !t.name || !t.worth) return;
      if (after >= t.threshold && (!best || t.threshold > best.threshold)) best = t;
    });
    return best;
  }

  /**
   * Ce qu'une pièce de [add] cents débloque sur un panier de base [base] (Mathis, 17/10 : « ultra personnalisation ») : le cadeau
   * du palier le plus haut atteint et pas encore acquis, et la livraison offerte si son seuil [shipThr] est franchi. Fonction pure.
   * tiers [{ threshold (cents), reached, name, worth }] ; renvoie { gift: palier | null, ship: bool }.
   */
  function unlocks(base, add, tiers, shipThr) {
    var after = Number(base) + Number(add);
    var gift = nextGift(tiers, base, add);
    var ship = Number(shipThr) > 0 && Number(base) < Number(shipThr) && after >= Number(shipThr);
    return { gift: gift, ship: !!ship };
  }

  /**
   * Ordre des pièces de l'écran Pièces : d'abord celles qui débloquent un cadeau (le plus haut d'abord), puis la livraison offerte,
   * puis les autres dans l'ordre reçu (déjà « 75 $ et plus d'abord »). Chaque pièce reçoit son « unlock ». Fonction pure.
   */
  function rankPieces(picks, base, tiers, shipThr) {
    var scored = (picks || []).map(function (pick, i) {
      var u = unlocks(base, pick.product.price, tiers, shipThr);
      var score = (u.gift ? 2000000 + Number(u.gift.threshold) : 0) + (u.ship ? 1000 : 0);
      return { pick: { product: pick.product, variant: pick.variant, unlock: u }, score: score, i: i };
    });
    scored.sort(function (a, b) { return b.score - a.score || a.i - b.i; });
    return scored.map(function (x) { return x.pick; });
  }

  /** Photo seule de l'écran A ou B (N7, relecture 3) : 1 200 px pour un écran 3x (la photo en paire reste en 640). Fonction pure. */
  function packBig(src) {
    return src ? String(src).replace(/([?&]width=)\d+/, '$11200') : src;
  }

  /**
   * Code du pack selon ce que la cliente a déjà (R7, coordinateur, 17/10) : pochon seul déjà pris (variante B) → GIFTPACKB, remise sur la
   * boîte (offCentsB) ; sinon (pack complet, variante A) → GIFTPACK, remise sur le pochon (offCents). Fonction pure.
   * Renvoie { code, off, b, all: [codes du pack en majuscules] }.
   */
  function packPick(pack, data, boxIn, pouchIn) {
    var b = !!(pouchIn && !boxIn);
    var all = [String((data && data.packCode) || '').toUpperCase(), String((data && data.packCodeB) || '').toUpperCase()].filter(Boolean);
    var code = b ? (data && data.packCodeB) || '' : (data && data.packCode) || '';
    var off = b ? Number(pack && pack.offCentsB != null ? pack.offCentsB : pack && pack.offCents) || 0 : Number(pack && pack.offCents) || 0;
    return { code: code, off: off, b: b, all: all };
  }

  /**
   * Verdict du pack face aux codes déjà posés (QA du 17/10, lot 4 ; faits API du 17/10 : NIGHTMARE25, WELCOME, avoirs ne se
   * combinent pas). Lu sur des paniers jetables de l'API Storefront, jamais deviné. Fonction pure.
   * before : codes du panier réel [{ code, applicable }] (hors GIFTPACK) ; withPack : codes du panier jetable « ses lignes + pack +
   * ses codes + GIFTPACK » ; p0, p2 : totaux (cents) de « ses lignes + ses codes » et « ses lignes + pack + ses codes » ;
   * full : prix plein de ce qui est ajouté (cents) ; pct : pourcentage de SON code (0 si montant fixe ou inconnu).
   * Renvoie { mode: 'normal' (prix du pack) | 'pct' (son code couvre le pack : cost) | 'none' (aucune offre), cost }.
   */
  function packVerdict(before, withPack, packCode, p0, p2, full, pct) {
    var want = String(packCode || '').toUpperCase();
    var probe = {};
    (withPack || []).forEach(function (c) { probe[String(c.code).toUpperCase()] = !!c.applicable; });
    var kept = (before || []).every(function (c) { return !c.applicable || probe[String(c.code).toUpperCase()] === true; });
    if (probe[want] && kept) return { mode: 'normal', cost: null };
    var cost = Number(p2) - Number(p0);
    if (Number(pct) > 0 && cost > 0 && cost < Number(full)) return { mode: 'pct', cost: cost };
    return { mode: 'none', cost: null };
  }

  /**
   * Écran Pièces (1 pièce payée, copy du 17/10) : version principale « Don't leave your [gift_value] gift behind. » seulement si le
   * panier est SOUS le 1er palier cadeau et qu'au moins 2 pièces (dans sa taille, en vente) le font passer, ET la livraison offerte,
   * calcul comme la jauge APRÈS son code en pourcentage (pct) ; on ne montre alors QUE ces pièces (« Gift unlocked » vrai à chaque
   * tap). Sinon version de repli, pièces dans l'ordre reçu. Au plus [max]. Fonction pure.
   * Renvoie { mode: 'main' | 'fallback', gift: palier | null, pieces: [...] }.
   */
  function piecesPlan(picks, base, tiers, shipThr, pct, max) {
    var first = (tiers || []).filter(function (t) { return t && t.threshold > 0 && !t.reached && t.name && t.worth; })
      .sort(function (a, b) { return a.threshold - b.threshold; })[0] || null;
    var keep = 1 - (Number(pct) > 0 ? Number(pct) / 100 : 0);
    if (first && Number(base) < first.threshold) {
      var passing = (picks || []).filter(function (pick) {
        var after = Number(base) + Math.round(Number(pick.product.price) * keep);
        return after >= first.threshold && (!(Number(shipThr) > 0) || after >= Number(shipThr));
      });
      // La marche la plus petite d'abord (coordinateur, 18/10/2026 : 13 écrans Pièces vus aux US, 0 pièce ajoutée) : prix croissant
      // parmi celles qui débloquent le cadeau ; l'écart (gap) part dans le sous-titre « Just $45 more ».
      passing = passing.slice().sort(function (a, b) { return Number(a.product.price) - Number(b.product.price); });
      if (passing.length >= 2) return { mode: 'main', gift: first, gap: first.threshold - Number(base), pieces: passing.slice(0, max || 4) };
    }
    return { mode: 'fallback', gift: null, pieces: (picks || []).slice(0, max || 4) };
  }

  /**
   * Écran Pièces v2 (Mathis, 25/10/2026 : « proposer couleur similaire un best seller mettre un badge FOMO ») : sa pièce dans une autre couleur
   * (kind « couleur », la plus vendue dans son pays), puis la meilleure vente du pays (kind « top »), puis le plan v1. Mêmes règles que
   * piecesPlan : en version principale, seules les pièces qui débloquent le cadeau (et la livraison) passent. Fonction pure.
   * colours : toutes les autres couleurs de son modèle dans sa taille (nuancier de la direction C).
   */
  function piecesV2(p1, picks, base, tiers, shipThr, pct) {
    var first = (tiers || []).filter(function (t) { return t && t.threshold > 0 && !t.reached && t.name && t.worth; })
      .sort(function (a, b) { return a.threshold - b.threshold; })[0] || null;
    var keep = 1 - (Number(pct) > 0 ? Number(pct) / 100 : 0);
    var ok = function (pick) {
      if (p1.mode !== 'main' || !first) return true;
      var after = Number(base) + Math.round(Number(pick.product.price) * keep);
      return after >= first.threshold && (!(Number(shipThr) > 0) || after >= Number(shipThr));
    };
    var colours = (picks || []).filter(function (pick) { return pick.product.kind === 'couleur' && ok(pick); })
      .sort(function (a, b) { return (Number(b.product.sold) || 0) - (Number(a.product.sold) || 0); });
    var sib = colours[0] || null;
    var top = (picks || []).filter(function (pick) { return pick.product.kind === 'top' && ok(pick) && (!sib || String(pick.product.id) !== String(sib.product.id)); })
      .sort(function (a, b) { return (Number(a.product.rank) || 99) - (Number(b.product.rank) || 99); })[0] || null;
    var used = {};
    [sib, top].forEach(function (pick) { if (pick) used[String(pick.product.id)] = true; });
    var rest = (p1.pieces || []).filter(function (pick) { return !used[String(pick.product.id)]; });
    var pieces = [sib, top].filter(Boolean).concat(rest).slice(0, 4);
    return { mode: p1.mode, gift: p1.gift, gap: p1.gap, pieces: pieces, colours: colours.slice(0, 6), v2: true };
  }

  /** Pack du jour (US, Mathis 25/10/2026 : « un compteur qui se termine à minuit […] l'offre exclusive d'aujourd'hui ») : secondes jusqu'à
   *  minuit, heure de la cliente ; jour local « AAAA-MM-JJ » ; durée « HH:MM:SS ». Fonctions pures. */
  function untilMidnight(now) {
    var d = new Date(now == null ? Date.now() : now);
    var end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0);
    return Math.max(0, Math.floor((end - d) / 1000));
  }
  function dayKey(now) {
    var d = new Date(now || Date.now());
    return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
  }
  function hms(rest) {
    var hh = Math.floor(rest / 3600);
    var mm = Math.floor((rest % 3600) / 60);
    var ss = rest % 60;
    return (hh < 10 ? '0' : '') + hh + ':' + (mm < 10 ? '0' : '') + mm + ':' + (ss < 10 ? '0' : '') + ss;
  }
  /** Jours entiers entre deux jours locaux « AAAA-MM-JJ » (b − a). */
  function daysBetween(a, b) { return Math.round((Date.parse(b) - Date.parse(a)) / 864e5); }

  /** Montant des pièces avec GLOW (BXGY 2+1, répété : la moins chère offerte par tranche de 3) ou sans. Prix unitaires d'origine. */
  function glowSum(prices, glowOn) {
    var list = (prices || []).map(Number).sort(function (a, b) { return a - b; });
    var total = list.reduce(function (sum, p) { return sum + p; }, 0);
    if (glowOn) for (var i = 0; i < Math.floor(list.length / 3); i += 1) total -= list[i];
    return total;
  }

  /** Base des paliers après l'ajout d'une pièce de [add] cents : GLOW recalculé, puis SON code en pourcentage (calcul de la jauge). */
  function baseWithPiece(base, prices, add, glowOn, pct) {
    var keep = 1 - (Number(pct) > 0 ? Number(pct) / 100 : 0);
    var delta = glowSum((prices || []).concat([Number(add)]), glowOn) - glowSum(prices || [], glowOn);
    return Number(base) + Math.round(delta * keep);
  }

  /**
   * Écran Palier (Mathis, 17/10 : « s'il est proche d'un palier cadeau tu marques c'est dommage, il vous reste tant à dépenser ») :
   * 2 pièces payées ou plus, une seule pièce dans sa taille suffit à passer le palier suivant (calcul de la jauge, après code) ; on ne
   * montre QUE ces pièces (cadeau vraiment débloqué au tap). Jamais quand GLOW rendrait la pièce ajoutée gratuite (nouveau nombre de
   * pièces multiple de 3 : 2 → 3, 5 → 6). prices : prix unitaires d'origine des pièces payées. Fonction pure.
   * Renvoie { show, gift, gap (cents), pieces }.
   */
  function tierPlan(picks, base, prices, tiers, glowOn, pct, max) {
    var none = { show: false, gift: null, gap: 0, pieces: [] };
    var paid = (prices || []).length;
    if (paid < 2) return none;
    if (glowOn && (paid + 1) % 3 === 0) return none;
    var next = (tiers || []).filter(function (t) { return t && t.threshold > Number(base) && !t.reached && t.name && t.worth; })
      .sort(function (a, b) { return a.threshold - b.threshold; })[0];
    if (!next) return none;
    var passing = (picks || []).filter(function (pick) { return baseWithPiece(base, prices, pick.product.price, glowOn, pct) >= next.threshold; });
    if (!passing.length) return none;
    return { show: true, gift: next, gap: next.threshold - Number(base), pieces: passing.slice(0, max || 4) };
  }

  /** Total d'un panier de l'API Storefront, en cents, hors taxe : lignes après remises de ligne, moins les remises de commande. */
  function storefrontTotal(cart) {
    if (!cart) return null;
    var cents = function (m) { return Math.round(Number(m && m.amount || 0) * 100); };
    var total = 0;
    (((cart.lines || {}).nodes) || []).forEach(function (l) { total += cents(l.cost && l.cost.totalAmount); });
    (cart.discountAllocations || []).forEach(function (d) { total -= cents(d.discountedAmount); });
    return total;
  }

  root.NakedCartEngine = {
    planGifts: planGifts, resolveThreshold: resolveThreshold, CORNER_PROP: CORNER_PROP,
    protectTier: protectTier, protectChoice: protectChoice, protectUpdates: protectUpdates, planUpsell: planUpsell, pickPieces: pickPieces, nextGift: nextGift,
    unlocks: unlocks, rankPieces: rankPieces, piecesPlan: piecesPlan, glowSum: glowSum, baseWithPiece: baseWithPiece, tierPlan: tierPlan,
    packVerdict: packVerdict, packPick: packPick, packBig: packBig, storefrontTotal: storefrontTotal
  };

  if (typeof document === 'undefined' || typeof customElements === 'undefined') return;
  if (customElements.get('naked-cart')) return;

  /* ------------------------------------------------------------------ */
  /* 2. Outils                                                            */
  /* ------------------------------------------------------------------ */

  var SECTION = 'naked-cart';
  var SELF_MARK = 'nkc=1';
  var CART_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/i;
  var MAX_QTY = 99;
  var KEEP_DAYS = 14;
  var TIMER_RESET = 6 * 3600000;

  function timeout() { return Number(root.NakedCartTimeout) > 0 ? Number(root.NakedCartTimeout) : 10000; }

  /**
   * Mémoire liée au jeton du panier : refus de cadeau, minuteur, palier fêté, taille choisie.
   * localStorage (commun aux onglets) doublé d'une copie en mémoire : si le stockage est bloqué,
   * la copie en mémoire suffit pour la page.
   */
  var store = {
    token: null,
    memory: {},
    name: function () { return 'nkc:' + (this.token || '-'); },
    read: function () {
      var name = this.name();
      try {
        var raw = root.localStorage.getItem(name);
        if (raw) { var data = JSON.parse(raw); if (data && typeof data === 'object') return data; }
        return this.memory[name] || {};
      } catch (e) { /* stockage indisponible : mémoire, puis cookie */ }
      return this.memory[name] || this.fromCookie(name) || {};
    },
    /**
     * Stockage bloqué (localStorage inaccessible) : l'état tient dans un cookie de la boutique, pour
     * survivre au changement de page. Le panier Shopify lui-même a besoin des cookies : s'ils sont
     * refusés aussi, il n'y a pas de panier à protéger. Rien n'est écrit dans les attributs du panier.
     */
    fromCookie: function (name) {
      try {
        var match = /(?:^|;\s*)nkc_state=([^;]+)/.exec(document.cookie || '');
        if (!match) return null;
        var saved = JSON.parse(decodeURIComponent(match[1]));
        return saved && saved.n === name && saved.d && typeof saved.d === 'object' ? saved.d : null;
      } catch (e) { return null; }
    },
    toCookie: function (name, data) {
      try {
        document.cookie = 'nkc_state=' + encodeURIComponent(JSON.stringify({ n: name, d: data })) +
          '; path=/; max-age=' + (KEEP_DAYS * 86400) + '; SameSite=Lax';
      } catch (e) { /* cookies refusés aussi : mémoire seule */ }
    },
    get: function (key, fallback) {
      var data = this.read();
      return Object.prototype.hasOwnProperty.call(data, key) ? data[key] : fallback;
    },
    set: function (key, value) {
      var name = this.name();
      var data = this.read();
      data[key] = value;
      data.at = Date.now();
      this.memory[name] = data;
      try { root.localStorage.setItem(name, JSON.stringify(data)); } catch (e) { this.toCookie(name, data); }
    },
    /** Le jeton du panier est connu (cookie, puis /cart.js) : l'état noté avant suit le panier. */
    bind: function (token) {
      token = token ? String(token).split('?')[0] : null;
      if (!token || token === this.token) return;
      var before = this.token ? null : (this.memory['nkc:-'] || this.fromCookie('nkc:-'));
      this.token = token;
      if (before && !Object.keys(this.read()).length) {
        var self = this;
        Object.keys(before).forEach(function (key) { if (key !== 'at') self.set(key, before[key]); });
      }
    },
    /** Paniers oubliés : les entrées de plus de 14 jours sont effacées. */
    sweep: function () {
      try {
        var limit = Date.now() - KEEP_DAYS * 86400000;
        for (var i = root.localStorage.length - 1; i >= 0; i -= 1) {
          var name = root.localStorage.key(i);
          if (!name || name.indexOf('nkc:') !== 0) continue;
          var data = JSON.parse(root.localStorage.getItem(name) || '{}');
          if (!data.at || data.at < limit) root.localStorage.removeItem(name);
        }
      } catch (e) { /* stockage indisponible */ }
    }
  };

  function cookieToken() {
    try {
      var match = /(?:^|;\s*)cart=([^;]+)/.exec(document.cookie || '');
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) { return null; }
  }

  /**
   * Code posé par un lien /discount/CODE : Shopify le garde dans le cookie « discount_code » (lisible par
   * script) et le réapplique à la création du paiement, même si le panier ne le porte plus. Le panier et
   * le paiement doivent dire la même chose : un code retiré dans le panier est aussi retiré du cookie, et
   * un code du cookie absent du panier y est rattaché (il est alors affiché et compte dans la base).
   */
  function linkCode() {
    try {
      var match = /(?:^|;\s*)discount_code=([^;]*)/.exec(document.cookie || '');
      var code = match ? decodeURIComponent(match[1]).trim() : '';
      return code || null;
    } catch (e) { return null; }
  }

  function dropLinkCode() {
    try {
      var host = root.location.hostname;
      ['', '; domain=' + host, '; domain=.' + host].forEach(function (scope) {
        document.cookie = 'discount_code=; path=/' + scope + '; max-age=0';
      });
    } catch (e) { /* cookies inaccessibles */ }
  }

  function routeRoot() {
    return (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
  }

  /** Valeur sûre dans un sélecteur d'attribut (CSS.escape absent de certains environnements). */
  function esc(value) {
    if (root.CSS && typeof root.CSS.escape === 'function') return root.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function mark(url) {
    return url + (url.indexOf('?') > -1 ? '&' : '?') + SELF_MARK;
  }

  /**
   * fetch « discret » pour les seuls ajouts de cadeaux : celui d'un cadre vide de la même origine, que les
   * scripts de la page n'ont pas enveloppé. Sans cela, le suivi Shopify (« Added Product », donc l'évènement
   * product_added_to_cart des pixels Meta, Klaviyo…) et le SDK CRO comptent chaque cadeau comme un ajout au
   * panier fait par la cliente. Indisponible : repli sur window.fetch (le cadeau est ajouté quand même).
   */
  var quietFrame = null;
  function quietWindow() {
    try {
      if (!quietFrame) {
        quietFrame = document.createElement('iframe');
        quietFrame.hidden = true;
        quietFrame.tabIndex = -1;
        quietFrame.setAttribute('aria-hidden', 'true');
        quietFrame.setAttribute('data-nkc-quiet', '');
        (document.body || document.documentElement).appendChild(quietFrame);
      }
      var win = quietFrame.contentWindow;
      return win && typeof win.fetch === 'function' ? win : null;
    } catch (e) { return null; }
  }

  /** fetch avec délai maximal : une requête sans réponse est abandonnée (la file ne reste jamais bloquée). */
  function timedFetch(url, init, quiet) {
    var win = quiet ? quietWindow() : null;
    var Controller = win ? win.AbortController : (typeof AbortController === 'function' ? AbortController : null);
    var controller = typeof Controller === 'function' ? new Controller() : null;
    if (controller) init.signal = controller.signal;
    var timer;
    var limit = new Promise(function (resolve, reject) {
      timer = setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error('nkc-timeout'));
      }, timeout());
    });
    var done = function () { clearTimeout(timer); };
    var call = win ? win.fetch(new URL(url, root.location.href).href, init) : root.fetch(url, init);
    call.then(done, done);
    return Promise.race([call, limit]);
  }

  /**
   * Écritures panier en vol (cart/update.js, cart/change.js, cart/add.js passés par cartApi). Correctif
   * paiement (07/10) : le clic « Valider ma commande » n'attend que ces RÉPONSES (le panier Shopify est la
   * vérité), jamais le re-rendu de la section ni les recommandations, et 1 200 ms au plus.
   */
  var writes = 0;
  function writing() { return writes > 0; }
  var PAY_HOLD_MS = 1200;
  var upArrived = false; // aperçu de la popup (?nkc_upsell=1) : arrivée sur la page déjà lue

  /** Appel de l'API panier. window.fetch est lu ici, à chaque appel (sauf « quiet » : ajout de cadeau). */
  function cartApi(path, body, quiet) {
    var init = { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } };
    if (body) {
      init.method = 'POST';
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
      writes += 1;
    }
    var release = function () { if (body) writes = Math.max(0, writes - 1); };
    return timedFetch(mark(routeRoot() + path), init, quiet).then(function (res) {
      release();
      return Promise.race([
        res.json().catch(function () { return {}; }),
        new Promise(function (resolve, reject) { setTimeout(function () { reject(new Error('nkc-timeout')); }, timeout()); })
      ]).then(function (data) {
        return { ok: res.ok, status: res.status, data: data };
      });
    }, function (err) { release(); throw err; });
  }

  /**
   * Évènement du panier : CustomEvent « naked-cart:<nom> » pour les scripts de la page, et, pour la mesure
   * (cro-ARBITRAGE n° 1), Shopify.analytics.publish('naked_cart_<nom>', résumé) lisible par un pixel personnalisé
   * (analytics.subscribe). Le résumé ne porte jamais l'objet panier : total, nombre d'articles, devise, pays, hors France,
   * plus le détail propre à l'évènement. Jamais émis pour l'ajout automatique d'un cadeau.
   */
  function fire(name, detail) {
    detail = detail || {};
    document.dispatchEvent(new CustomEvent('naked-cart:' + name, { bubbles: true, detail: detail }));
    try {
      var analytics = root.Shopify && root.Shopify.analytics;
      if (!analytics || typeof analytics.publish !== 'function') return;
      var el = typeof document !== 'undefined' ? document.querySelector('naked-cart') : null;
      var cart = detail.cart || (el && el.getCart && el.getCart()) || {};
      var config = (el && el._config) || {};
      var slim = { total_price: cart.total_price, item_count: cart.item_count, currency: cart.currency || config.currency, country: config.country || null, abroad: config.abroad === true };
      Object.keys(detail).forEach(function (key) { if (key !== 'cart') slim[key] = detail[key]; });
      analytics.publish('naked_cart_' + name, slim);
    } catch (e) { /* mesure indisponible : le panier n'en dépend pas */ }
  }

  /**
   * Montant en CENTS écrit dans le format d'un échantillon rendu par le Liquid (« $1,234,567 » ou « $20 ») : préfixe et suffixe repris,
   * point décimal, « .00 » retiré (comme money_without_trailing_zeros). Seul usage : le prix du pack sous le code en pourcentage de la
   * cliente (popup US, 17/10/2026), calculé sur un panier jetable. Échantillon illisible : « $ » devant.
   */
  function centsMoney(cents, sample) {
    var s = String(sample || '$');
    var m = /^([^\d]*)[\d.,\s]*([^\d]*)$/.exec(s);
    var pre = m ? m[1] : '$';
    var suf = m ? m[2] : '';
    var units = (Math.round(Number(cents) || 0) / 100).toFixed(2).replace(/\.00$/, '');
    return pre + units + suf;
  }

  /** Taux Shopify de la devise affichée (devise de la boutique = 1), ou null. */
  function liveRate(currency) {
    var cur = root.Shopify && root.Shopify.currency;
    if (!cur || !currency || String(cur.active) !== String(currency)) return null;
    var rate = Number(cur.rate);
    return rate > 0 ? rate : null;
  }

  /**
   * Montant ENTIER (unités de la devise) écrit dans le format du Liquid, appris d'un échantillon rendu par
   * « 123456700 | money_without_trailing_zeros » (« 1.234.567 kr », « £1,234,567 », « CHF 1'234'567ʼ ») :
   * préfixe avant le 1, séparateur de milliers entre 1 et 2, suffixe après le 7. Seul usage : le seuil
   * converti du panier vide (F8). Échantillon illisible : le nombre nu.
   */
  function moneyUnits(units, sample) {
    var s = String(sample || '');
    var i1 = s.indexOf('1');
    var i2 = s.indexOf('2');
    var i7 = s.lastIndexOf('7');
    var digits = String(Math.max(0, Math.round(units)));
    if (i1 < 0 || i2 < i1 || i7 < i2) return digits;
    var sep = s.slice(i1 + 1, i2);
    return s.slice(0, i1) + digits.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + s.slice(i7 + 1);
  }

  /**
   * Jauge-devises (08/10) : seuil sous la vignette compacté quand le symbole est en lettres, même règle que le Liquid
   * (nkc:compact) : « Dhs. 421 » → « 421 AED », « CHF 100 » → « 100 CHF » (code ISO), « 759 kr. » → « 759 kr », « 450 zł »
   * inchangé ; « $120 », « 100€ », « €100 », « £90 », « R$120 » intacts. Le préfixe et le suffixe sont lus sur l'échantillon.
   * Seul usage : le gabarit du panier vide (data-nkc-compact), le Liquid ayant déjà compacté les autres seuils.
   */
  function moneyCompact(text, sample, iso) {
    var s = String(sample || '');
    var t = String(text || '');
    var i1 = s.indexOf('1');
    var i7 = s.lastIndexOf('7');
    if (i1 < 0 || i7 < i1) return t;
    var pre = s.slice(0, i1);
    var suf = s.slice(i7 + 1);
    var preSym = pre.trim().replace(/\./g, '');
    var sufSym = suf.trim().replace(/\./g, '');
    var letters = /^[^\d\s$€£¥]+$/;
    var mode = preSym.length > 1 && letters.test(preSym) ? 'iso' : sufSym && letters.test(sufSym) ? 'suf' : '';
    if (!mode) return t;
    var num = t.trim();
    if (pre && num.indexOf(pre) === 0) num = num.slice(pre.length);
    if (suf && num.slice(-suf.length) === suf) num = num.slice(0, -suf.length);
    num = num.trim();
    return num + ' ' + (mode === 'iso' ? String(iso || '').toUpperCase() : sufSym);
  }

  /* ------------------------------------------------------------------ */
  /* 3. Composant                                                         */
  /* ------------------------------------------------------------------ */

  class NakedCartElement extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.mode = this.getAttribute('mode') === 'page' ? 'page' : 'drawer';
      this.root = this.querySelector('[data-nkc-root]');
      this.panel = this.querySelector('.nkc-panel') || this;
      this._queue = Promise.resolve();
      this._cart = null;
      this._recoCache = {};
      this._failed = {};
      this._placing = {}; // cadeaux en cours de pose (écriture partie, rendu pas encore arrivé) : jamais « oubliés » (25/10/2026)
      this._qtyTimers = {};
      this._rateSends = 0;
      this._linked = {};
      this._lastSync = Date.now();
      this._skipReminder = false;
      this._inflight = 0;
      this._closedAt = 0;
      this._earlyAt = 0;
      this._openedAt = 0;
      this._recoPicks = {};
      this._undo = null;
      this._lastCause = null;
      this._lastCode = null;
      this._pushed = false;
      this._config = this._readConfig(this.root);

      store.bind(cookieToken());
      store.sweep();

      // Le natif reste dans le thème (retour arrière = interrupteur) : il est retiré ici, à l'exécution.
      document.querySelectorAll('cart-drawer, cart-notification-drawer').forEach(function (el) { el.remove(); });

      if (this.mode === 'page') this._place();

      this.addEventListener('click', this._onClick.bind(this));
      this.addEventListener('change', this._onChange.bind(this));
      this.addEventListener('submit', this._onSubmit.bind(this));
      this.addEventListener('keydown', this._onKeydown.bind(this));

      // Retour arrière du téléphone : le tiroir ouvert a posé une entrée d'historique, le geste retour la
      // dépile et ferme le tiroir au lieu de quitter la fiche. Une entrée restée d'une page précédente est effacée.
      if (this.mode === 'drawer') {
        var self = this;
        try { if (root.history && root.history.state && root.history.state.nkc) root.history.replaceState(null, ''); } catch (e) { /* historique indisponible */ }
        root.addEventListener('popstate', function () {
          if (self.hasAttribute('open')) { self._pushed = false; self.close(true); }
        });
      }

      this._afterRender({ initial: true });

      // Palier déjà atteint au chargement : le cadeau est ajouté sans attendre un changement de panier.
      // Code de lien (/discount/…) présent : le panier est relu pour vérifier qu'il le porte bien.
      var plan = this._plan(this._config);
      var linked = linkCode() && this._config && this._config.cart && this._config.cart.item_count > 0;
      // Attribut de taux résiduel (devise changée) : effacé par une relecture.
      var staleRate = this._config && this._config.rate === null && !this._config.needRate && !!this._config.rateAttr;
      if (this._hasWork(plan) || this._rateWanted(this._config) || linked || staleRate) this.refresh();
      // Sinon : un code attaché sans effet (GLOW sous trois pièces, code refusé) n'est connu que de cart.js (vague D, F2).
      else this._primeCodes();
    }

    /**
     * Premier rendu (vague D, F2) : le JSON du Liquid ne porte pas les codes attachés sans effet (cart.discount_codes
     * n'existe qu'en Ajax ; discount_applications ne liste que les remises appliquées). Sans relecture, la consigne
     * GLOW et le lien « Retirer » n'apparaissaient qu'au prochain changement du panier, et le champ replié hors France
     * restait fermé. Une lecture de cart.js (jamais d'écriture), panier non vide ou code refusé en mémoire ; sans effet
     * si un rendu complet est passé entre-temps.
     */
    _primeCodes() {
      var self = this;
      var config = this._config;
      if (!config || !config.cart) return;
      if (!(config.cart.item_count > 0) && !store.get('refused', []).length) return;
      cartApi('cart.js').then(function (res) {
        if (self._cart || !res.ok || !res.data || !res.data.items) return;
        if (res.data.token) store.bind(res.data.token);
        self._cart = res.data;
        self._pendingCodes();
        self._autoCode();
        self._sweepRefused();
      }).catch(function () { /* relecture impossible : les codes apparaîtront au prochain changement du panier */ });
    }

    disconnectedCallback() {
      clearInterval(this._dailyTick);
      clearInterval(this._upMidTimer);
      clearInterval(this._tick);
      if (!document.querySelector('naked-cart[open]')) document.documentElement.classList.remove('nkc-lock');
    }

    /** Page /cart : le panier prend la place du panier natif (masqué par la section), dans <main>. */
    _place() {
      var native = document.querySelector('.shopify-section--main-cart');
      var main = document.getElementById('main') || document.querySelector('main');
      document.querySelectorAll('naked-cart[mode="page"]').forEach(function (other) {
        if (other !== this) other.remove(); // éditeur de thème : un seul panier en mode page
      }, this);
      if (native && native.parentNode) native.parentNode.insertBefore(this, native);
      else if (main) main.insertBefore(this, main.firstChild);
      // Le natif est masqué ici aussi : si le script arrive après la minuterie de la section (réseau lent),
      // le style qui le masquait a été retiré pour ne jamais laisser la page vide.
      if (native) native.hidden = true;
      this.setAttribute('data-placed', '');
    }

    /** Texte d'erreur qui nomme l'action échouée (retrait, ajout, code, cadeau) ; repli sur le texte général. */
    _errorText(context) {
      var strings = (this._config && this._config.strings) || {};
      var key = { remove: 'errorRemove', add: 'errorAdd', code: 'errorCode', gift: 'errorGift' }[context];
      return (key && strings[key]) || strings.error || '';
    }

    /**
     * Conteneur qui défile : la liste, ou le corps entier quand l'écran est peu haut (téléphone couché,
     * zoom fort : le CSS fait alors défiler [data-nkc-body], en-tête et paiement collants).
     */
    _scroller() {
      var body = this.root.querySelector('[data-nkc-body]');
      if (this.mode === 'drawer' && body && root.getComputedStyle && root.getComputedStyle(body).overflowY === 'auto') return body;
      return this.root.querySelector('[data-nkc-scroll]');
    }

    /* ---------- État ---------- */

    getCart() {
      return this._cart || (this._config && this._config.cart) || null;
    }

    _readConfig(scope) {
      var node = scope && scope.querySelector('[data-nkc-config]');
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (e) { return null; }
    }

    _declined() {
      return store.get('declined', []).concat(Object.keys(this._failed));
    }

    _decline(tier) {
      var list = store.get('declined', []).filter(function (id) { return String(id) !== String(tier); });
      list.push(String(tier));
      store.set('declined', list);
    }

    _undecline(tiers) {
      var drop = {};
      tiers.forEach(function (id) { drop[String(id)] = true; });
      var list = store.get('declined', []);
      var kept = list.filter(function (id) { return !drop[String(id)]; });
      if (kept.length !== list.length) store.set('declined', kept);
    }

    /**
     * Le taux transmis au Liquid n'est accepté que s'il correspond au taux Shopify de la devise affichée
     * (écart toléré 2 %). Sinon les paliers convertis sont gelés (non évaluables) le temps de le renvoyer.
     */
    _rateTrusted(config) {
      if (!config || !config.rate) return true;
      var live = liveRate(config.currency);
      return !!live && Math.abs(config.rate - live) / live <= 0.02;
    }

    _rateWanted(config) {
      if (!config || !liveRate(config.currency)) return false;
      return !!config.needRate || !this._rateTrusted(config);
    }

    _plan(config) {
      if (!config || !config.gifts) return null;
      var view = config;
      if (!this._rateTrusted(config)) {
        view = Object.assign({}, config, {
          tiers: config.tiers.map(function (t) { return t.converted ? Object.assign({}, t, { threshold: null, frozen: true }) : t; })
        });
      }
      var plan = planGifts(view.cart, view, this._declined(), store.get('sizes', {}));
      // Un refus n'est effacé que si le palier n'est plus atteint (jamais parce que le cadeau est encore
      // dans un panier lu avant le clic de la cliente).
      if (plan.forget.length) this._undecline(plan.forget);
      return plan;
    }

    _hasWork(plan) {
      return !!plan && (plan.add.length > 0 || plan.remove.length > 0 || plan.setQuantity.length > 0);
    }

    /* ---------- File d'exécution : une seule opération panier à la fois ---------- */

    _enqueue(task, context, write) {
      var self = this;
      this._pending = (this._pending || 0) + 1;
      // Tâche qui ÉCRIT (quantité, cadeau, code, ajout) : le paiement l'attend (1 200 ms au plus) ; un simple
      // rechargement (refresh) ne retient pas le paiement au-delà de ses écritures anticipées.
      var writeTask = !!context || write === true;
      if (writeTask) this._queuedWrites = (this._queuedWrites || 0) + 1;
      this.setAttribute('busy', '');
      var run = function () {
        return Promise.resolve().then(function () { return self._leaveAll ? null : task(); }).catch(function () {
          return self._recover(context);
        }).then(function () {
          self._pending -= 1;
          if (writeTask) self._queuedWrites = Math.max(0, (self._queuedWrites || 0) - 1);
          if (self._pending === 0) self._idle();
        });
      };
      this._queue = this._queue.then(run, run);
      return this._queue;
    }

    /**
     * Opération en échec (délai dépassé, réseau) : l'état réel du panier est relu et réaffiché, avec le
     * message d'erreur. Si la relecture échoue aussi, l'affichage revient au dernier état connu : une
     * quantité annoncée au clic ne reste jamais à l'écran sans avoir été confirmée.
     */
    _recover(context) {
      var self = this;
      var message = this._errorText(context);
      return this._fetchState()
        .then(function (state) { return self._settle(state, 0, { notice: message, error: true }); })
        .catch(function () { self._revert(); self._notice(message, false, true); });
    }

    _revert() {
      var known = {};
      ((this._config && this._config.cart && this._config.cart.items) || []).forEach(function (item) { known[item.key] = item.quantity; });
      this.root.querySelectorAll('[data-nkc-line]').forEach(function (line) {
        var quantity = known[line.getAttribute('data-key')];
        line.removeAttribute('data-pending');
        line.removeAttribute('data-removing');
        if (quantity === undefined) return;
        line.setAttribute('data-qty', quantity);
        var input = line.querySelector('[data-nkc-qty-input]');
        if (input) input.value = quantity;
      });
    }

    /** Fin de la file. Un ajout extérieur encore en vol (openEarly) garde l'état de chargement et le fantôme. */
    _idle() {
      if (this._inflight > 0) return;
      this.removeAttribute('busy');
      clearTimeout(this._earlyTimer);
      this.root.querySelectorAll('[data-nkc-ghost]').forEach(function (node) { node.remove(); });
      this.root.querySelectorAll('[aria-busy]').forEach(function (node) { node.removeAttribute('aria-busy'); });
      this._upSync(true); // popup ouverte : un ajout resté absent du panier est dit refusé, le bouton revient
    }

    _sectionUrl() {
      return routeRoot() + (this.mode === 'page' ? 'cart' : '');
    }

    _fetchState(early) {
      var self = this;
      var sectionUrl = mark(this._sectionUrl() + '?sections=' + SECTION);
      var cartRead = cartApi('cart.js');
      // Sans en-tête Accept JSON : sur /cart, Shopify renverrait le panier au lieu des sections.
      var sectionRead = timedFetch(sectionUrl, { credentials: 'same-origin', cache: 'no-store' }).then(function (res) { return res.json(); });
      // Correctif paiement (07/10) : après un ajout, le plan de cadeaux part dès la lecture de /cart.js, en
      // parallèle de la lecture de section (et non après elle). Le paiement n'attend que la réponse de l'écriture.
      var planned = Promise.resolve(false);
      if (early) {
        // _deciding a été posé par refresh() (synchrone) : levé dès que le plan est décidé (écriture partie ou rien à faire).
        var decided = function (done) { self._deciding = Math.max(0, (self._deciding || 0) - 1); return done; };
        planned = cartRead.then(function (res) { return self._earlyPlan(res.data); }, function () { return false; }).then(decided, function () { return decided(false); });
      }
      return Promise.all([cartRead, sectionRead, planned]).then(function (triple) {
        return { cart: triple[0].data, html: triple[1][SECTION], early: triple[2] };
      });
    }

    /**
     * Plan de cadeaux calculé sur /cart.js seul, avec la dernière configuration rendue (paliers, devise, taux).
     * N'écrit que si la vue reconstruite est cohérente avec le total Shopify (sinon le plan est laissé à la
     * section, comme avant). Renvoie true si une écriture a eu lieu : la section lue en parallèle est alors
     * périmée et _settle la relit une fois (un seul rendu après ajout + cadeaux).
     */
    _earlyPlan(cart) {
      var config = this._config;
      if (!config || !config.gifts || !cart || !cart.items || !cart.items.length) return false;
      var view = this._viewFromCartJs(cart, config);
      if (!view) return false;
      var plan = this._plan(Object.assign({}, config, { cart: view }));
      if (!this._hasWork(plan)) return false;
      this._earlyTiers = plan.add.map(function (item) { return item.tier; });
      return this._execute(plan).then(function () { return true; }, function () { return true; });
    }

    _viewFromCartJs(cart, config) {
      var sum = 0;
      var items = [];
      for (var i = 0; i < cart.items.length; i++) {
        var it = cart.items[i];
        var paid = Number(it.original_line_price != null ? it.original_line_price : it.final_line_price);
        if (!isFinite(paid)) return null;
        (it.discounts || []).forEach(function (d) { paid -= Number(d.amount) || 0; });
        if (paid > Number(it.final_line_price)) paid = Number(it.final_line_price);
        if (paid < 0) paid = 0;
        sum += paid;
        items.push({ key: it.key, variant_id: it.variant_id, product_id: it.product_id, quantity: it.quantity, final_line_price: it.final_line_price, paid: paid, properties: it.properties, corner: isCornerLine(it) });
      }
      // Remise de commande non ventilée par ligne, montants absents : vue incohérente, pas d'écriture anticipée.
      if (sum !== Number(cart.total_price)) return null;
      return { currency: cart.currency || config.currency, item_count: cart.item_count, total_price: cart.total_price, items: items };
    }

    /** Recharge panier + section, exécute le moteur de cadeaux, puis affiche. */
    refresh(options) {
      var self = this;
      this._refreshDue = false;
      this._deciding = (this._deciding || 0) + 1; // levé par _fetchState(true) dès que le plan de cadeaux est décidé
      return this._enqueue(function () {
        return self._fetchState(true).then(function (state) { return self._settle(state, 0, options); });
      });
    }

    _settle(state, depth, options, tried) {
      var self = this;
      tried = tried || {};
      if (state.cart && state.cart.token) store.bind(state.cart.token);
      // Écriture anticipée (plan sur /cart.js) : la section lue en parallèle est périmée, relue une fois.
      if (state.early) {
        (this._earlyTiers || []).forEach(function (tier) { tried[tier] = true; });
        this._earlyTiers = null;
        return this._fetchState().then(function (next) { return self._settle(next, depth + 1, options, tried); });
      }
      var incoming = this._parse(state.html);
      if (!incoming) return null;
      var config = this._readConfig(incoming);

      // Devise sans seuil saisi : le taux Shopify est transmis au Liquid, lié à la devise (« CAD:1.62 »).
      // Jamais sur panier vide ; renvoyé si l'attribut a disparu ou ne correspond plus (3 fois au plus par page).
      if (this._rateWanted(config) && this._rateSends < 3) {
        this._rateSends += 1;
        return cartApi('cart/update.js', { attributes: { _nkc_rate: config.currency + ':' + liveRate(config.currency) } })
          .then(function () { return self._fetchState(); })
          .then(function (next) { return self._settle(next, depth, options, tried); });
      }
      // Attribut de taux résiduel (panier revenu en euros ou dans une devise à seuil saisi, panier vidé) :
      // effacé une fois par page, pour ne pas partir dans les attributs d'une commande qui n'en a pas besoin.
      var stale = config && config.rate === null && !config.needRate && state.cart && state.cart.attributes && state.cart.attributes._nkc_rate;
      if (stale && !this._rateCleared) {
        this._rateCleared = true;
        return cartApi('cart/update.js', { attributes: { _nkc_rate: '' } })
          .then(function () { return self._fetchState(); })
          .then(function (next) { return self._settle(next, depth, options, tried); });
      }

      // Code du lien /discount/… absent du panier : rattaché (une fois par page), sinon le paiement
      // l'appliquerait sans que le panier l'affiche ni le compte dans la base des paliers.
      var orphan = this._orphanCode(state.cart);
      if (orphan) {
        this._linked[orphan.toUpperCase()] = true;
        var kept = (state.cart.discount_codes || []).map(function (entry) { return entry.code; });
        return cartApi('cart/update.js', { discount: kept.concat([orphan]).join(',') })
          .then(function () { return self._fetchState(); })
          .then(function (next) { return self._settle(next, depth, options, tried); });
      }

      // Code de l'offre « N pièces » posé tout seul (levier 2) : AVANT le moteur de cadeaux, pour que les paliers
      // soient évalués sur la base après remise en un seul rendu (sinon un cadeau apparaît puis repart).
      var autoCode = this._autoCodeDue(config, state.cart);
      if (autoCode) {
        this._autoTried = true;
        this._lastCause = 'code';
        this._lastCode = autoCode;
        var keptCodes = (state.cart.discount_codes || []).map(function (entry) { return entry.code; })
          .filter(function (existing) { return String(existing).toUpperCase() !== autoCode; });
        return cartApi('cart/update.js', { discount: keptCodes.concat([autoCode]).join(',') })
          .then(function (res) {
            var entry = res.ok && res.data ? (res.data.discount_codes || []).filter(function (item) { return String(item.code).toUpperCase() === autoCode; })[0] : null;
            fire('auto_code', { code: autoCode, applicable: !!(entry && entry.applicable) });
            return self._fetchState();
          })
          .then(function (next) { return self._settle(next, depth, options, tried); });
      }

      var plan = this._plan(config);
      if (plan) {
        // Un même palier n'est tenté qu'une fois par passe complète : jamais de boucle d'ajout.
        plan.add = plan.add.filter(function (item) { return !tried[item.tier]; });
      }
      if (this._hasWork(plan) && depth < 3) {
        plan.add.forEach(function (item) { tried[item.tier] = true; });
        return this._execute(plan)
          .then(function () { return self._fetchState(); })
          .then(function (next) { return self._settle(next, depth + 1, options, tried); });
      }
      // Le JSON du panier et la section sont lus en parallèle : si l'un des deux est en retard d'une
      // écriture, la section (ce que la cliente voit) fait foi et le JSON est relu une fois.
      var shown = config && config.cart;
      if (shown && state.cart && state.cart.item_count !== shown.item_count && !state.reread) {
        return cartApi('cart.js').then(function (res) {
          var cart = res.data || state.cart;
          if (cart.item_count !== shown.item_count) {
            cart.item_count = shown.item_count;
            cart.total_price = shown.total_price;
          }
          return self._settle({ cart: cart, html: state.html, reread: true }, depth, options, tried);
        });
      }
      this._render(incoming, config, state.cart, options);
      return null;
    }

    _orphanCode(cart) {
      var code = linkCode();
      if (!code || !cart || !cart.items || !cart.items.length || this._linked[code.toUpperCase()]) return null;
      var present = (cart.discount_codes || []).some(function (entry) { return String(entry.code).toUpperCase() === code.toUpperCase(); });
      return present ? null : code;
    }

    /**
     * Cadeaux : retraits et quantités par cart/change.js (clé de ligne) ; ajouts par cart/update.js, un
     * cadeau par requête, quantité fixée à 1 (une requête rejouée ne multiplie rien, et ce n'est pas un
     * « ajout au panier » pour les pixels ni pour le SDK CRO).
     */
    _execute(plan) {
      var self = this;
      var chain = Promise.resolve();
      // Retraits et quantités : une seule requête cart/update.js, par CLÉ de ligne (jamais par variante :
      // une ligne Corner partage sa variante avec un article payant). Si elle est refusée, repli ligne à ligne.
      var updates = {};
      var count = 0;
      plan.remove.forEach(function (key) { updates[key] = 0; count += 1; });
      plan.setQuantity.forEach(function (fix) { updates[fix.key] = fix.quantity; count += 1; });
      if (count > 1) {
        chain = chain.then(function () { return cartApi('cart/update.js', { updates: updates }); })
          .then(function (res) { if (!res.ok) return self._executeOneByOne(plan); return res; });
      } else if (count === 1) {
        chain = chain.then(function () { return self._executeOneByOne(plan); });
      }
      plan.add.forEach(function (item) {
        chain = chain.then(function () { return self._giftIn(item.id, item.tier); });
      });
      return chain;
    }

    _executeOneByOne(plan) {
      var chain = Promise.resolve();
      plan.remove.forEach(function (key) {
        chain = chain.then(function () { return cartApi('cart/change.js', { id: key, quantity: 0 }); });
      });
      plan.setQuantity.forEach(function (fix) {
        chain = chain.then(function () { return cartApi('cart/change.js', { id: fix.key, quantity: fix.quantity }); });
      });
      return chain;
    }

    _giftIn(variantId, tier) {
      var self = this;
      var updates = {};
      updates[String(variantId)] = 1;
      // Audit du 25/10/2026 : un « Paiement » tapé pendant la pose lisait encore la pastille « réclamer » du rendu d'avant, ouvrait le
      // rappel cadeau, que le rendu suivant effaçait aussitôt : clic perdu. Le cadeau en route n'est plus compté manquant jusqu'au rendu.
      self._placing[String(tier)] = true;
      return cartApi('cart/update.js', { updates: updates }, true).then(function (res) {
        // Refus définitif (stock, produit retiré) : pas de nouvelle tentative pendant cette page.
        if (res.status === 422 || res.status === 404) self._failed[tier] = true;
        if (!res.ok) delete self._placing[String(tier)];
        return res;
      }, function (error) { delete self._placing[String(tier)]; throw error; });
    }

    /* ---------- Rendu ---------- */

    _parse(html) {
      if (!html) return null;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector('[data-nkc-root]');
    }

    _render(incoming, config, cart, options) {
      options = options || {};
      var scroller = this._scroller();
      var scrollTop = scroller ? scroller.scrollTop : 0;
      var anchor = this._anchor(scroller);
      var focusPath = this._focusPath();
      var previousReached = store.get('reached', 0);
      var before = this._snapshot();
      var previousCart = this._cart || (this._config && this._config.cart) || null;

      this.root.innerHTML = incoming.innerHTML;
      this._config = config;
      if (cart && cart.items) this._cart = cart;
      this._lastSync = Date.now();

      var reached = this._reachedNow();
      var after = this._snapshot();
      // Palier franchi, tiroir visible : « Le harnais BELLORE est à vous » 4 s à la place de la phrase du palier
      // suivant (cro-ARBITRAGE n° 5), puis la phrase revient. Un rendu intermédiaire ne coupe pas les 4 s.
      clearTimeout(this._wonTimer);
      if (reached > previousReached && this._visible()) {
        this._wonUntil = Date.now() + 4000;
        fire('tier_reached', { reached: reached });
      }
      if (this._wonUntil && Date.now() < this._wonUntil) this._won(this._wonUntil - Date.now());
      // Palier gagné ou carte de taille apparue : la liste remonte là où sont la jauge et la carte ;
      // sinon la liste reste en place (repère sur la ligne touchée). Une carte apparue parce que la cliente
      // vient de retirer son cadeau (« Ajouter mon cadeau ») ne fait pas remonter la liste.
      var nextScroller = this._scroller();
      var up = reached > previousReached || (after.claims > before.claims && this._lastCause !== 'remove');
      if (nextScroller) {
        if (up) this._scrollTop(nextScroller);
        else { nextScroller.scrollTop = scrollTop; this._reanchor(nextScroller, anchor); }
      }
      // Ajout extérieur encore en vol : le fantôme survit au rendu (l'état « panier vide » n'apparaît pas).
      if (this._inflight > 0) this.ghost();
      // Ce que la cliente tape survit au rendu : code en cours de saisie (jamais écrasé s'il est déjà rempli).
      var input = this.root.querySelector('[data-nkc-code-form] input');
      if (input && before.typed && !input.value) {
        input.value = before.typed.value;
        try { input.setSelectionRange(before.typed.start, before.typed.end); } catch (e) { /* champ non éditable */ }
      }
      this._restoreFocus(focusPath);
      // Tiroir ouvert : le focus ne reste jamais derrière le voile (élément remplacé par le rendu).
      var popBox = this._upEl && !this._upEl.hidden ? this._upEl.querySelector('.nkc-pop__box') : null;
      if (popBox && !this._upEl.contains(document.activeElement)) popBox.focus({ preventScroll: true }); // popup ouverte : le focus y reste (N4)
      else if (this.mode === 'drawer' && this.hasAttribute('open') && !this.contains(document.activeElement)) {
        (this.root.querySelector('[data-nkc-close]') || this.panel).focus({ preventScroll: true });
      }
      this._placing = {}; // le rendu montre maintenant le panier réel : les pastilles « réclamer » restantes sont de vrais oublis
      this._afterRender(options);

      if (reached !== previousReached) store.set('reached', reached);
      if (reached > previousReached && config && config.confetti && this._visible()) this._confetti();

      this._afterChange(before, after, previousCart, options);
      this._announce();
      this._sweepRefused();
      this._sweepEmpty();
    }

    /** Phrase « est à vous » du dernier palier atteint, affichée [duration] ms à la place de la phrase de la jauge. */
    _won(duration) {
      var self = this;
      var won = this.root.querySelector('[data-nkc-won]');
      var text = this.root.querySelector('[data-nkc-goal-text]');
      if (!won || !text) return;
      won.hidden = false;
      text.hidden = true;
      clearTimeout(this._wonTimer);
      this._wonTimer = setTimeout(function () {
        self._wonUntil = 0;
        var w = self.root.querySelector('[data-nkc-won]');
        var t = self.root.querySelector('[data-nkc-goal-text]');
        if (w) w.hidden = true;
        if (t) t.hidden = false;
      }, duration);
    }

    /**
     * Code de l'offre « N pièces » posé tout seul (levier 2, marchés du réglage, jamais en France) : dès que le compte
     * de pièces payées est atteint et que le code n'est pas attaché, une écriture par panier (dans _settle, avant le
     * moteur de cadeaux). Retiré par la cliente, il n'est pas reposé (mémoire liée au panier). Renvoie le code à poser, ou null.
     */
    _autoCodeDue(config, cart) {
      var bxgy = config && config.bxgy;
      if (!config || !config.autoCode || !bxgy || !bxgy.code || !cart || !cart.items || !cart.items.length) return null;
      var code = String(bxgy.code).toUpperCase();
      var attached = (cart.discount_codes || []).some(function (entry) { return String(entry.code).toUpperCase() === code; });
      if (attached || this._autoTried || store.get('autoOff', false)) return null;
      if (!(Number(bxgy.qty) >= Number(bxgy.min))) return null;
      return code;
    }

    /** La ligne d'annonce du code automatique est masquée dès que le code est attaché (la consigne de la vague B prend la place). */
    _autoCode() {
      var config = this._config;
      var cart = this._cart;
      var bxgy = config && config.bxgy;
      var hint = this.root.querySelector('[data-nkc-auto-hint]');
      if (!hint || !bxgy || !bxgy.code || !cart || !config.autoCode) return;
      var code = String(bxgy.code).toUpperCase();
      if ((cart.discount_codes || []).some(function (entry) { return String(entry.code).toUpperCase() === code; })) hint.hidden = true;
    }

    /**
     * Boutons de paiement express (levier 5) : Shopify les dessine dans le conteneur rendu par le Liquid ; après un rendu
     * par la Section Rendering API, Shopify.PaymentButton.init() les redessine. Masqués tant qu'un cadeau débloqué n'est
     * pas au panier (l'express saute le rappel) : la cliente passe alors par « Paiement », qui rappelle le cadeau.
     */
    _express() {
      var box = this.root.querySelector('[data-nkc-express]');
      if (!box) return;
      box.hidden = this._missing() > 0;
      try {
        if (root.Shopify && root.Shopify.PaymentButton && typeof root.Shopify.PaymentButton.init === 'function') root.Shopify.PaymentButton.init();
      } catch (e) { /* boutons non redessinés : le bouton « Paiement » reste */ }
    }

    /** Champ de code replié (levier 9) : déplié au clic, ou dès qu'un code est attaché, refusé ou expliqué. */
    _unfoldCode(focus) {
      var form = this.root.querySelector('[data-nkc-code-form]');
      var toggle = this.root.querySelector('[data-nkc-code-toggle]');
      if (!form) return;
      form.hidden = false;
      if (toggle) { toggle.hidden = true; toggle.setAttribute('aria-expanded', 'true'); }
      if (focus) { var input = form.querySelector('input'); if (input) input.focus({ preventScroll: true }); }
      // Pied aéré (07/10 soir) : le champ vit dans la liste qui défile ; déplié, il se montre en entier au-dessus du pied.
      if (focus && form.scrollIntoView) { try { form.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (e) { form.scrollIntoView(false); } }
    }

    /** Repli du champ de code (tour 1 du jury, décision 160) : champ vidé, message effacé, lien « Code promo ? » de retour. */
    /** @param {boolean} keepMessage  consigne sous le champ gardée (le champ seul se replie : GLOW posé sous le minimum, tour 3). */
    _foldCode(keepMessage) {
      clearTimeout(this._foldTimer);
      var form = this.root.querySelector('[data-nkc-code-form]');
      var toggle = this.root.querySelector('[data-nkc-code-toggle]');
      var node = this.root.querySelector('[data-nkc-code-error]');
      if (!form) return;
      var input = form.querySelector('input');
      var hadFocus = form.contains(document.activeElement);
      if (input) { input.value = ''; input.removeAttribute('aria-invalid'); input.removeAttribute('aria-describedby'); }
      form.classList.remove('is-error');
      form.hidden = true;
      if (node && !keepMessage) { node.hidden = true; node.textContent = ''; }
      if (toggle) { toggle.hidden = false; toggle.setAttribute('aria-expanded', 'false'); if (hadFocus) toggle.focus({ preventScroll: true }); }
    }

    /** Un message sous le champ (refus, consigne) se replie tout seul après 5 s, sauf si la cliente a recommencé à taper. */
    _foldLater() {
      var self = this;
      clearTimeout(this._foldTimer);
      this._foldTimer = setTimeout(function () {
        var input = self.root.querySelector('[data-nkc-code-form] input');
        // Elle a repris la saisie (valeur différente du code refusé) : rien ne bouge.
        if (input && input.value && input.value !== self._refused) return;
        // Le champ a le focus (clavier ouvert sur le code refusé) : rien ne bouge sous ses doigts tant qu'elle tape. Tour 6 du jury (DA) :
        // sans une frappe dans la seconde qui suit (6 s en tout), le champ se replie quand même ; une frappe rend la main au repli après sortie.
        if (input && document.activeElement === input) {
          var typed = false;
          var onType = function () { typed = true; };
          input.addEventListener('input', onType, { once: true });
          self._foldTimer = setTimeout(function () {
            input.removeEventListener('input', onType);
            if (typed || (input.value && input.value !== self._refused)) { input.addEventListener('blur', function () { self._foldLater(); }, { once: true }); return; }
            self._refused = null;
            self._foldCode();
          }, 1000);
          return;
        }
        self._refused = null;
        self._foldCode();
      }, 5000);
    }

    /**
     * Texte tenu sur UNE ligne sans coupe : si la phrase déborde, le corps descend par pas de 0,5 px jusqu'au plancher
     * (jauge, minuteur expiré). Aucune phrase n'est tronquée ni renvoyée à la ligne.
     */
    _fit(node, floor) {
      if (!node || !root.getComputedStyle) return;
      node.style.removeProperty('font-size');
      if (!node.clientWidth || root.getComputedStyle(node).whiteSpace !== 'nowrap') return; // sur deux lignes (320 px) : rien à réduire
      var size = parseFloat(root.getComputedStyle(node).fontSize) || 13;
      var box = node.closest('.nkc-timer') || node;
      var guard = 12;
      // Décision 299 : la taille posée ici doit battre un « font-size … !important » de la feuille (ligne 2 de la carte d'écart) : priorité important.
      while (guard-- > 0 && size > floor && box.scrollWidth > box.clientWidth + 1) {
        size -= 0.5;
        node.style.setProperty('font-size', size + 'px', 'important');
      }
    }

    /**
     * Ligne 2 de la carte d'écart (décision 299) : « En cadeau : nuisette TENDRESSE (59,90€) », le nom complet du cadeau d'abord ; si la phrase
     * déborde encore au plancher de 12 px (plus jamais 10,5), la version au nom court (« En cadeau : nuisette (59,90€) », data-text-short) :
     * jamais clippée. Le type de pièce ne la préfixe plus (il n'est plus affiché dans cette carte, la vignette montre la pièce).
     */
    _fitKicker(slot, kicker) {
      if (!slot || !kicker) return;
      // Tour 5 du jury (clientes, décision 241) : devant un coffret (carte data-set), les versions data-text-set (mêmes phrases depuis la décision 299).
      var isSet = !!slot.querySelector('[data-nkc-reco-card][data-set]');
      var long = (isSet && slot.getAttribute('data-text-set')) || slot.getAttribute('data-text') || '';
      var short = (isSet && slot.getAttribute('data-text-set-short')) || slot.getAttribute('data-text-short') || '';
      var list = [long];
      if (short && short !== long) list.push(short);
      for (var i = 0; i < list.length; i += 1) {
        kicker.textContent = list[i];
        this._fit(kicker, 12);
        var nowrap = kicker.clientWidth && root.getComputedStyle && root.getComputedStyle(kicker).whiteSpace === 'nowrap';
        if (!nowrap || kicker.scrollWidth <= kicker.clientWidth + 1) return;
      }
    }

    /** Texte d'un bouton tenu sur une ligne : le corps descend par pas de 0,5 px jusqu'au plancher si le libellé déborde. */
    _fitInline(node, floor) {
      if (!node || !root.getComputedStyle || !node.clientWidth) return;
      node.style.fontSize = '';
      var size = parseFloat(root.getComputedStyle(node).fontSize) || 11;
      var guard = 8;
      while (guard-- > 0 && size > floor && node.scrollWidth > node.clientWidth + 1) {
        size -= 0.5;
        node.style.fontSize = size + 'px';
      }
    }

    /**
     * Carte d'écart (décision 299) : ligne 1 « WILD Noir : les 60€ qui manquent » (data-title du slot, [model] = nom de la carte retenue, mémorisé
     * dans data-nkc-model à la pose). Si elle déborde à 13 px : 12 px, puis la forme courte « WILD Noir · 60,00€ » (data-title-short, [price] = prix
     * de la carte) ; jamais d'ellipse sur le nom, jamais une ligne de plus. Idempotente : rappelée au redimensionnement, à l'ouverture, après les
     * pastilles. Le type de pièce n'est plus affiché. Rangée du bouton : si « Taille M · changer » et « AJOUTER · 60,00€ » ne tiennent pas côte à côte,
     * le bouton garde TOUJOURS son verbe au format court « Ajouter · 60,00€ » (fillerAddShort ; tour 4, décision 227 : plus jamais « + €60,00 »).
     */
    _fitFiller() {
      var card = this.root.querySelector('[data-nkc-filler] [data-nkc-reco-card]');
      if (!card) return;
      var strings = (this._config && this._config.strings) || {};
      var slot = card.closest('[data-nkc-filler]');
      var title = card.querySelector('.nkc-rc__title');
      var button = card.querySelector('.nkc-rc__actions [data-nkc-reco-add]');
      var price = card.querySelector('.nkc-rc__amount > span');
      var priceText = price ? (price.textContent || '').trim() : '';
      var kind = card.querySelector('.nkc-rc__kind');
      if (kind) kind.hidden = true;
      var model = card.getAttribute('data-nkc-model') || (title ? (title.textContent || '').trim() : '');
      if (title && slot && model) {
        var tpl = slot.getAttribute('data-title') || '';
        var over = function () { return title.clientWidth > 0 && title.scrollWidth > title.clientWidth + 1; };
        card.classList.remove('is-short');
        title.style.fontSize = '';
        title.textContent = tpl ? tpl.replace('[model]', model) : model;
        if (tpl && over()) {
          title.style.fontSize = '12px';
          if (over()) {
            var shortTpl = slot.getAttribute('data-title-short') || '[model] · [price]';
            if (!priceText) shortTpl = shortTpl.replace(/\s*·\s*\[price\]/, '');
            card.classList.add('is-short');
            title.style.fontSize = '';
            title.textContent = shortTpl.replace('[model]', model).replace('[price]', priceText);
            if (over()) title.style.fontSize = '12px';
          }
        }
      }
      if (!button || !price || !card.clientWidth || button.hasAttribute('aria-disabled') || button.disabled) return;
      var note = card.querySelector('[data-nkc-size-note]');
      var rowOver = card.scrollWidth > card.clientWidth + 1 || !!(note && note.clientWidth > 0 && note.scrollWidth > note.clientWidth + 1);
      if (rowOver && !card.classList.contains('is-tight')) {
        card.classList.add('is-tight');
        var verb = strings.fillerAddShort || strings.add || '';
        var short = (verb ? verb + ' · ' : '') + priceText;
        button.setAttribute('aria-label', button.getAttribute('data-label') || '');
        button.setAttribute('data-label', short);
        button.textContent = short;
      }
    }

    /** Bouton de paiement : jamais de libellé coupé. Si le libellé long déborde, le libellé court de la langue prend la place. */
    _fitPay() {
      var button = this.root.querySelector('.nkc-btn--pay');
      var long = button && button.querySelector('.nkc-btn__long');
      if (!button || !long) return;
      button.classList.remove('is-short');
      if (!button.clientWidth) return;
      if (long.scrollWidth > long.clientWidth + 1) button.classList.add('is-short');
    }

    /**
     * Jauge-devises (08/10) : trois formes rendues par le Liquid sous chaque vignette (« Harnais dès 421 AED », « Harnais · 421 AED »,
     * « 421 AED », le nom restant dans title) ; la plus longue qui tient reste (data-fit), à 11 px d'abord puis à 10 px (data-shrink sur
     * la liste, commun aux trois libellés) : 10 px n'est retenu que s'il garde une forme plus complète. Jamais d'ellipse ni de coupe.
     */
    _fitGoal() {
      var steps = this.root.querySelector('.nkc-goal__steps');
      if (!steps) return;
      var labels = Array.prototype.slice.call(steps.querySelectorAll('.nkc-goal__label'));
      steps.removeAttribute('data-shrink');
      labels.forEach(function (label) { label.setAttribute('data-fit', '1'); });
      if (!labels.length || !steps.clientWidth) return;
      // Largeurs fractionnaires : à 1 px près (scrollWidth entier), un débordement de 0,4 px suffit à poser l'ellipse (vu sur « Body dès 1,051 AE… », AE 390).
      var fits = function (label) {
        var form = label.querySelector('.nkc-goal__v--' + label.getAttribute('data-fit')) || label.firstElementChild;
        if (!form) return true;
        var cs = root.getComputedStyle(label);
        var inner = label.getBoundingClientRect().width - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
        return form.getBoundingClientRect().width <= inner + 0.01;
      };
      var pass = function () {
        var score = 0;
        labels.forEach(function (label) {
          var forms = label.querySelectorAll('.nkc-goal__v').length || 1;
          var v = 1;
          label.setAttribute('data-fit', '1');
          while (v < forms && !fits(label)) { v += 1; label.setAttribute('data-fit', String(v)); }
          score += v;
        });
        return score;
      };
      var at11 = pass();
      if (at11 === labels.length) return;
      var kept = labels.map(function (label) { return label.getAttribute('data-fit'); });
      steps.setAttribute('data-shrink', '');
      if (pass() < at11) return;
      steps.removeAttribute('data-shrink');
      labels.forEach(function (label, i) { label.setAttribute('data-fit', kept[i]); });
    }

    /**
     * Badges des cadeaux (08/10, dirigeant) : en tête du bloc, si « Offerts avec votre commande, non facturés » ne tient pas, « Valeur 90€ »
     * s'efface (is-tight) ; sur une ligne cadeau, si « OFFERT · dès 100€ » et « Valeur 90€ » ne tiennent pas sur la rangée, la valeur passe
     * à 11 px (is-tight) puis s'efface (is-bare) : le badge garde la priorité, la ligne ne grandit pas.
     */
    _fitGifts() {
      var head = this.root.querySelector('.nkc-giftshead');
      if (head) {
        head.classList.remove('is-tight');
        var note = head.querySelector('.nkc-giftshead__note');
        if (note && note.clientWidth && note.scrollWidth > note.clientWidth + 1) head.classList.add('is-tight');
      }
      this.root.querySelectorAll('.nkc-line--gift .nkc-line__amount').forEach(function (amount) {
        amount.classList.remove('is-tight', 'is-bare');
        var row = amount.parentElement;
        if (!row || !row.clientWidth || !amount.querySelector('.nkc-worth')) return;
        var tags = row.querySelector('.nkc-line__tags');
        var over = function () { return row.scrollWidth > row.clientWidth + 1 || (tags && tags.clientWidth && tags.scrollWidth > tags.clientWidth + 1); };
        if (!over()) return;
        amount.classList.add('is-tight');
        if (over()) amount.classList.add('is-bare');
      });
    }

    /** Mesures qui dépendent de la largeur réelle : au rendu, à l'ouverture, au redimensionnement. */
    _fitAll() {
      var self = this;
      this._fitGoal();
      this._fitGifts();
      this.root.querySelectorAll('.nkc-goal__text:not([hidden])').forEach(function (node) { self._fit(node, 11); });
      this._fitKicker(this.root.querySelector('[data-nkc-filler]'), this.root.querySelector('[data-nkc-filler] [data-nkc-filler-text]'));
      this._fitFiller();
      this.root.querySelectorAll('.nkc-picks__go').forEach(function (node) { self._fitInline(node, 9.5); });
      var done = this.root.querySelector('[data-nkc-timer][data-done] [data-nkc-timer-done]:not([hidden]), [data-nkc-timer][data-done] [data-nkc-timer-after]:not([hidden])');
      if (done) this._fit(done, 10.5);
      // Le libellé du bouton de paiement passe avant la phrase de livraison : si « VALIDER MA COMMANDE » devait céder la place à
      // « PAIEMENT », la ligne sous le total descend d'un cran (compact, puis « Hors livraison ») jusqu'à ce que le bouton reste entier.
      this._fitShip();
      this._fitPay();
      var payBtn = this.root.querySelector('.nkc-btn--pay');
      var guard = 3;
      while (guard-- > 0 && payBtn && payBtn.classList.contains('is-short') && this._shipDegrade()) this._fitPay();
      this._fitLines();
      this._fitReassure();
      this.root.querySelectorAll('.nkc-reco__track').forEach(function (track) { self._fitRecoTitles(track); });
    }

    /**
     * Tour 5 du jury (CRO, décision 243) : sous le seuil, la ligne sous le total est un objectif (« Livraison offerte dès 100€ »), puis la
     * forme compacte (« Port offert dès 100€ »), puis « Hors livraison » : la plus longue qui tient dans la colonne du total reste.
     */
    _fitShip() {
      var ship = this.root.querySelector('.nkc-ship--under');
      if (!ship || !ship.clientWidth) return;
      var long = ship.querySelector('.nkc-ship__long');
      var mid = ship.querySelector('.nkc-ship__mid');
      ship.classList.remove('is-mid', 'is-short');
      if (!long) return;
      // Les spans sont en ligne (scrollWidth = 0) : la largeur se lit sur le rectangle.
      var fits = function (span) { return span.getBoundingClientRect().width <= ship.clientWidth + 1; };
      if (fits(long)) return;
      ship.classList.add('is-mid');
      if (mid && fits(mid)) return;
      ship.classList.remove('is-mid');
      ship.classList.add('is-short');
    }

    /** Un cran plus court sous le total (long → compact → « Hors livraison ») ; faux s'il n'y a plus rien à raccourcir. */
    _shipDegrade() {
      var ship = this.root.querySelector('.nkc-ship--under');
      if (!ship || !ship.querySelector('.nkc-ship__long') || ship.classList.contains('is-short')) return false;
      if (ship.classList.contains('is-mid')) { ship.classList.remove('is-mid'); ship.classList.add('is-short'); return true; }
      ship.classList.add('is-mid');
      return true;
    }

    /**
     * Tour 5 du jury (CRO, clientes, UX ; décision 244) : la taille d'une ligne ne se tronque jamais. Si « Taille M/M » déborde de sa rangée,
     * l'étiquette Best-seller cède (is-tight), puis le nom du code (« GLOW −40,00€ » → « −40,00€ ») et, sous 360 px, le prix barré (is-tighter).
     * Un titre qui déborde à 320 px prend deux lignes (has-long-title) et le type une seule : la ligne garde ses 91-92 px.
     */
    _fitLines() {
      var narrow = !!(root.matchMedia && root.matchMedia('(max-width: 359px)').matches);
      this.root.querySelectorAll('.nkc-line:not(.nkc-line--gift)').forEach(function (line) {
        line.classList.remove('is-tight', 'is-tighter', 'has-long-title');
        var size = line.querySelector('.nkc-line__tags .nkc-line__size');
        var over = function () { return size && size.clientWidth > 0 && size.scrollWidth > size.clientWidth + 1; };
        if (over()) {
          line.classList.add('is-tight');
          if (narrow && over()) line.classList.add('is-tighter');
        }
        // Le titre est une boîte à une ligne (-webkit-line-clamp: 1) : un nom trop long déborde en HAUTEUR (scrollHeight), pas en largeur.
        var title = line.querySelector('.nkc-line__title');
        if (narrow && title && title.clientWidth > 0 && (title.scrollWidth > title.clientWidth + 1 || title.scrollHeight > title.clientHeight + 1)) line.classList.add('has-long-title');
      });
    }

    /**
     * Tour 5 du jury (DA, clientes, UX ; décision 242) : la dernière promesse encore visible de la réassurance (ligne de 18 px, la suite
     * est masquée) porte data-last : plus de point médian orphelin « Paiement sécurisé · » à 320 px. Sur /cart ordinateur (deux lignes
     * visibles), la dernière visible est la dernière promesse : rien ne change.
     */
    _fitReassure() {
      var box = this.root.querySelector('.nkc-reassure'); // pied aéré (07/10 soir) : la réassurance vit sous la carte « Récapitulatif »
      if (!box || !box.clientWidth) return;
      var spans = Array.prototype.filter.call(box.children, function (span) { return span.offsetParent !== null; });
      spans.forEach(function (span) { span.removeAttribute('data-last'); });
      if (!spans.length) return;
      var bottom = box.getBoundingClientRect().bottom + 1;
      var last = null;
      spans.forEach(function (span) { if (span.getBoundingClientRect().bottom <= bottom) last = span; });
      if (last && last !== spans[spans.length - 1]) last.setAttribute('data-last', '');
      // Tour 6 du jury (CRO, DA, UX) : sur /cart ordinateur la ligne peut en prendre deux ; la dernière promesse de CHAQUE ligne visible
      // perd son point médian (plus de « Paiement sécurisé · » orphelin en fin de ligne 1).
      var tops = spans.map(function (span) { return Math.round(span.getBoundingClientRect().top); });
      spans.forEach(function (span, i) { if (i < spans.length - 1 && tops[i + 1] > tops[i] + 2) span.setAttribute('data-last', ''); });
    }

    /**
     * Tour 5 du jury (DA, CRO, clientes ; décision 239) : dès qu'un nom de carte déborde de sa ligne (« INCONTOURNA… », « AUDACIOUS Bl… »),
     * TOUTES les cartes de la rangée passent au nom sur deux lignes (data-two-lines) : le nom et sa couleur se lisent, les prix restent alignés ;
     * quand tout tient sur une ligne, rien ne change (décision 233 c tenue).
     */
    _fitRecoTitles(track) {
      if (!track) return;
      track.removeAttribute('data-two-lines');
      track.removeAttribute('data-shrink');
      var titles = track.querySelectorAll('.nkc-rc__title');
      var overflows = function () { return Array.prototype.some.call(titles, function (title) { return title.clientWidth > 0 && title.scrollWidth > title.clientWidth + 1; }); };
      if (overflows()) track.setAttribute('data-two-lines', '');
      // Tour 6 du jury (DA, majeur) : un mot seul plus large que la carte (« INCONTOURNABLE » dans 108 px) déborde encore sur deux lignes et se
      // faisait trancher par overflow: hidden ; la rangée passe à 11 px (data-shrink) et, au-delà, le mot casse proprement (overflow-wrap: anywhere).
      if (track.hasAttribute('data-two-lines') && overflows()) track.setAttribute('data-shrink', '');
    }

    /** État visible avant un rendu : cartes de taille, cadeaux présents (par palier), saisie du champ code. */
    _snapshot() {
      var input = this.root.querySelector('[data-nkc-code-form] input');
      var typed = input && input.value ? { value: input.value, start: input.selectionStart, end: input.selectionEnd } : null;
      var gifts = {};
      this.root.querySelectorAll('[data-nkc-line][data-gift-tier]').forEach(function (line) { gifts[line.getAttribute('data-gift-tier')] = true; });
      // Tour 6 du jury (UX, majeur) : paliers atteints (jauge) et livraison offerte acquise, pour nommer ce qu'un retrait fait perdre.
      var reached = {};
      this.root.querySelectorAll('.nkc-goal__step.is-reached[data-tier]').forEach(function (step) { reached[step.getAttribute('data-tier')] = true; });
      var shipOk = !!this.root.querySelector('.nkc-ship--ok');
      return { claims: this.root.querySelectorAll('[data-nkc-claim]').length, gifts: gifts, typed: typed, reached: reached, shipOk: shipOk };
    }

    _scrollTop(scroller) {
      var reduced = root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (typeof scroller.scrollTo === 'function') scroller.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
      else scroller.scrollTop = 0;
    }

    /**
     * Après un rendu : ce qui a changé est dit. « [title] retiré · Annuler » (8 s) après une croix ; un cadeau
     * perdu (palier repassé sous le seuil, par un retrait ou par un code) est nommé avec le montant où il revient ;
     * la ligne du cadeau qui vient d'arriver est montrée ; les lecteurs d'écran reçoivent le nouvel état ;
     * un code qui cesse de s'appliquer est dit sous le champ.
     */
    _afterChange(before, after, previousCart, options) {
      var self = this;
      var strings = (this._config && this._config.strings) || {};
      var cart = this._cart;
      var messages = [];
      var undo = this._undo;
      this._undo = null;

      // Cadeau perdu : présent avant, absent après, palier plus atteint (sinon c'est un simple ré-ajout en cours).
      var plan = this._config && this._config.gifts ? planGifts(this._config.cart, this._config, [], {}) : null;
      var reachedIds = {};
      if (plan) plan.reached.forEach(function (id) { reachedIds[String(id)] = true; });
      var lost = Object.keys(before.gifts).filter(function (tier) { return !after.gifts[tier] && !reachedIds[tier]; });
      if (undo && undo.gift && lost.indexOf(String(undo.tier)) > -1) lost = lost.filter(function (tier) { return tier !== String(undo.tier); });
      var emptied = !!(this._config && this._config.cart && this._config.cart.item_count === 0);
      var byCode = this._lastCause === 'code' && !!this._lastCode;
      var saved = byCode && this._config && this._config.codes ? this._config.codes[String(this._lastCode).toUpperCase()] : null;
      // Tour 1 du jury (décision 158) : « GLOW appliqué : −50,00€. Plus que 40€ pour retrouver la nuisette TENDRESSE. »
      // Le premier cadeau perdu seulement (le plus proche à retrouver) ; rien dans un panier vidé.
      if (!emptied) lost.slice(0, 1).forEach(function (tier) {
        var info = ((self._config && self._config.tiers) || []).filter(function (t) { return String(t.id) === tier; })[0];
        if (!info || !info.left) return;
        var text = byCode && strings.giftLostCode ? strings.giftLostCode : strings.giftLost;
        if (!text) return;
        if (!saved) text = text.replace(/\s*:\s*−\[value\]/, '');
        messages.push(text.replace('[gift]', info.the || info.name || '').replace('[amount]', info.left).replace('[code]', self._lastCode || '').replace('[value]', saved || ''));
      });
      // Code qui coûte un cadeau : « Annuler » retire le code (le cadeau revient).
      if (!undo && byCode && messages.length) undo = { code: String(this._lastCode) };

      // Tour 6 du jury (UX, majeur) : un retrait qui fait repasser sous un palier dont le cadeau n'était pas encore pris (débloqué, non
      // réclamé ou retiré) perdait le cadeau et la livraison offerte sans un mot. Le bandeau les nomme avec la valeur :
      // « ERES retiré · vous perdez Harnais BELLORE (90€) et la livraison offerte · Annuler ». Un cadeau présent et perdu garde la
      // phrase d'avant (« … Plus que 35€ pour retrouver le harnais »).
      var removedText = undo && !undo.code && strings.removed ? strings.removed.replace('[title]', undo.title || '') : null;
      if (removedText && !undo.gift && !messages.length && !emptied) {
        var lostUnlocked = Object.keys(before.reached || {}).filter(function (tier) { return !(after.reached || {})[tier]; });
        var shipLost = !!before.shipOk && !after.shipOk;
        var lostInfo = lostUnlocked.length ? ((self._config && self._config.tiers) || []).filter(function (t) { return String(t.id) === lostUnlocked[0]; })[0] : null;
        var lostText = shipLost && strings.undoLostGiftShip ? strings.undoLostGiftShip : strings.undoLostGift;
        if (lostInfo && lostText) {
          if (!lostInfo.worth) lostText = lostText.replace(' ([value])', '');
          removedText = lostText.replace('[title]', undo.title || '').replace('[gift]', lostInfo.name || '').replace('[value]', lostInfo.worth || '');
        }
      }
      if (removedText) messages.unshift(removedText);
      // Tour 6 du jury (clientes) : le bandeau d'un code (« GLOW appliqué : … ») dure 7 s et part au premier défilement de plus de 40 px
      // (la jauge et la puce le redisent) ; le bandeau « retiré · Annuler » garde ses 12 s (il porte un rattrapage).
      if (messages.length) this._showUndo(messages.join(' '), undo, byCode && !removedText ? 7000 : 12000, lost, byCode && !removedText);
      else if (this._undoShown && Date.now() < this._undoShown.until) {
        // Rendu survenu dans l'intervalle (cadeau retiré à la suite, relecture) : le message reste jusqu'à son terme,
        // SAUF si un cadeau qu'il dit perdu est revenu (panier recomplété dans les 8 s : vague D, F6).
        var back = (this._undoShown.lost || []).some(function (tier) { return !!after.gifts[tier]; });
        if (back) this._hideUndo();
        else this._showUndo(this._undoShown.text, this._undoShown.payload, this._undoShown.until - Date.now(), this._undoShown.lost);
      }

      // Ligne du cadeau qui vient d'arriver (taille choisie) : montrée, mise en évidence, focus sur son titre.
      if (this._justClaimed != null) {
        var line = this.root.querySelector('[data-nkc-line][data-gift-tier="' + esc(String(this._justClaimed)) + '"]');
        this._justClaimed = null;
        if (line) {
          if (typeof line.scrollIntoView === 'function') line.scrollIntoView({ block: 'nearest' });
          line.classList.add('is-new');
          setTimeout(function () { line.classList.remove('is-new'); }, 1500);
          // Tour 4 du jury (clientes, décision 232) : le focus se pose sur la ligne (sans anneau), plus sur son titre (rectangle noir après une saisie clavier).
          if (this.mode === 'drawer' && this.hasAttribute('open')) { line.setAttribute('tabindex', '-1'); line.focus({ preventScroll: true }); }
        }
      }
      // Tour 6 du jury (UX) : après un ajout depuis la carte d'écart, une carte de recommandation ou « Annuler », le focus se pose sur la
      // ligne ajoutée ou restaurée (sans anneau, comme le cadeau arrivé : décision 232), plus sur « Fermer le panier ».
      if (this._justAdded != null) {
        var addedId = String(this._justAdded);
        this._justAdded = null;
        var added = this.root.querySelector('[data-nkc-line][data-vid="' + esc(addedId) + '"]');
        if (added && !options.error && this._visible() && !(this.mode === 'drawer' && !this.hasAttribute('open'))) {
          added.setAttribute('tabindex', '-1');
          added.focus({ preventScroll: true });
        }
      }

      // Code qui cessait de s'appliquer (GLOW à deux articles après un retrait) : dit sous le champ, en consigne.
      if (previousCart && cart && cart.discount_codes && strings.codeLost && !options.codeError && !options.codeInfo) {
        var wasOn = {};
        (previousCart.discount_codes || []).forEach(function (entry) { if (entry.applicable) wasOn[String(entry.code).toUpperCase()] = true; });
        var dropped = cart.discount_codes.filter(function (entry) { return !entry.applicable && wasOn[String(entry.code).toUpperCase()]; })[0];
        var bxgy = this._config && this._config.bxgy;
        if (dropped && bxgy && String(dropped.code).toUpperCase() === String(bxgy.code).toUpperCase()) {
          this._codeError(strings.codeLost.replace('[code]', String(dropped.code).toUpperCase()).replace('[min]', bxgy.min), true);
          this._markSaid(dropped.code);
        }
      }

      // Lecteurs d'écran : annoncer le nouvel état quand le nombre d'articles ou le total a changé.
      var live = this.querySelector('[data-nkc-live]');
      if (live && cart && previousCart && !options.initial && (cart.item_count !== previousCart.item_count || cart.total_price !== previousCart.total_price)) {
        var sum = this.root.querySelector('.nkc-pay__sum');
        var goal = this.root.querySelector('.nkc-goal__text');
        var parts = [strings.updated || '', String(cart.item_count), sum ? sum.textContent.trim() : ''].filter(Boolean);
        var text = parts.join(' · ') + (goal ? '. ' + goal.textContent.trim() : '') + (messages.length ? '. ' + messages.join(' ') : '');
        live.textContent = '';
        setTimeout(function () { live.textContent = text; }, 50);
      }
      this._lastCause = null;
    }

    _showUndo(text, undo, duration, lost, closeOnScroll) {
      var template = this.root.querySelector('[data-nkc-undo-item]');
      var home = this.root.querySelector('.nkc-foot') || this.root.querySelector('.nkc-empty') || this.root.querySelector('[data-nkc-scroll]');
      if (!template || !home) return;
      var focusCroix = this._focusWasRemove;
      this._hideUndo();
      if (closeOnScroll) {
        var scroller = this._scroller();
        if (scroller) {
          var from = scroller.scrollTop;
          var selfScroll = this;
          this._undoScroll = { node: scroller, fn: function () { if (Math.abs(scroller.scrollTop - from) > 40) selfScroll._hideUndo(); } };
          scroller.addEventListener('scroll', this._undoScroll.fn, { passive: true });
        }
      }
      var node = template.content.firstElementChild.cloneNode(true);
      node.querySelector('[data-nkc-undo-text]').textContent = text;
      var button = node.querySelector('[data-nkc-undo]');
      if (undo) { this._undoPayload = undo; } else { button.textContent = ''; button.hidden = true; }
      var anchor = home.classList.contains('nkc-empty') ? home.querySelector('a') : home.firstElementChild;
      home.insertBefore(node, anchor || null);
      if (focusCroix && undo && !button.hidden) button.focus({ preventScroll: true });
      var self = this;
      this._undoShown = { text: text, payload: undo, until: Date.now() + duration, lost: lost || [] };
      this._undoTimer = setTimeout(function () { self._hideUndo(); }, duration);
    }

    _hideUndo() {
      clearTimeout(this._undoTimer);
      if (this._undoScroll) { this._undoScroll.node.removeEventListener('scroll', this._undoScroll.fn); this._undoScroll = null; }
      this._undoPayload = null;
      this._undoShown = null;
      this._focusWasRemove = false;
      this.root.querySelectorAll('.nkc-undo').forEach(function (node) { node.remove(); });
    }

    /** Panier vidé : le prochain panier (même jeton Shopify) repart sans taille de body mémorisée. */
    _sweepEmpty() {
      var config = this._config;
      if (!config || !config.cart || config.cart.item_count !== 0) return;
      if (Object.keys(store.get('sizes', {})).length) store.set('sizes', {});
    }

    /** Ligne de repère pour garder la liste en place quand la hauteur du contenu change au-dessus. */
    _anchor(scroller) {
      if (!scroller || !scroller.scrollTop) return null;
      var key = this._lastKey;
      var line = key ? this.root.querySelector('[data-nkc-line][data-key="' + esc(key) + '"]') : null;
      if (!line) return null;
      return { key: key, top: line.getBoundingClientRect().top };
    }

    _reanchor(scroller, anchor) {
      if (!anchor) return;
      var line = this.root.querySelector('[data-nkc-line][data-key="' + esc(anchor.key) + '"]');
      if (!line) return;
      var delta = line.getBoundingClientRect().top - anchor.top;
      if (delta) scroller.scrollTop += delta;
    }

    /**
     * Panier vide dans une devise convertie (vague D, F8) : le taux n'est jamais écrit sur un panier vide, le Liquid a
     * rendu la jauge avec des gabarits (data-nkc-eur = seuil en euros) sous [data-nkc-rate-wait] masqué. Le seuil est
     * calculé ici au taux Shopify de la page (seuil € × taux, arrondi à l'unité supérieure : la même règle que le Liquid)
     * et écrit dans le format appris de l'échantillon moneyUnit. Sans taux, la jauge reste masquée.
     */
    _fillRates() {
      var waits = this.root.querySelectorAll('[data-nkc-rate-wait]');
      if (!waits.length) return;
      var config = this._config;
      var rate = liveRate(config && config.currency);
      if (!rate) return;
      var sample = config.moneyUnit;
      this.root.querySelectorAll('[data-nkc-eur]').forEach(function (node) {
        var eur = Number(node.getAttribute('data-nkc-eur'));
        if (!(eur > 0)) return;
        var text = moneyUnits(Math.ceil(eur * rate), sample);
        // Jauge-devises (08/10) : sous les vignettes, « Dhs. 421 » devient « 421 AED » comme dans le Liquid (nkc:compact).
        if (node.hasAttribute('data-nkc-compact')) text = moneyCompact(text, sample, config.currency);
        node.textContent = text;
      });
      waits.forEach(function (node) { node.hidden = false; });
    }

    _afterRender(options) {
      var self = this;
      this._protectPreview();
      this._upPreview(); // ?nkc_upsell=1 retenu pour l'onglet dès l'arrivée
      if (this.mode === 'page' || this.hasAttribute('open')) this._upPreload();
      this._timer();
      this._fillRates();
      this._pendingCodes();
      this._loadReco();
      this._autoCode();
      this._express();
      if (options.notice) this._notice(options.notice, false, !!options.error);
      else if (this._sticky) this._notice(this._sticky, true); // plafond de quantité : dit une fois, après le rendu
      this._sticky = null;
      if (options.codeError || options.codeInfo) {
        this._codeError(options.codeError || options.codeInfo, !!options.codeInfo);
        var input = this.root.querySelector('[data-nkc-code-form] input');
        if (input) {
          // Tour 2 du jury (décision 182) : un code refusé reste écrit et sélectionné (une faute d'une lettre se corrige
          // sans tout retaper) ; le repli automatique l'oublie (_foldLater). Une consigne (GLOW sous le minimum) vide le champ.
          if (options.codeError) {
            input.value = options.codeRefused || this._lastCode || '';
            this._refused = input.value;
            if (this._visible()) { input.focus({ preventScroll: true }); try { input.select(); } catch (e) { /* champ non éditable */ } }
          } else {
            input.value = '';
          }
        }
      } else if (options.codeApplied) {
        // Code appliqué : le champ (replié par le rendu) ne garde pas le code tapé ; la rangée d'entrée disparaît tant que le code est posé (décision 303).
        var applied = this.root.querySelector('[data-nkc-code-form] input');
        if (applied) applied.value = '';
        if (this.mode === 'drawer' && this.hasAttribute('open')) {
          var payAfter = this.root.querySelector('[data-nkc-checkout] [name="checkout"]');
          if (payAfter) payAfter.focus({ preventScroll: true });
        }
      }
      this._suggestSize();
      // Tour 5 du jury (UX, décision 247) : après le retrait d'un code (ligne de la colonne ou puce), le focus se pose sur « Ajouter un code promo » (la rangée qui vient de réapparaître).
      if (this._focusCodeToggle) {
        this._focusCodeToggle = false;
        var toggleBack = this.root.querySelector('[data-nkc-code-toggle]');
        if (toggleBack && !toggleBack.hidden && this._visible()) toggleBack.focus({ preventScroll: true });
      }
      if (options.initial) {
        // Le thème réécrit la bulle depuis cart.js au chargement (pageshow, réponse asynchrone) : on repasse derrière lui.
        this._syncBubble();
        setTimeout(function () { self._syncBubble(); }, 600);
        setTimeout(function () { self._syncBubble(); }, 2000);
        if (!this._bubbleBound) {
          this._bubbleBound = true;
          root.addEventListener('pageshow', function () { setTimeout(function () { self._syncBubble(); }, 400); });
        }
      }
      this._dock();
      this._fitAll();
      if (!this._fitBound) {
        this._fitBound = true;
        root.addEventListener('resize', function () { clearTimeout(self._fitTimer); self._fitTimer = setTimeout(function () { self._dock(); self._fitAll(); }, 120); });
        // Tour 6 du jury : les mesures de largeur faites avant l'arrivée de la police (Montserrat) sont refaites une fois les fontes prêtes :
        // sur de. à 1440, « Sichere Zahlung » gardait un data-last posé quand le texte de repli était plus large.
        if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') document.fonts.ready.then(function () { self._fitAll(); });
      }
      if (options.initial) {
        var reached = Math.min(store.get('reached', 0), this._reachedNow());
        if (reached !== store.get('reached', 0)) store.set('reached', reached);
      }
      this._upSync(); // popup ouverte : textes, totaux et ajouts suivent le panier rendu
    }

    /** Cadeau à taille (body) : la taille déjà portée dans le panier est signalée (bord d'encre), jamais ajoutée d'office. */
    _suggestSize() {
      var size = this._giftSize();
      var strings = (this._config && this._config.strings) || {};
      // Tour 2 du jury (décision 188) : la taille du panier est écrite sous les pastilles (« Votre taille au panier : M »),
      // la pastille correspondante est seulement en gras : rien ne ressemble à un choix déjà fait.
      this.root.querySelectorAll('[data-nkc-size-hint]').forEach(function (hint) {
        var block = hint.closest('[data-nkc-claim], [data-nkc-reminder-tier]');
        var has = !!(size && block && Array.prototype.some.call(block.querySelectorAll('.nkc-size'), function (button) { return (button.textContent || '').trim().toUpperCase() === size.toUpperCase(); }));
        hint.hidden = !(has && strings.giftSizeHint);
        hint.textContent = has && strings.giftSizeHint ? strings.giftSizeHint.replace('[size]', size.toUpperCase()) : '';
      });
      if (!size) return;
      this.root.querySelectorAll('[data-nkc-claim] .nkc-size, [data-nkc-reminder] .nkc-size').forEach(function (button) {
        button.classList.toggle('is-suggested', (button.textContent || '').trim().toUpperCase() === size.toUpperCase());
      });
    }

    /**
     * Bulle du panier dans l'en-tête du thème (<cart-count>, Impact) : elle dit le même nombre que « PANIER (n) »,
     * les pièces payées (tour 2 du jury, décision 187). Le thème la remplit depuis cart:change (detail.cart.item_count) :
     * _announce lui passe ce nombre ; au premier rendu (bulle écrite par le Liquid du thème), elle est réécrite ici.
     */
    _paidCount() {
      var body = this.root.querySelector('[data-nkc-body]');
      var paid = body ? Number(body.getAttribute('data-paid')) : NaN;
      return isNaN(paid) ? null : paid;
    }

    _syncBubble() {
      var paid = this._paidCount();
      if (paid == null) return;
      document.querySelectorAll('cart-count, .cart-count').forEach(function (node) {
        if (String((node.textContent || '').trim()) !== String(paid)) node.textContent = String(paid);
        if (node.tagName === 'CART-COUNT') node.classList.toggle('opacity-0', paid === 0);
      });
    }

    /** Taille unique du panier (toutes les lignes payées portent la même), sinon null. */
    _cartSize() {
      var sizes = {};
      var count = 0;
      this.root.querySelectorAll('[data-nkc-line]').forEach(function (line) {
        if (line.hasAttribute('data-gift-tier')) return;
        var opt = line.getAttribute('data-opt');
        if (!opt) return;
        opt.split('/').forEach(function (size) {
          size = size.trim();
          if (size && !sizes[size]) { sizes[size] = true; count += 1; }
        });
      });
      return count === 1 ? Object.keys(sizes)[0] : null;
    }

    /**
     * Taille suggérée pour un cadeau à taille (body) : la taille unique du panier s'il y en a une ; sinon (tour 4 du jury, CRO,
     * décision 228 : lignes mêlant « M » et des ensembles « S/M ») la dernière ligne payée dont la taille est SIMPLE (S, M, L, XL…),
     * les valeurs jointes d'un ensemble ignorées. Les recommandations gardent _cartSize (décisions 93 et 121).
     */
    _giftSize() {
      var size = this._cartSize();
      if (size) return size;
      var simple = /^(XXS|XS|S|M|L|XL|XXL|XXXL)$/i;
      var found = null;
      this.root.querySelectorAll('[data-nkc-line]').forEach(function (line) {
        if (line.hasAttribute('data-gift-tier')) return;
        var opt = (line.getAttribute('data-opt') || '').trim();
        if (opt && opt.indexOf('/') === -1 && simple.test(opt)) found = opt;
      });
      return found;
    }

    /**
     * Page /cart sur ordinateur (décision 168) : la boîte cadeau et le récapitulatif rejoignent la carte de paiement,
     * au-dessus de la barre Total | bouton (colonne de droite utile, tout ce qui touche au montant au même endroit).
     */
    _dock() {
      if (this.mode !== 'page') return;
      var foot = this.root.querySelector('.nkc-foot');
      if (!foot) return;
      var wide = !!(root.matchMedia && root.matchMedia('(min-width: 1000px)').matches);
      var dock = foot.querySelector('.nkc-foot__dock');
      var scroll = this.root.querySelector('[data-nkc-scroll]');
      if (wide) {
        if (!dock) { dock = document.createElement('div'); dock.className = 'nkc-foot__dock'; foot.insertBefore(dock, foot.firstChild); }
        // Pied aéré (07/10 soir) : la carte « Récapitulatif » (code promo + lignes) et la confiance (réassurance, logos) suivent la boîte cadeau.
        this.root.querySelectorAll('[data-nkc-scroll] .nkc-giftbox, [data-nkc-scroll] .nkc-recap, [data-nkc-scroll] .nkc-trust').forEach(function (node) { dock.appendChild(node); });
        dock.hidden = !dock.children.length;
      } else if (dock && scroll) {
        var box = scroll.querySelector('.nkc-extras--box');
        // Tour 3 du jury : le récapitulatif a sa propre carte sous la boîte cadeau (.nkc-extras--sum) ; repli sur l'ancienne carte.
        var rest = scroll.querySelector('.nkc-extras--sum') || scroll.querySelector('.nkc-extras:not(.nkc-extras--box)');
        var exit = scroll.querySelector('.nkc-exit');
        Array.prototype.slice.call(dock.children).forEach(function (node) {
          if (node.classList.contains('nkc-giftbox')) {
            // Pochon (08/10) : chaque case regagne SA carte (boîte, pochon).
            var home = node.classList.contains('nkc-giftbox--pouch') ? scroll.querySelector('.nkc-extras--pouch') : box;
            if (home) home.insertBefore(node, home.firstChild); return;
          }
          // Pied aéré : la carte « Récapitulatif » puis la confiance reprennent leur place en fin de liste, avant le lien de sortie.
          if (exit) scroll.insertBefore(node, exit); else scroll.appendChild(node);
        });
        dock.remove();
      }
    }

    /** Ligne « CODE · … » d'un code déjà expliqué sous le champ : réduite au lien « Retirer le code CODE ». */
    _markSaid(code) {
      var strings = (this._config && this._config.strings) || {};
      var wanted = String(code || '').toUpperCase();
      if (!wanted) return;
      this.root.querySelectorAll('[data-nkc-pending] [data-nkc-code-remove]').forEach(function (button) {
        var own = String(button.getAttribute('data-nkc-code-remove'));
        if (own.toUpperCase() !== wanted) return;
        button.parentNode.classList.add('is-said');
        if (strings.codeRemove) button.textContent = strings.codeRemove.replace('[code]', own.toUpperCase());
      });
    }

    _reachedNow() {
      var body = this.root.querySelector('[data-nkc-body]');
      return body ? Number(body.getAttribute('data-reached')) || 0 : 0;
    }

    _visible() {
      return this.mode === 'page' || this.hasAttribute('open');
    }

    /** Annonce le nouvel état aux autres scripts (compteur d'en-tête, barres promo…). Jamais d'évènement d'ajout. */
    _announce() {
      var cart = this.getCart();
      // La bulle du thème lit detail.cart.item_count : elle reçoit le nombre de pièces payées (décision 187), le reste du panier intact.
      var paid = this._paidCount();
      var shown = cart;
      if (cart && paid != null && paid !== cart.item_count) {
        shown = {};
        Object.keys(cart).forEach(function (key) { shown[key] = cart[key]; });
        shown.item_count = paid;
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'naked-cart', cart: shown }
      }));
      fire('updated', { cart: cart });
      this._syncBubble();
    }

    _focusPath() {
      var el = document.activeElement;
      if (!el || !this.root.contains(el)) return null;
      var line = el.closest('[data-nkc-line]');
      var role = ['data-nkc-qty', 'data-nkc-qty-input', 'data-nkc-remove', 'data-nkc-claim-add', 'data-nkc-code-remove', 'data-nkc-reco-add', 'data-nkc-gift-resize', 'data-nkc-reminder-add', 'data-nkc-code-toggle', 'data-nkc-reco-more']
        .filter(function (attr) { return el.hasAttribute(attr); })[0];
      var index = line ? Array.prototype.indexOf.call(this.root.querySelectorAll('[data-nkc-line]'), line) : 0;
      return { id: el.id, key: line ? line.getAttribute('data-key') : null, role: role, value: role ? el.getAttribute(role) : null, index: index };
    }

    _restoreFocus(path) {
      if (!path) return;
      var target = null;
      if (path.id) target = this.root.querySelector('#' + esc(path.id));
      // Après une croix : jamais une autre croix (un appui de trop sur Entrée retirerait la ligne voisine) ;
      // le titre de la ligne suivante, ou le bouton « Annuler » s'il est affiché.
      if (!target && path.role === 'data-nkc-remove') {
        this._focusWasRemove = true;
        var lines = this.root.querySelectorAll('[data-nkc-line] .nkc-line__title');
        var next = lines[Math.min(path.index || 0, lines.length - 1)];
        if (next) { next.setAttribute('tabindex', '-1'); target = next; }
      }
      if (!target && path.key && path.role) {
        var line = this.root.querySelector('[data-nkc-line][data-key="' + esc(path.key) + '"]');
        if (line) target = line.querySelector('[' + path.role + (path.value ? '="' + path.value + '"' : '') + ']');
      }
      if (!target && path.role === 'data-nkc-code-remove') target = this.root.querySelector('[data-nkc-code-form] input');
      if (!target && this._upEl && !this._upEl.hidden) target = this._upEl.querySelector('.nkc-pop__box'); // popup ouverte : jamais derrière le voile (N4)
      if (!target && this.mode === 'drawer' && this.hasAttribute('open')) target = this.root.querySelector('[data-nkc-close]') || this.panel;
      if (target && target.focus) target.focus({ preventScroll: true });
    }

    /** Message général en haut de la liste : erreur (role alert) ou information (role status). */
    _notice(text, stay, isError) {
      var node = this.root.querySelector('[data-nkc-notice]');
      if (!node || !text) return;
      node.setAttribute('role', isError ? 'alert' : 'status');
      node.textContent = text;
      node.hidden = false;
      var scroller = this._scroller();
      if (scroller && this.mode === 'drawer' && !stay) scroller.scrollTop = 0;
    }

    /**
     * Sous le champ de code : erreur (bord rouge, champ marqué invalide) ou consigne (info : « code enregistré,
     * ajoutez encore une pièce »), sans déplacer la liste.
     */
    _codeError(text, info) {
      var node = this.root.querySelector('[data-nkc-code-error]');
      var form = this.root.querySelector('[data-nkc-code-form]');
      var input = form && form.querySelector('input');
      if (form) form.classList.toggle('is-error', !!text && !info);
      if (input) {
        if (text && !info) { input.setAttribute('aria-invalid', 'true'); if (node && node.id) input.setAttribute('aria-describedby', node.id); }
        else { input.removeAttribute('aria-invalid'); input.removeAttribute('aria-describedby'); }
      }
      if (!node) return;
      node.classList.toggle('is-info', !!text && !!info);
      node.setAttribute('role', info ? 'status' : 'alert');
      node.textContent = text || '';
      node.hidden = !text;
      // Un refus : le champ se montre (le code reste écrit, sélectionné), puis se replie. Une consigne (GLOW sous le minimum, tour 3 du
      // jury, décision 160 tenue) : le champ se replie et se vide tout de suite, la puce estompée reste, la consigne reste 5 s.
      if (text && info) { this._foldCode(true); this._foldLater(); }
      else if (text) { this._unfoldCode(false); this._foldLater(); }
      // Tour 5 du jury (DA, décision 246) : le message est calé sur le champ (même bord gauche de texte), avec ou sans puce devant le champ.
      node.style.paddingLeft = '';
      if (text && !info && input && input.offsetParent && node.parentNode) {
        var shift = Math.round(input.getBoundingClientRect().left - node.getBoundingClientRect().left);
        var pad = parseFloat(root.getComputedStyle(node).paddingLeft) || 0;
        if (shift > 0) node.style.paddingLeft = (shift + pad) + 'px';
      }
    }

    /* ---------- Ouverture, fermeture, clavier ---------- */

    open() {
      var self = this;
      if (this.mode === 'page') return;
      var scroller = this._scroller();
      if (scroller) scroller.scrollTop = 0; // la liste repart du haut : la ligne ajoutée est visible
      if (this.hasAttribute('open')) return;
      this._returnFocus = document.activeElement;
      this._openedAt = performance.now();
      this._fitAll();
      // Entrée d'historique : le geste « retour » du téléphone fermera le tiroir (popstate), pas la page.
      try {
        if (root.history && typeof root.history.pushState === 'function' && !(root.history.state && root.history.state.nkc)) {
          root.history.pushState({ nkc: 1 }, '');
          this._pushed = true;
        }
      } catch (e) { this._pushed = false; }
      // Les fenêtres d'achat rapide du thème ne restent pas ouvertes derrière le tiroir.
      document.querySelectorAll('quick-buy-drawer[open], quick-buy-popover[open], cart-notification-drawer[open]').forEach(function (el) {
        if (typeof el.hide === 'function') el.hide();
      });
      // Barre de défilement d'ordinateur : sa largeur est compensée pour que la page ne saute pas.
      document.documentElement.style.setProperty('--nkc-sbw', Math.max(0, root.innerWidth - document.documentElement.clientWidth) + 'px');
      this.removeAttribute('inert');
      this.setAttribute('open', '');
      // Tour 6 du jury (clientes, majeur) : les mesures de largeur (noms des cartes du panier vide, réassurance) sont refaites une fois le
      // tiroir affiché : avant l'attribut open, le panneau n'a pas de largeur et « AUDACIOUS Bleu Marine » restait coupé.
      requestAnimationFrame(function () { if (self.hasAttribute('open')) self._fitAll(); });
      this._upPreload(); // photos de la popup chargées dès l'ouverture du tiroir (M2), jamais au tap
      document.documentElement.classList.add('nkc-lock');
      var focusTarget = this.root.querySelector('[data-nkc-close]') || this.panel;
      focusTarget.focus({ preventScroll: true });
      // Gardien du focus : un script de la page (landing qui fait status.focus()) ne l'emmène pas derrière le voile.
      this._guard = this._guard || function (event) {
        if (self.contains(event.target)) return;
        requestAnimationFrame(function () {
          if (self.hasAttribute('open') && !self.contains(document.activeElement)) {
            var popBox = self._upEl && !self._upEl.hidden ? self._upEl.querySelector('.nkc-pop__box') : null; // popup ouverte : sa boîte (N4)
            (popBox || self.root.querySelector('[data-nkc-close]') || self.panel).focus({ preventScroll: true });
          }
        });
      };
      document.addEventListener('focusin', this._guard, true);
      fire('open');
    }

    /** @param {boolean} byUser  fermeture voulue par la cliente (croix, voile, Échap) : un ajout en vol ne rouvre pas. */
    close(byUser) {
      if (this.mode === 'page' || !this.hasAttribute('open')) return;
      if (byUser) this._closedAt = Date.now();
      if (this._guard) document.removeEventListener('focusin', this._guard, true);
      this.removeAttribute('open');
      this.setAttribute('inert', '');
      document.documentElement.classList.remove('nkc-lock');
      document.documentElement.style.removeProperty('--nkc-sbw');
      this._hideUndo();
      // L'entrée d'historique posée à l'ouverture est dépilée (fermeture par la croix, le voile ou Échap) ;
      // pas au départ de la page (pagehide) : l'historique est alors celui de la navigation.
      if (this._pushed && byUser) {
        this._pushed = false;
        try { if (root.history.state && root.history.state.nkc) root.history.back(); } catch (e) { /* historique indisponible */ }
      }
      var back = this._returnFocus;
      this._returnFocus = null;
      // Élément d'origine remplacé (achat rapide re-rendu) ou absent (tiroir ouvert par un script, focus sur <body> : tour 4 du jury,
      // UX, décision 231) : repli sur l'icône panier de l'en-tête.
      if (!back || !document.contains(back) || back === document.body || back === document.documentElement || typeof back.focus !== 'function') {
        back = document.querySelector('[aria-controls="cart-drawer"], [data-naked-cart-open], a[href$="/cart"]');
      }
      if (back && document.contains(back) && back.focus) back.focus({ preventScroll: true });
      fire('close');
    }

    /** Un ajout qui vient de partir ne rouvre pas un tiroir que la cliente a fermé depuis (fenêtre de 5 s). */
    _closedSince() {
      return this._closedAt > (this._earlyAt || 0) && Date.now() - this._closedAt < 5000;
    }

    /** Ajout en cours (requête partie, réponse attendue) : ligne fantôme, jamais l'état « panier vide ». */
    /** @param {boolean} atEnd  cadeau en route : il arrivera en fin de liste, le fantôme s'y place aussi. */
    ghost(atEnd) {
      var list = this.root.querySelector('.nkc-lines');
      if (!list || list.querySelector('[data-nkc-ghost]')) return;
      var item = document.createElement('li');
      item.className = 'nkc-line nkc-skel';
      item.setAttribute('data-nkc-ghost', atEnd ? 'end' : '');
      item.setAttribute('aria-hidden', 'true');
      item.innerHTML = '<span class="nkc-skel__img"></span><span class="nkc-skel__main"><span class="nkc-skel__bar"></span><span class="nkc-skel__bar nkc-skel__bar--sm"></span></span>';
      if (atEnd) list.appendChild(item); else list.insertBefore(item, list.firstChild);
    }

    _focusables() {
      var up = this._upEl && !this._upEl.hidden ? this._upEl.querySelector('.nkc-pop__box') : null; // QA B8 : ancien nom .nkc-up__sheet
      var scope = up || this.root.querySelector('[data-nkc-reminder]:not([hidden])') || this.panel;
      return Array.prototype.filter.call(
        scope.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), [tabindex="0"]'),
        function (el) { return el.offsetParent !== null; }
      );
    }

    _onKeydown(event) {
      if (event.key === 'Escape' && this._upEl && !this._upEl.hidden && !(this._up && this._up.leaving)) { event.stopPropagation(); this._upLogStep(this._up.screen, 'x'); this._upLeave(false); this._upClose(true); return; } // Échap = croix : vers le paiement (19/10/2026)
      if (event.key === 'Escape') {
        var reminder = this.root.querySelector('[data-nkc-reminder]:not([hidden])');
        if (reminder) {
          reminder.hidden = true;
          event.stopPropagation();
          var pay = this.root.querySelector('[data-nkc-checkout] [name="checkout"]');
          if (pay) pay.focus({ preventScroll: true });
          return;
        }
        if (this.mode === 'drawer') { event.stopPropagation(); this.close(true); }
        return;
      }
      if (event.key === 'Enter' && event.target.matches('[data-nkc-qty-input]')) {
        event.preventDefault();
        this._commitQuantity(event.target);
        return;
      }
      if (event.key !== 'Tab') return;
      var trapped = this.mode === 'drawer' || this.root.querySelector('[data-nkc-reminder]:not([hidden])') || (this._upEl && !this._upEl.hidden);
      if (!trapped) return;
      var list = this._focusables();
      if (!list.length) { event.preventDefault(); return; }
      var first = list[0];
      var last = list[list.length - 1];
      var active = document.activeElement;
      if (this._upEl && !this._upEl.hidden) {
        // Popup ouverte : Tab tourne dans ses boutons, toujours à la main (QA B8). Safari ne tabule pas les boutons par défaut :
        // laissé au navigateur, le focus sortait vers le tiroir et les champs de la page derrière (banc du 17/10 : 3 Tab sur 12).
        event.preventDefault();
        var at = list.indexOf(active);
        var next = event.shiftKey ? (at <= 0 ? last : list[at - 1]) : (at === -1 || at === list.length - 1 ? first : list[at + 1]);
        next.focus();
        return;
      }
      if (event.shiftKey && (active === first || !this.contains(active) || active === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------------------------------------------------------------- */
    /* 5. Actions                                                         */
    /* ---------------------------------------------------------------- */

    _onClick(event) {
      var target = event.target;
      var hit = function (selector) { return target.closest(selector); };
      var node;

      // Popup avant paiement (US, 17/10/2026) : hors du rendu de la section, ses gestes passent ici en premier.
      if (hit('[data-nkc-up]')) {
        // Croix, voile et Échap partent au paiement sur TOUS les écrans (Mathis, 19/10/2026 : « Bien sûr, corrige la croix ») : sur l'écran Pièces
        // ou Palier, la croix renvoyait au panier ; le groupe B y perdait ses paniers à 1 pièce (66 commandes contre 88 en A, 17-19/10).
        // La fenêtre se ferme tout de suite ; « One moment… » sur le bouton de paiement du panier jusqu'au départ (_upLeave → _payLabel).
        if (hit('[data-nkc-up-close]')) { if (this._up) { this._upLogStep(this._up.screen, 'x'); this._upLeave(false); this._upClose(true); } return; }
        if ((node = hit('[data-nkc-up-swatch]'))) { if (this._up && !node.disabled) { this._up.colourIdx = Number(node.getAttribute('data-nkc-up-swatch')) || 0; this._upPaint(this._upData()); } return; } // nuancier v2 (25/10/2026)
        if ((node = hit('[data-nkc-up-add]'))) { this._upAddPiece(node); return; }
        if ((node = hit('[data-nkc-up-go]'))) { if (node.hasAttribute('data-pack')) this._upAddPack(); else this._upNext(); return; }
        if (hit('[data-nkc-up-skip]')) { if (this._up) this._upLogStep(this._up.screen, 'no'); this._upLeave(false); return; }
        return;
      }

      if (hit('[data-nkc-close]')) {
        if (hit('[data-nkc-exit]')) fire('exit_click'); // lien « Continuer mes achats · plus que … » (levier 12)
        this.close(true);
        return;
      }
      // Panneau encore en train de glisser : un second appui impatient ne tombe pas sur ce qui passe sous le doigt.
      if (event.isTrusted && this.mode === 'drawer' && performance.now() - (this._openedAt || 0) < 350) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // Toute autre action de la cliente retire le message « retiré · Annuler ».
      if (this._undoShown && !hit('[data-nkc-undo]') && !hit('[data-nkc-slide]') && hit('button, a[href], input, select')) this._hideUndo();

      if ((node = hit('[data-nkc-qty]'))) {
        var line = node.closest('[data-nkc-line]');
        if (line.hasAttribute('data-removing')) return;
        // La quantité visée part de l'état affiché, mis à jour tout de suite : aucun clic rapide n'est perdu.
        this._setQuantity(line, Number(line.getAttribute('data-qty')) + Number(node.getAttribute('data-nkc-qty')));
        return;
      }

      if ((node = hit('[data-nkc-undo]'))) {
        var payload = this._undoPayload;
        if (node.getAttribute('aria-busy') === 'true') return;
        if (!payload) { this._hideUndo(); return; }
        // Tour 5 du jury (UX, décision 245) : le bandeau reste pendant la requête et son bouton montre qu'il travaille (anneau) ;
        // le rendu qui suit le remplace (le message n'est plus rejoué : état effacé tout de suite, filet de sûreté à 6 s).
        clearTimeout(this._undoTimer);
        this._undoPayload = null;
        this._undoShown = null;
        this._focusWasRemove = false;
        node.setAttribute('aria-busy', 'true');
        var selfUndo = this;
        this._undoTimer = setTimeout(function () { selfUndo._hideUndo(); }, 6000);
        if (payload.code) { this._removeCode(payload.code); return; } // code qui coûtait un cadeau : retiré
        if (payload.gift) this._claimGift(payload.tier, payload.id); // cadeau retiré : repris (refus oublié)
        else { this._justAdded = String(payload.id); this._add([{ id: payload.id, quantity: payload.quantity, properties: payload.properties }]); }
        return;
      }

      if ((node = hit('[data-nkc-gift-pick]'))) {
        // Tour 4 du jury (UX, décision 223) : l'échange de taille part au tap d'une AUTRE pastille, en une requête ; la taille
        // actuelle referme la rangée.
        var pline = node.closest('[data-nkc-line]');
        if (!pline) return;
        if (node.getAttribute('aria-pressed') === 'true') { this._giftUnpick(pline); return; }
        this._resizeGift(pline, node.getAttribute('data-nkc-gift-pick-tier'), node.getAttribute('data-nkc-gift-pick'));
        return;
      }

      if ((node = hit('[data-nkc-gift-resize]'))) {
        var gline = node.closest('[data-nkc-line]');
        if (!gline || gline.hasAttribute('data-removing')) return;
        var rtier = node.getAttribute('data-nkc-gift-resize');
        // Tour 4 du jury (UX, décision 223) : les pastilles s'ouvrent DANS la ligne du cadeau (aucun retrait, aucun défilement) ;
        // sans fenêtre de rappel dans la page (réglage coupé), l'ancien chemin reste : la ligne part, la carte de taille revient.
        if (this._giftPicks(gline, rtier)) return;
        // Body offert : la ligne part (sans compter comme un refus), la taille est oubliée, la carte « Choisissez votre
        // taille » revient en haut (le rendu y remonte : nouvelle carte).
        var rsizes = store.get('sizes', {});
        if (rsizes[String(rtier)]) { delete rsizes[String(rtier)]; store.set('sizes', rsizes); }
        gline.setAttribute('data-removing', '');
        this._lastKey = gline.getAttribute('data-key');
        this._changeLine(gline.getAttribute('data-key'), 0, null, 'remove');
        return;
      }

      if ((node = hit('[data-nkc-remove]'))) {
        var row = node.closest('[data-nkc-line]');
        if (row.hasAttribute('data-removing')) return; // second clic sur la même croix : ignoré
        var tier = row.getAttribute('data-gift-tier');
        // « [title] retiré · Annuler » : ce qu'il faudra remettre (une unité, mêmes propriétés ; un cadeau : le reprendre).
        var known = ((this._cart && this._cart.items) || []).filter(function (it) { return it.key === row.getAttribute('data-key'); })[0];
        this._undo = {
          key: row.getAttribute('data-key'),
          id: Number(row.getAttribute('data-vid')) || (known && known.variant_id),
          quantity: row.hasAttribute('data-unit') ? 1 : Math.max(1, Number(row.getAttribute('data-qty')) || 1),
          properties: known && known.properties && Object.keys(known.properties).length ? known.properties : undefined,
          title: row.getAttribute('data-title') || '',
          gift: !!tier,
          tier: tier
        };
        this._lastCause = 'remove';
        if (tier) {
          fire('gift_decline', { tier: tier });
          this._decline(tier); // cadeau retiré : non réimposé
          var sizes = store.get('sizes', {});
          if (sizes[String(tier)]) { delete sizes[String(tier)]; store.set('sizes', sizes); } // la taille non plus
        }
        if (row.hasAttribute('data-unit')) {
          // Article affiché sur une ligne par unité (ou « × N ») : la croix retire UNE unité de la ligne Shopify.
          // La quantité envoyée est relue sur un panier frais au moment de l'envoi (clé périmée, autre onglet).
          var left = Math.max(0, Number(row.getAttribute('data-qty')) - 1);
          this.root.querySelectorAll('[data-nkc-line][data-key="' + esc(row.getAttribute('data-key')) + '"]')
            .forEach(function (mate) { mate.setAttribute('data-qty', left); });
          row.setAttribute('data-removing', '');
          this._setQuantity(row, left, true, { variantId: row.getAttribute('data-vid') });
          return;
        }
        this._setQuantity(row, 0, true);
        return;
      }

      if ((node = hit('[data-nkc-claim-add]'))) {
        if (node.getAttribute('aria-busy') === 'true') return; // double appui : un seul exemplaire
        node.setAttribute('aria-busy', 'true');
        if (node.hasAttribute('aria-pressed')) node.setAttribute('aria-pressed', 'true');
        var claimTier = node.closest('[data-nkc-claim]').getAttribute('data-tier');
        fire('gift_claim', { tier: claimTier, variantId: node.getAttribute('data-nkc-claim-add') });
        this._claimGift(claimTier, node.getAttribute('data-nkc-claim-add'));
        return;
      }

      if ((node = hit('[data-nkc-box-off]'))) {
        // Récapitulatif (08/10) : la croix « Boîte cadeau premium +5,90€ » décoche la case de la carte boîte (même chemin, _giftbox).
        var boxInput = this.root.querySelector('[data-nkc-giftbox]:not([data-nkc-pouch])');
        if (boxInput) { boxInput.checked = false; this._giftbox(Number(boxInput.getAttribute('data-nkc-giftbox')), false); }
        return;
      }
      if ((node = hit('[data-nkc-pouch-off]'))) {
        // Pochon de rangement (08/10) : même geste que la boîte, sur sa propre case.
        var pouchInput = this.root.querySelector('[data-nkc-giftbox][data-nkc-pouch]');
        if (pouchInput) { pouchInput.checked = false; this._giftbox(Number(pouchInput.getAttribute('data-nkc-giftbox')), false, 'pouch'); }
        return;
      }

      if ((node = hit('[data-nkc-code-remove]'))) {
        // Tour 5 du jury (UX, décisions 245 et 247) : le bouton de retrait montre qu'il travaille (aria-busy → anneau), et le focus
        // revient ensuite sur la rangée « Ajouter un code promo » (plus jamais sur « Fermer le panier »). Récap 3 (décision 303) : le retrait
        // part de la ligne « Code GLOW · Retirer » de la colonne (nkc-sum__drop) ou de la puce d'un code sans effet (nkc-chip__x).
        if (node.getAttribute('aria-busy') === 'true') return;
        node.setAttribute('aria-busy', 'true');
        if (node.classList.contains('nkc-chip__x') || node.classList.contains('nkc-sum__drop')) this._focusCodeToggle = true;
        this._removeCode(node.getAttribute('data-nkc-code-remove'));
        return;
      }

      if ((node = hit('[data-nkc-code-toggle]'))) { this._unfoldCode(true); return; }
      if ((node = hit('[data-nkc-code-close]'))) { this._refused = null; this._foldCode(); return; }
      if ((node = hit('[data-nkc-code-quick]'))) {
        // « Appliquer GLOW » : le code de l'offre est saisi et envoyé par le formulaire du pied (même chemin qu'une saisie).
        if (node.getAttribute('aria-busy') === 'true') return;
        node.setAttribute('aria-busy', 'true');
        this._applyCode(node.getAttribute('data-nkc-code-quick'));
        return;
      }

      if ((node = hit('[data-nkc-reco-more]'))) {
        // Recommandations repliées (tous les paliers atteints) : « More to love » les déplie.
        var folded = node.closest('[data-nkc-reco]');
        if (folded) folded.classList.remove('is-folded');
        node.setAttribute('aria-expanded', 'true');
        node.hidden = true;
        fire('reco_unfold');
        return;
      }

      if ((node = hit('[data-nkc-picks-close]'))) {
        var closing = node.closest('[data-nkc-reco-card]');
        if (closing) this._unpick(closing);
        return;
      }

      if ((node = hit('[data-nkc-size-change]'))) {
        var changing = node.closest('[data-nkc-reco-card]');
        if (changing) { if (changing.classList.contains('is-picking')) this._unpick(changing); else this._picks(changing); }
        return;
      }

      if ((node = hit('[data-nkc-pick]'))) {
        var pickCard = node.closest('[data-nkc-reco-card]');
        if (!pickCard || node.disabled) return;
        var pickSelects = pickCard.querySelectorAll('select[data-nkc-reco-opt], select[data-nkc-reco-variant]');
        var pickSelect = pickSelects[Number(node.getAttribute('data-nkc-pick'))];
        if (!pickSelect) return;
        pickSelect.value = node.getAttribute('data-value');
        pickSelect.dispatchEvent(new Event('change', { bubbles: true }));
        this._picks(pickCard);
        // Carte d'écart (pastilles de 44 × 40, décision 150) : une seule option (S, M, L…), le choix de la taille ajoute ;
        // ensemble (haut + bas) : la carte se referme sur « Taille M/L · changer » et « Ajouter » confirme.
        // Carte de recommandation (pastilles de 28 px, tour 2 du jury, décision 184) : un tap choisit, « Ajouter · M » confirme.
        var pickLeft = Array.prototype.filter.call(pickSelects, function (one) { return !one.value; }).length;
        var pickAdd = pickCard.querySelector('.nkc-rc__actions [data-nkc-reco-add]');
        var pickFiller = !!pickCard.closest('[data-nkc-filler]');
        // Tour 6 du jury (UX) : la rangée de pastilles se referme AVANT que l'ajout parte (une seule commande à l'écran : « Taille M » + anneau).
        if (!pickLeft && pickFiller && pickSelects.length === 1 && pickAdd && !pickAdd.disabled) { this._unpick(pickCard); this._recoAdd(pickCard, pickAdd); }
        else if (!pickLeft && pickSelects.length > 1 && pickFiller) this._unpick(pickCard);
        return;
      }

      if ((node = hit('[data-nkc-reco-add]'))) {
        if (node.getAttribute('aria-busy') === 'true' || node.disabled) return; // double appui : un seul exemplaire ; combinaison indisponible : rien
        var card = node.closest('[data-nkc-reco-card]');
        var picker = card.querySelector('[data-nkc-reco-variant]');
        if (!picker) return;
        var sized = card.querySelectorAll('select[data-nkc-reco-opt], select[data-nkc-reco-variant]').length > 0;
        var confirmed = node.hasAttribute('data-nkc-picks-go');
        var inFiller = !!card.closest('[data-nkc-filler]');
        if (sized && !confirmed) {
          // Aucune taille choisie : les pastilles s'ouvrent, rien n'est envoyé (cro-ARBITRAGE n° 2).
          if (!picker.value) { this._picks(card); return; }
          // Taille reprise du panier : jamais ajoutée sans être lue. Carte d'écart : elle est écrite sur la carte
          // (« Taille M · changer »), l'ajout part. Carte de recommandation : les pastilles s'ouvrent, taille cochée,
          // et le bouton « Ajouter · M » confirme.
          if (!inFiller) { this._picks(card); return; }
        }
        if (!picker.value) return;
        this._recoAdd(card, node);
        return;
      }

      if ((node = hit('.nkc-rc__title, .nkc-rc__media'))) {
        var clicked = node.closest('[data-nkc-reco-card]');
        if (clicked) fire('reco_click', { productId: clicked.getAttribute('data-pid'), filler: !!clicked.closest('[data-nkc-filler]') });
        return; // navigation normale
      }

      if ((node = hit('[data-nkc-slide]'))) {
        if (node.getAttribute('aria-disabled') === 'true') return;
        var track = node.closest('[data-nkc-reco]').querySelector('[data-nkc-reco-track]');
        var step = Number(node.getAttribute('data-nkc-slide')) * track.clientWidth * 0.85;
        if (typeof track.scrollBy === 'function') track.scrollBy({ left: step, behavior: 'smooth' }); else track.scrollLeft += step;
        return;
      }

      // Rappel au paiement : clic sur le fond ou sur la croix = fermer, le focus revient sur « Paiement ».
      if ((node = hit('[data-nkc-reminder]:not([hidden])')) && (!hit('.nkc-modal__box') || hit('[data-nkc-reminder-close]'))) {
        node.hidden = true;
        var payBtn = this.root.querySelector('[data-nkc-checkout] [name="checkout"]');
        if (payBtn) payBtn.focus({ preventScroll: true });
        return;
      }

      // Rappel au paiement en un geste (cro-ARBITRAGE n° 3) : la taille (ou « Ajouter mon cadeau et payer ») ajoute le
      // cadeau ET part au paiement dès que l'ajout est fini (quatre secondes au plus) ; un ajout refusé n'empêche pas de payer.
      if ((node = hit('[data-nkc-reminder-add]'))) {
        if (node.getAttribute('aria-busy') === 'true') return;
        node.setAttribute('aria-busy', 'true');
        var remTier = node.closest('[data-nkc-reminder-tier]');
        var remModal = this.root.querySelector('[data-nkc-reminder]');
        if (remModal) remModal.hidden = true;
        var tierId = remTier ? remTier.getAttribute('data-nkc-reminder-tier') : null;
        fire('reminder_pick', { tier: tierId, variantId: node.getAttribute('data-nkc-reminder-add') });
        this._skipReminder = true;
        this._claimGift(tierId, node.getAttribute('data-nkc-reminder-add'));
        this._payWhenIdle();
        return;
      }

      if (hit('[data-nkc-protect-skip]')) {
        fire('protect_skip');
        this._protectWant = false;
        this._pay();
        return;
      }

      if (hit('[data-nkc-reminder-no]')) {
        fire('reminder_skip');
        this._skipReminder = true;
        this._pay();
      }
    }

    _onChange(event) {
      var target = event.target;
      if (target.matches('[data-nkc-qty-input]')) { this._commitQuantity(target); return; }
      if (target.matches('[data-nkc-giftbox]')) {
        var isPouch = target.hasAttribute('data-nkc-pouch');
        fire(isPouch ? 'pouch_toggle' : 'giftbox_toggle', { on: !!target.checked });
        this._giftbox(Number(target.getAttribute('data-nkc-giftbox')), target.checked, isPouch ? 'pouch' : 'box');
        return;
      }
      // Taille choisie dans une recommandation : retenue, réappliquée après un rendu (la carte est re-rendue).
      if (target.matches('[data-nkc-reco-variant]')) {
        target.classList.remove('is-missing');
        var card = target.closest('[data-nkc-reco-card]');
        if (card) this._recoPicks[card.getAttribute('data-pid')] = target.value;
        return;
      }
      // Ensemble (décision 121) : un choix par option, retenu par option ; la variante est recomposée.
      if (target.matches('select[data-nkc-reco-opt]')) {
        target.classList.remove('is-missing');
        var optCard = target.closest('[data-nkc-reco-card]');
        if (!optCard) return;
        var pid = optCard.getAttribute('data-pid');
        var picks = this._recoPicks[pid];
        if (!picks || typeof picks !== 'object') picks = this._recoPicks[pid] = {};
        picks[target.getAttribute('data-nkc-reco-opt')] = target.value;
        this._recoResolve(optCard);
      }
    }

    /**
     * Carte à plusieurs options (ensemble soutien-gorge + string, parfois couleur ; décision 121) : la variante est
     * recomposée à partir des choix. data-variants (rendu par le Liquid) = variantes DISPONIBLES et leurs options.
     * Dans chaque sélecteur, une valeur sans variante disponible compatible avec les AUTRES choix est éteinte (disabled,
     * jamais retirée : le choix de la cliente reste visible). Tous les choix faits : le champ caché reçoit l'identifiant ;
     * aucune variante disponible pour cette combinaison (un choix précédent rendu incompatible) : « Ajouter » est
     * désactivé et dit « Indisponible » jusqu'au prochain changement. Choix incomplet : champ vide, bouton actif
     * (le clic montre le sélecteur manquant). Aucune valeur n'est choisie à la place de la cliente.
     */
    /** Ajout d'une carte (recommandation, carte d'écart, best-seller du panier vide) dont la variante est connue. */
    _recoAdd(card, node) {
      var picker = card.querySelector('[data-nkc-reco-variant]');
      if (!picker || !picker.value) return;
      node.setAttribute('aria-busy', 'true');
      this.ghost();
      var filler = !!card.closest('[data-nkc-filler]');
      var position = Array.prototype.indexOf.call(card.parentNode.querySelectorAll('[data-nkc-reco-card]'), card) + 1;
      fire(filler ? 'filler_add' : 'reco_add', {
        productId: card.getAttribute('data-pid'), variantId: picker.value, price: Number(card.getAttribute('data-price')) || null,
        position: position, gap: card.hasAttribute('data-gap-first') || filler, fromGapCollection: card.hasAttribute('data-gap-card'),
        empty: !!card.closest('[data-nkc-empty-reco]')
      });
      this._justAdded = String(picker.value); // tour 6 du jury (UX) : le focus ira sur la ligne ajoutée
      this._add([{ id: Number(picker.value), quantity: 1 }]);
    }

    /** Pastilles de taille d'une carte (une rangée par sélecteur, étiquetée pour un ensemble) : reflet des sélecteurs, qui restent la source. */
    _picks(card) {
      var selects = card.querySelectorAll('select[data-nkc-reco-opt], select[data-nkc-reco-variant]');
      if (!selects.length) return;
      var strings = (this._config && this._config.strings) || {};
      var inFiller = !!card.closest('[data-nkc-filler]');
      var box = card.querySelector('[data-nkc-picks]');
      if (!box) {
        box = document.createElement('div');
        box.className = 'nkc-picks';
        box.setAttribute('data-nkc-picks', '');
        card.appendChild(box);
      }
      // Cartes de recommandation : les pastilles se posent SUR le bas de l'image, le nom et le prix restent visibles dessous.
      var caption = card.querySelector('.nkc-rc__main');
      if (!inFiller && caption) card.style.setProperty('--nkc-cap', caption.offsetHeight + 'px');
      box.innerHTML = '';
      var chosen = [];
      var complete = true;
      // Option qui n'est pas une taille (chaîne WILSON : « Taille » = Or / Argent) : légende neutre « Votre choix », jamais « Taille ».
      var sizeLike = /^(XXS|XS|S|M|L|XL|XXL|XXXL|\d+[A-Z]?|[A-Z]{1,3})$/i;
      var isSizeSelect = function (select) {
        return Array.prototype.every.call(select.options, function (option) { return !option.value || sizeLike.test((option.textContent || '').trim()); });
      };
      var allSizes = Array.prototype.every.call(selects, isSizeSelect);
      Array.prototype.forEach.call(selects, function (select, index) {
        var row = document.createElement('div');
        row.className = 'nkc-picks__row';
        row.setAttribute('role', 'group');
        row.setAttribute('aria-label', select.getAttribute('aria-label') || '');
        var wrap = select.closest('.nkc-rc__opt');
        var name = wrap && wrap.querySelector('.nkc-rc__optname');
        var legend = name && selects.length > 1 ? name.textContent : (inFiller ? (isSizeSelect(select) ? strings.size : strings.pickOption || strings.size) : '');
        // Tour 6 du jury (UX, majeur) : sur une carte de recommandation, une option unique qui n'est pas une taille (WILSON : Or / Argent)
        // porte son nom en légende (« Couleur », lu dans l'aria-label « Couleur · WILSON » rendu par le Liquid).
        if (!legend && !inFiller && selects.length === 1 && !isSizeSelect(select)) legend = String(select.getAttribute('aria-label') || '').split(' · ')[0] || strings.pickOption || '';
        if (legend) {
          var label = document.createElement('span');
          label.className = 'nkc-picks__name';
          label.textContent = legend;
          row.appendChild(label);
        }
        Array.prototype.forEach.call(select.options, function (option) {
          if (!option.value) return;
          var chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'nkc-pick';
          chip.setAttribute('data-nkc-pick', String(index));
          chip.setAttribute('data-value', option.value);
          chip.setAttribute('aria-pressed', select.value === option.value ? 'true' : 'false');
          chip.disabled = option.disabled;
          chip.textContent = (option.textContent || '').trim();
          row.appendChild(chip);
        });
        box.appendChild(row);
        var picked = select.options[select.selectedIndex];
        if (select.value && picked) chosen.push((picked.textContent || '').trim().toUpperCase()); else complete = false;
      });
      var addButton = card.querySelector('.nkc-rc__actions [data-nkc-reco-add]');
      if (!inFiller) {
        // Tailles toutes choisies (reprises du panier ou choisies ici) : la cliente confirme la taille qu'elle lit.
        if (complete && !(addButton && addButton.disabled)) {
          var go = document.createElement('button');
          go.type = 'button';
          go.className = 'nkc-picks__go';
          go.setAttribute('data-nkc-reco-add', '');
          go.setAttribute('data-nkc-picks-go', '');
          // Tour 3 du jury (clientes) : « AJOUTER M/M » sans point médian, et le corps descend (plancher 9,5 px) plutôt qu'une ellipse
          // sur la taille (« AJOUTER · M… » à 97 px) : la cliente valide une taille qu'elle lit.
          go.textContent = (strings.add || '') + ' ' + chosen.join('/');
          go.setAttribute('aria-label', (strings.add || '') + ' · ' + chosen.join('/'));
          box.appendChild(go);
          this._fitInline(go, 9.5);
        }
        var close = document.createElement('button');
        close.type = 'button';
        close.className = 'nkc-picks__close';
        close.setAttribute('data-nkc-picks-close', '');
        close.setAttribute('aria-label', strings.closeDialog || strings.close || '');
        close.innerHTML = '<svg aria-hidden="true" focusable="false" width="8" height="8" viewBox="0 0 14 14"><path d="M1 1l12 12M13 1L1 13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
        box.appendChild(close);
      } else if (addButton && !complete && strings.chooseSize) {
        // Carte d'écart ouverte : le bouton dit quoi faire, l'ajout part au choix de la taille (« Choisissez » seul si l'option n'est pas une taille).
        var longChoose = allSizes ? strings.chooseSize : (strings.choose || strings.chooseSize);
        addButton.textContent = longChoose;
        addButton.setAttribute('aria-disabled', 'true');
        // Tour 5 du jury (UX, décision 249) puis décision 299 : le bouton a sa propre rangée ; si « CHOISISSEZ VOTRE TAILLE » (244 px) déborde
        // encore de la carte, il garde le prix et un mot (« Taille · 40,00€ », « Größe · €45,00 »), le libellé long reste dans aria-label.
        var fillerPrice = card.querySelector('.nkc-rc__amount > span');
        if (fillerPrice && card.clientWidth > 0 && card.scrollWidth > card.clientWidth + 1) {
          var word = allSizes ? (strings.size || '') : (strings.choose || '');
          addButton.textContent = (word ? word + ' · ' : '') + (fillerPrice.textContent || '').trim();
          addButton.setAttribute('aria-label', longChoose + ' · ' + (fillerPrice.textContent || '').trim());
        } else addButton.removeAttribute('aria-label');
      }
      card.classList.add('is-picking');
      this._sizeNote(card);
      if (inFiller) this._fitFiller();
    }

    /** Referme les pastilles d'une carte. */
    _unpick(card) {
      var box = card.querySelector('[data-nkc-picks]');
      if (box) box.remove();
      card.classList.remove('is-picking');
      var addButton = card.querySelector('.nkc-rc__actions [data-nkc-reco-add]');
      if (addButton && addButton.hasAttribute('aria-disabled')) {
        addButton.removeAttribute('aria-disabled');
        if (!addButton.disabled) addButton.textContent = addButton.getAttribute('data-label') || addButton.textContent;
        if (!card.classList.contains('is-tight')) addButton.removeAttribute('aria-label');
      }
      if (card.closest('[data-nkc-filler]')) this._fitFiller();
    }

    /**
     * Carte d'écart : la taille reprise du panier est ÉCRITE sur la carte (« Taille M · changer ») avant l'ajout en un geste ;
     * « changer » ouvre les pastilles. Sans taille connue : le type de pièce reste affiché.
     */
    _sizeNote(card) {
      if (!card.closest('[data-nkc-filler]')) return;
      var strings = (this._config && this._config.strings) || {};
      var selects = card.querySelectorAll('select[data-nkc-reco-opt], select[data-nkc-reco-variant]');
      var note = card.querySelector('[data-nkc-size-note]');
      var chosen = [];
      var complete = selects.length > 0;
      var sizeLike = /^(XXS|XS|S|M|L|XL|XXL|XXXL|\d+[A-Z]?|[A-Z]{1,3})$/i;
      var allSizes = true;
      Array.prototype.forEach.call(selects, function (select) {
        var picked = select.options[select.selectedIndex];
        if (select.value && picked) chosen.push((picked.textContent || '').trim().toUpperCase()); else complete = false;
        Array.prototype.forEach.call(select.options, function (option) { if (option.value && !sizeLike.test((option.textContent || '').trim())) allSizes = false; });
      });
      if (!complete) { if (note) note.remove(); card.classList.remove('has-size'); return; }
      if (!note) {
        note = document.createElement('span');
        note.className = 'nkc-rc__size';
        note.setAttribute('data-nkc-size-note', '');
        var after = card.querySelector('[data-nkc-filler-text]') || card.querySelector('.nkc-rc__title');
        if (after && after.parentNode) after.parentNode.insertBefore(note, after.nextSibling);
      }
      note.innerHTML = '';
      var label = document.createElement('span');
      label.textContent = (allSizes ? (strings.size || '') + ' ' : '') + chosen.join('/');
      var change = document.createElement('button');
      change.type = 'button';
      change.className = 'nkc-rc__change';
      change.setAttribute('data-nkc-size-change', '');
      change.textContent = strings.sizeChange || '';
      note.appendChild(label);
      note.appendChild(change);
      card.classList.add('has-size');
      var addButton = card.querySelector('.nkc-rc__actions [data-nkc-reco-add]');
      if (addButton && addButton.hasAttribute('aria-disabled')) {
        addButton.removeAttribute('aria-disabled');
        if (!addButton.disabled) addButton.textContent = addButton.getAttribute('data-label') || addButton.textContent;
      }
    }

    _recoResolve(card) {
      var selects = card.querySelectorAll('select[data-nkc-reco-opt]');
      var hidden = card.querySelector('input[data-nkc-reco-variant]');
      var button = card.querySelector('[data-nkc-reco-add]');
      if (!selects.length || !hidden) return;
      var variants = card._nkcVariants;
      if (!variants) {
        try { variants = JSON.parse(card.getAttribute('data-variants') || '[]'); } catch (e) { variants = []; }
        if (!Array.isArray(variants)) variants = [];
        card._nkcVariants = variants;
      }
      var chosen = {};
      var complete = true;
      Array.prototype.forEach.call(selects, function (s) {
        var i = s.getAttribute('data-nkc-reco-opt');
        if (s.value) chosen[i] = s.value; else complete = false;
      });
      var fits = function (v, skip) {
        var o = v && v.o;
        if (!o) return false;
        return Object.keys(chosen).every(function (i) { return i === skip || o[Number(i)] === chosen[i]; });
      };
      Array.prototype.forEach.call(selects, function (s) {
        var i = s.getAttribute('data-nkc-reco-opt');
        Array.prototype.forEach.call(s.options, function (o) {
          if (!o.value) return;
          o.disabled = !variants.some(function (v) { return v.o && v.o[Number(i)] === o.value && fits(v, i); });
        });
      });
      var match = complete ? variants.filter(function (v) { return fits(v, null); })[0] : null;
      hidden.value = match ? String(match.id) : '';
      var blocked = complete && !match;
      card.classList.toggle('is-unavailable', blocked);
      if (button) {
        button.disabled = blocked;
        var label = blocked ? button.getAttribute('data-unavailable') : button.getAttribute('data-label');
        if (label) button.textContent = label;
      }
    }

    /**
     * Boîte cadeau : quantité FIXÉE à 1 ou 0 par cart/update.js (jamais ajoutée : deux coches rapides ne
     * donnent pas deux boîtes). Le DERNIER geste de la cliente fait foi (mémorisé au clic, lu au moment de
     * l'envoi), une seule tâche en attente à la fois. Reconnue ensuite par son produit, plus par une propriété.
     */
    _giftbox(variantId, checked, slot) {
      var self = this;
      // Pochon (08/10) : une volonté et une tâche PAR option ('box', 'pouch') ; la boîte garde ses champs d'origine.
      slot = slot || 'box';
      this._optWanted = this._optWanted || {}; this._optTask = this._optTask || {};
      this._optWanted[slot] = !!checked;
      if (slot === 'box') this._giftboxWanted = !!checked;
      if (this._optTask[slot]) return;
      this._optTask[slot] = true;
      if (slot === 'box') this._giftboxTask = true;
      return this._enqueue(function () {
        self._optTask[slot] = false;
        if (slot === 'box') self._giftboxTask = false;
        var updates = {};
        updates[String(variantId)] = self._optWanted[slot] ? 1 : 0;
        return self._mutation('cart/update.js', { updates: updates }, { context: self._optWanted[slot] ? 'add' : 'remove' });
      }, 'add');
    }

    /**
     * Quantité d'une ligne : affichée tout de suite, envoyée après un court regroupement des clics
     * (un seul cart/change.js avec la quantité finale), plafonnée à 99.
     */
    _setQuantity(line, quantity, now, unit) {
      var self = this;
      var key = line.getAttribute('data-key');
      if (quantity > MAX_QTY) { this._sticky = this._config && this._config.strings.qtyMax; this._notice(this._sticky, true); }
      quantity = Math.max(0, Math.min(MAX_QTY, quantity));
      line.setAttribute('data-qty', quantity);
      this._lastKey = key;
      var input = line.querySelector('[data-nkc-qty-input]');
      if (input) input.value = quantity;
      if (quantity === 0) line.setAttribute('data-removing', '');
      line.setAttribute('data-pending', '');
      clearTimeout((this._qtyTimers[key] || {}).timer);
      var send = function () {
        delete self._qtyTimers[key];
        self._changeLine(key, quantity, unit, 'remove');
      };
      if (now) { delete this._qtyTimers[key]; send(); return; }
      this._qtyTimers[key] = { timer: setTimeout(send, 220), send: send };
    }

    /** Envoie tout de suite les quantités en attente de regroupement (avant un paiement). */
    _flushQuantities() {
      var timers = this._qtyTimers;
      Object.keys(timers).forEach(function (key) {
        clearTimeout(timers[key].timer);
        timers[key].send();
      });
    }

    /** Saisie directe d'une quantité (Entrée ou sortie du champ). */
    _commitQuantity(input) {
      var line = input.closest('[data-nkc-line]');
      var current = Number(line.getAttribute('data-qty'));
      var quantity = parseInt(input.value, 10);
      if (isNaN(quantity) || quantity < 0) { input.value = current; return; }
      if (quantity > MAX_QTY && current === MAX_QTY) this._notice(this._config && this._config.strings.qtyMax, true);
      if (Math.min(MAX_QTY, quantity) === current) { input.value = current; return; }
      this._setQuantity(line, quantity, true); // data-qty mis à jour : pas de second envoi (Entrée puis change)
    }

    _onSubmit(event) {
      var form = event.target;
      if (form.matches('[data-nkc-code-form]')) {
        event.preventDefault();
        var input = form.querySelector('input');
        var code = input.value.trim();
        if (!code) { input.focus(); return; }
        var submit = form.querySelector('[type="submit"]');
        if (submit) {
          if (submit.getAttribute('aria-busy') === 'true') return; // double appui
          submit.setAttribute('aria-busy', 'true');
        }
        this._applyCode(code);
        return;
      }
      if (form.matches('[data-nkc-checkout]')) {
        if (event.submitter && event.submitter.name === 'checkout') this._protectWant = true; // « Protected Checkout »
        this._flushQuantities();
        // Correctif paiement (07/10) : le départ n'attend que les ÉCRITURES en vol (ajout du thème, cadeau,
        // quantité, code), 1 200 ms au plus, jamais le re-rendu de la section ni les recommandations. Le panier
        // Shopify est la vérité : un cadeau encore en route est posé côté serveur avant la page de paiement.
        // Retour visuel immédiat (« Un instant… »). Le paiement n'est jamais annulé en silence.
        if (this._holding() && !this._payNow) {
          event.preventDefault();
          this._payWhenIdle();
          return;
        }
        this._payNow = false;
        this._payLabel(true);
        var reminder = this.root.querySelector('[data-nkc-reminder]');
        // Tour 3 du jury (CRO) : un cadeau retiré par la cliente (croix) et seul cadeau manquant est rappelé une fois aussi, avec sa
        // propre phrase (« Vous avez retiré votre cadeau. Le reprendre ? ») : une croix tapée par erreur ne coûte plus 60 € ; « Continuer
        // sans cadeau » part tout de suite, le second clic part toujours. Un cadeau refusé par Shopify (422/404) n'est jamais reproposé.
        var missingNow = this._missing();
        var declinedOnly = missingNow === 0 && reminder && !this._skipReminder ? this._declinedMissing() : 0;
        if (reminder && !this._skipReminder && (missingNow > 0 || declinedOnly > 0)) {
          event.preventDefault();
          // Une seule fois : un second clic sur « Paiement » part, quoi qu'il arrive (jamais bloquant).
          this._skipReminder = true;
          // Seuls les cadeaux non refusés par la cliente sont proposés dans la fenêtre ; s'il ne reste que des cadeaux retirés, ce sont eux.
          var failedNow = Object.keys(this._failed).map(String);
          var declinedNow = declinedOnly > 0 ? failedNow : this._declined().map(String);
          reminder.querySelectorAll('[data-nkc-reminder-tier]').forEach(function (block) {
            block.hidden = declinedNow.indexOf(String(block.getAttribute('data-nkc-reminder-tier'))) > -1;
          });
          var remText = reminder.querySelector('[data-nkc-reminder-text]');
          var strings = (this._config && this._config.strings) || {};
          if (remText) remText.textContent = declinedOnly > 0 && strings.reminderDeclined ? strings.reminderDeclined : (remText.getAttribute('data-text') || remText.textContent);
          if (this._up && this._upEl && !this._upEl.hidden) this._upLogStep(this._up.screen, 'r');
          this._upClose(true); // popup encore ouverte (départ depuis la popup) : le rappel prend la place, une seule fenêtre
          this._upMarkSeen(); // une seule interruption par session : la popup ne s'ouvrira plus
          reminder.hidden = false;
          fire('reminder_show', { missing: missingNow, declined: declinedOnly });
          // Tour 4 du jury (clientes, décision 232) : le focus se pose sur la boîte (tabindex −1), jamais sur la première pastille :
          // un « S » entouré d'un anneau se lisait comme une taille suggérée.
          var box = reminder.querySelector('.nkc-modal__box') || reminder.querySelector('[data-nkc-reminder-tier]:not([hidden]) button') || reminder.querySelector('button');
          if (box && box.tagName !== 'BUTTON' && !box.hasAttribute('tabindex')) box.setAttribute('tabindex', '-1');
          if (box) box.focus({ preventScroll: true });
          this._payLabel(false); // le rappel retient la cliente : le bouton reprend son libellé
          return;
        }
        if (this._upsellGo(event)) return;
        if (this._protectGo(event)) return;
        if (this._popGo(event)) return;
        fire('checkout', { cart: this.getCart() });
      }
    }

    /** Bloc de la protection colis s'il est actif (US, en ligne ou en aperçu), sinon null. */
    _protectBox() {
      var form = this.root.querySelector('[data-nkc-checkout]');
      if (!form || !form.classList.contains('nkc-pay--protect') || form.classList.contains('nkc-pay--protect-wait')) return null;
      return form.querySelector('[data-nkc-protect]');
    }

    /**
     * Protection colis (US, Mathis 16/10/2026) : « Protected Checkout » pose la protection (quantité 1), « Continue without protection »
     * la retire, puis départ DIRECT vers /checkout. 3 paliers (Mathis, 17/10/2026) : la variante du palier du total actuel à 1, toute
     * autre ligne de protection à 0, en UN cart/update.js par clé de ligne ou variante (idempotent, jamais 2 lignes). Le même appel
     * écrit l'attribut de mesure de la popup (_nkc_pop = A | B) s'il manque. Le paiement part en 2,5 s au plus.
     * Renvoie true si le départ est pris en charge ici.
     */
    _protectGo(event) {
      var box = this._protectBox();
      var want = this._protectWant !== false;
      this._protectWant = true;
      if (!box) return false;
      var config = this._config || {};
      var protect = config.protect || {};
      var cart = this._cart || config.cart || null;
      // Palier vu sur la ligne (relecture du 17/10, D10 ; QA B5 : jamais recalculé sur un panier d'onglet périmé). Chaque variante de
      // protection est posée par son id (1 la bonne, 0 les autres) : jamais 2 lignes, même si un autre onglet en a posé une.
      var vid = protectChoice(this._upDone ? this._upFrozenVid : Number(box.getAttribute('data-vid')) || null, cart, protect, box.getAttribute('data-vid'));
      var lines = ((cart && cart.items) || []).filter(function (it) { return String(it.product_id) === String(protect.pid); });
      if (!cart && box.getAttribute('data-key')) lines = [{ key: box.getAttribute('data-key'), variant_id: box.getAttribute('data-vid'), quantity: 1 }];
      var updates = protectUpdates(lines, vid, want, protect.vids);
      var group = this._upGroupNow();
      var attributes = group && protect.pop !== group ? { _nkc_pop: group } : null;
      var logA = this._upLogAttrs(); // popup vue plus tôt dans la session (croix puis paiement) : sa trace part avec la commande
      if (logA) { attributes = attributes || {}; Object.keys(logA).forEach(function (k) { attributes[k] = logA[k]; }); }
      fire('protect', { on: want, variantId: vid });
      if (!Object.keys(updates).length && !attributes) return false;
      event.preventDefault();
      fire('checkout', { cart: this.getCart() });
      this._payLabel(true);
      var body = {};
      if (Object.keys(updates).length) body.updates = updates;
      if (attributes) body.attributes = attributes;
      var gone = false;
      var go = function () { if (gone) return; gone = true; root.location.assign(routeRoot() + 'checkout'); };
      setTimeout(go, 2500);
      fetch(mark(routeRoot() + 'cart/update.js'), { // marqué nkc=1 : jamais pris pour une écriture extérieure (QA B7)
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(go, go);
      return true;
    }

    /**
     * Hors US (phase 2, 18/10/2026) : pas de protection, donc le départ normal écrit lui-même le groupe de mesure (_nkc_pop) et la trace
     * de la popup s'ils manquent, en UN cart/update.js, puis /checkout (2,5 s au plus). Renvoie true si le départ est pris en charge ici.
     */
    _popGo(event) {
      if (this._protectBox()) return false;
      var group = this._upGroupNow();
      var had = ((this._cart || (this._config && this._config.cart) || {}).attributes) || {};
      var attributes = group && had._nkc_pop !== group ? { _nkc_pop: group } : null;
      var logA = this._upLogAttrs();
      if (logA) Object.keys(logA).forEach(function (k) { if (had[k] !== logA[k]) { attributes = attributes || {}; attributes[k] = logA[k]; } });
      if (!attributes) return false;
      event.preventDefault();
      fire('checkout', { cart: this.getCart() });
      this._payLabel(true);
      var gone = false;
      var go = function () { if (gone) return; gone = true; root.location.assign(routeRoot() + 'checkout'); };
      setTimeout(go, 2500);
      fetch(mark(routeRoot() + 'cart/update.js'), {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      }).then(go, go);
      return true;
    }

    /** Aperçu de la protection avant sa mise en ligne : ?nkc_protect=1 la montre pour l'onglet (sessionStorage). */
    _protectPreview() {
      var form = this.root.querySelector('.nkc-pay--protect-wait');
      if (!form) return;
      var asked = /[?&]nkc_protect=1(?:&|#|$)/.test(root.location.search || '');
      var on = asked;
      try {
        if (asked) root.sessionStorage.setItem('nkc_protect', '1');
        on = root.sessionStorage.getItem('nkc_protect') === '1';
      } catch (e) { /* stockage bloqué : le paramètre seul */ }
      if (on) form.classList.remove('nkc-pay--protect-wait');
    }

    /* ---------- Popup avant paiement (US, Mathis 17/10/2026) ---------- */
    /*
     * Au clic sur « Protected Checkout » ou « Continue without protection » (US) : une fenêtre, une fois par session, jamais deux
     * interruptions (rappel cadeau montré = pas de popup), un 2e clic part toujours. À 1 ou 2 pièces : « la 3e offerte » et des
     * pièces dans SA taille, ajoutées en un tap (le 1er ajout pose GLOW : son geste, jamais une pose d'office, décision du 12/10).
     * Boîte et pochon absents : le pack cadeau à 20 $ au lieu de 30 $ (code GIFTPACK). La fenêtre vit HORS du rendu de la section
     * (this.panel, ou le composant sur /cart) : elle survit aux ajouts et se met à jour après chaque rendu (_upSync).
     * Mesure : groupe A (sans) ou B (avec) tiré une fois par visiteuse, écrit dans l'attribut _nkc_pop au départ (_protectGo).
     */

    _upData() {
      var node = this.root.querySelector('[data-nkc-up-data]');
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (e) { return null; }
    }

    /** Aperçu : ?nkc_upsell=1 montre la popup (groupe B) pour l'onglet, interrupteur coupé ou non. */
    _upPreview() {
      var asked = /[?&]nkc_upsell=1(?:&|#|$)/.test(root.location.search || '');
      try {
        if (asked) root.sessionStorage.setItem('nkc_upsell', '1');
        // Lien d'aperçu ouvert ou retapé : la popup se revoit. Retour arrière (depuis le paiement) ou rechargement : une fois par
        // session, comme en ligne (QA B4 ; banc du 17/10 : la popup se rouvrait au retour et le 2e paiement ne partait plus).
        if (asked && !upArrived) {
          upArrived = true;
          var nav = root.performance && root.performance.getEntriesByType ? root.performance.getEntriesByType('navigation')[0] : null;
          if (!nav || nav.type === 'navigate') root.sessionStorage.removeItem('nkc_up_seen');
        }
        return root.sessionStorage.getItem('nkc_upsell') === '1';
      } catch (e) { return asked; }
    }

    /** Groupe de mesure de la visiteuse : tiré une fois (50/50), gardé dans le navigateur ; stockage bloqué : la page.
     *  US : toutes en B depuis le 24/10/2026 (Mathis, QCM « US à 100 % maintenant ») ; le groupe tiré reste en mémoire, ailleurs 50/50. */
    _upGroup() {
      var data = this._upData();
      if (data && String(data.market) === 'US') return 'B';
      var group = null;
      try { group = root.localStorage.getItem('nkc_up_group'); } catch (e) { group = this._upMem || null; }
      if (group !== 'A' && group !== 'B') {
        group = Math.random() < 0.5 ? 'A' : 'B';
        this._upMem = group;
        try { root.localStorage.setItem('nkc_up_group', group); } catch (e) { /* mémoire de la page */ }
      }
      return group;
    }

    /** Groupe écrit au départ (attribut _nkc_pop) : seulement là où la popup existe (US), en ligne ou en aperçu ; sinon null. */
    _upGroupNow() {
      var data = this._upData();
      if (!data) return null;
      if (this._upPreview()) return 'B';
      return data.live ? this._upGroup() : null;
    }

    _upSeen() {
      try { return root.sessionStorage.getItem('nkc_up_seen') === '1'; } catch (e) { return !!this._upShown; }
    }

    _upMarkSeen() {
      this._upShown = true;
      try { root.sessionStorage.setItem('nkc_up_seen', '1'); } catch (e) { /* la page seule */ }
    }

    /**
     * Photos de la popup préchargées dès que le tiroir (ou /cart) est à l'écran, popup pas encore vue (M2 de la relecture 4 : sur réseau
     * froid, les cartes s'ouvraient vides) : les cartes qu'elle verra, dans sa taille, et les emballages. Une fois par photo.
     */
    _upPreload() {
      var state = null;
      try { state = this._upState(); } catch (e) { return; }
      if (!state || !state.plan.show) return;
      var seen = this._upLoaded || (this._upLoaded = {});
      var pack = state.data.pack;
      var srcs = (state.pieces || []).map(function (pick) { return pick.product && pick.product.img; });
      if (state.gift && state.gift.img) srcs.push(state.gift.img); // photo du cadeau du sous-titre (18/10)
      if (state.plan.pack && pack) srcs = srcs.concat(pack.box.in || pack.pouch.in ? [packBig(pack.box.in ? pack.pouch.img : pack.box.img)] : [pack.box.img, pack.pouch.img]);
      srcs.forEach(function (src) { if (src && !seen[src]) { seen[src] = true; var img = new Image(); img.decoding = 'async'; img.src = src; } });
    }

    /**
     * Journal de la popup pour la mesure (coordinateur, 18/10/2026) : écrans vus dans l'ordre (P Pièces, T Palier, E Emballage) et
     * dernière action par écran (add, skip, x, r pour le rappel cadeau ; yes, no, x sur l'Emballage), gardés pour l'onglet. Écrits en
     * attributs de panier _nkc_pop_vu et _nkc_pop_act dans l'envoi du départ (aucune requête de plus).
     */
    _upLog() {
      try { return JSON.parse(root.sessionStorage.getItem('nkc_up_log') || 'null') || { vu: [], act: {} }; } catch (e) { return this._upLogMem || { vu: [], act: {} }; }
    }

    _upLogSave(log) {
      this._upLogMem = log;
      try { root.sessionStorage.setItem('nkc_up_log', JSON.stringify(log)); } catch (e) { /* la page seule */ }
    }

    _upLogStep(screen, action, fresh) {
      var code = { pieces: 'P', tier: 'T', pack: 'E' }[screen];
      if (!code) return;
      var log = fresh ? { vu: [], act: {} } : this._upLog();
      if (log.vu.indexOf(code) === -1) log.vu.push(code);
      if (action) log.act[code] = action;
      this._upLogSave(log);
    }

    _upLogAttrs() {
      var log = this._upLog();
      if (!log.vu.length) return null;
      var out = { _nkc_pop_vu: log.vu.join(','), _nkc_pop_act: log.vu.filter(function (c) { return log.act[c]; }).map(function (c) { return c + ':' + log.act[c]; }).join(',') };
      var pcs = this._upPiecesGroup(this._upData()); // test de l'écran Pièces (US, 25/10/2026) : v1 ou v2 sur la commande
      if (pcs) out._nkc_pcs = pcs;
      return out;
    }

    /** Ce que la popup sait du panier : base des paliers, prix unitaires des pièces payées, la moins chère, sa taille. */
    _upContext(data) {
      var config = this._config || {};
      var cart = config.cart || { items: [] };
      var protect = config.protect || {};
      var skip = {};
      if (data.pack) { skip[String(data.pack.box.vid)] = true; skip[String(data.pack.pouch.vid)] = true; }
      (config.tiers || []).forEach(function (tier) { (tier.variants || []).forEach(function (v) { skip[String(v.id)] = true; }); });
      var prices = [];
      (cart.items || []).forEach(function (it) {
        if (it.gift || it.corner || skip[String(it.variant_id)] || String(it.product_id) === String(protect.pid)) return;
        var unit = Math.round((typeof it.paid === 'number' ? it.paid : it.final_line_price) / Math.max(1, it.quantity));
        for (var i = 0; i < it.quantity; i += 1) prices.push(unit);
      });
      // Prix d'origine (avant GLOW et codes) des pièces payées, lus sur cart.js : le calcul de GLOW en a besoin.
      var originals = [];
      ((this._cart && this._cart.items) || []).forEach(function (it) {
        if (skip[String(it.variant_id)] || String(it.product_id) === String(protect.pid) || !(Number(it.original_price) > 0)) return;
        for (var j = 0; j < it.quantity; j += 1) originals.push(Number(it.original_price));
      });
      return {
        base: planGifts(cart, config, this._declined(), {}).base,
        originals: originals.length === prices.length ? originals : prices,
        prices: prices,
        cheapest: prices.length ? Math.min.apply(null, prices) : 0,
        size: this._cartSize() || this._giftSize()
      };
    }

    _upState() {
      if (this._upDone) return null;
      var data = this._upData();
      if (!data || (!this._protectBox() && data.protect !== false)) return null; // US : bloc de la protection exigé ; ailleurs, aucun
      var preview = this._upPreview();
      if (!data.live && !preview) return null;
      var group = preview ? 'B' : this._upGroup();
      var paid = Number(data.paid);
      var ctx = this._upContext(data);
      var tiers = (this._config && this._config.tiers) || [];
      // 1 pièce : les pièces dans SA taille, en vente ; version principale si 2 au moins débloquent le cadeau (après son code), sinon repli.
      // Candidats de la v1 : jamais les pièces de la v2 (kind « couleur » ou « top ») : le groupe v1 du test voit l'écran d'avant, à l'identique.
      var basePieces = (data.pieces || []).filter(function (piece) { return !piece.kind; });
      var p1 = paid === 1 ? piecesPlan(rankPieces(pickPieces(basePieces, ctx.size, 7500, 24), ctx.base, tiers, Number(data.shipThr) || 0), ctx.base, tiers, Number(data.shipThr) || 0, this._upCodePct(data).pct, 4) : { mode: 'fallback', gift: null, pieces: [] };
      // Écran Pièces v2 (25/10/2026) : seulement si le thème l'envoie (data.piecesV = 2) ; sinon la v1 reste telle quelle.
      if (paid === 1 && (Number(data.piecesV) === 2 || this._upPiecesGroup(data) === 'v2')) p1 = piecesV2(p1, pickPieces(data.pieces || [], ctx.size, 0, 40), ctx.base, tiers, Number(data.shipThr) || 0, this._upCodePct(data).pct);
      var pieces = p1.pieces;
      // 2 pièces ou plus : écran Palier si une seule pièce dans sa taille passe le palier suivant (jamais une pièce que GLOW offrirait).
      var glowCode = String(((this._config && this._config.bxgy) || {}).code || 'GLOW').toUpperCase();
      var glowOn = ((this._cart && this._cart.discount_codes) || []).some(function (c) { return String(c.code).toUpperCase() === glowCode; }) || String(linkCode() || '').toUpperCase() === glowCode;
      var tp = paid >= 2 ? tierPlan(pickPieces(basePieces, ctx.size, 0, 12), ctx.base, ctx.originals, tiers, glowOn, this._upCodePct(data).pct, 4) : { show: false };
      if (tp.show) { pieces = tp.pieces; p1 = { mode: 'tier', gift: tp.gift, gap: tp.gap }; }
      var pack = this._upPackDayOk(data) ? data.pack : null; // pack du jour déjà vu un autre jour : pas d'écran Emballage (25/10/2026)
      var plan = planUpsell({
        on: true, group: group, seen: !!this._upShown || this._upSeen(), paid: paid, pieces: paid === 1 ? pieces.length : 0, tier: tp.show,
        boxOn: !!pack, pouchOn: !!pack, boxIn: !!(pack && pack.box.in), pouchIn: !!(pack && pack.pouch.in)
      });
      return { data: data, plan: plan, pieces: pieces, group: group, gift: p1.gift, gap: p1.gap || 0, p1: p1.mode, v2: !!p1.v2, colours: p1.colours || [], layout: plan.pieces > 0 ? 'S' : plan.tier > 0 ? 'T' : 'alone' };
    }

    /**
     * Cartes de l'écran Pièces v2 (Mathis, 25/10/2026). Chaque carte dit POURQUOI elle est là (« Your AUDACE, in Burgundy », « #1 in the US »,
     * repris du « Buy the look » d'Asos) et ce qu'elle vend vraiment (« 1,232 sold this month », ventes payées des 30 derniers jours du pays,
     * jamais un stock : le stock Shopify est fictif). Directions : A duo raisonné (2 grandes cartes), B carrousel à étiquettes cousues sur la
     * photo, C nuancier (sa pièce dans les autres couleurs de son modèle, puis la meilleure vente). Les boutons gardent data-nkc-up-add.
     */
    _upCardsV2(up, t, data, anyAdded, h) {
      var dir = String(data.piecesDir || 'B').toUpperCase();
      var loc = document.documentElement.lang || undefined;
      var nf = function (n) { try { return Number(n).toLocaleString(loc); } catch (e) { return String(n); } };
      var put = function (text, map) { var out = String(text || ''); Object.keys(map).forEach(function (k) { out = out.split('[' + k + ']').join(map[k]); }); return out; };
      // Raison de la carte, au format de la direction : A « Your AUDACE, in Bordeaux » (au-dessus), B « Same style » (étiquette courte sur
      // une carte de 120 px), C « Your AUDACE, other colours » (le nom et les pastilles disent la couleur choisie).
      var tagOf = function (p) {
        if (p.kind === 'couleur') return put(dir === 'B' ? (t.pcsColourTag || t.pcsColour) : dir === 'C' ? (t.pcsColourAlt || t.pcsColour) : t.pcsColour, { model: p.model || p.name, colour: p.colour || '' });
        if (p.kind === 'top') return Number(p.rank) === 1 && t.pcsTop1 ? t.pcsTop1 : (t.pcsTop || '');
        return '';
      };
      // Preuve sous le nom (Mathis, 25/10/2026 : jamais nos ventes réelles, les concurrents liraient notre volume) : la part des commandes du
      // pays qui contiennent la pièce, « In 1 of 4 US orders » (30 derniers jours), seulement si elle est forte (1 sur 15 au plus).
      var soldOf = function (p) { var n = Number(p.oneIn); return n >= 2 && n <= 15 && t.pcsOneIn ? put(t.pcsOneIn, { n: nf(n) }) : ''; };
      var sizeOf = function (variant) {
        var size = String(variant.t || '').replace(/\s*\/\s*/g, '/');
        var parts = size.split('/').filter(function (part) { return SIZE_LIKE.test(part); });
        if (parts.length && parts.every(function (part) { return part === parts[0]; })) size = parts[0];
        else if (parts.length) size = parts.join('/');
        return String(t.size || '').replace('[size]', size);
      };
      var tick = '<svg aria-hidden="true" width="11" height="9" viewBox="0 0 12 10"><path d="M1 5l3.4 3.4L11 1.6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      // Carte : un bouton entier (directions A et B, tap n'importe où = ajout, comme la v1) ; en direction C, une boîte horizontale dont la
      // photo et le bouton ajoutent, et qui porte le nuancier (jamais un bouton dans un bouton).
      var card = function (pick, cls, onPhoto, extra) {
        var p = pick.product;
        var vid = String(pick.variant.id);
        var added = !!up.added[vid];
        var off = anyAdded && !added;
        var label = added ? t.added : String(t.p1Add || '').replace('[price]', p.money);
        var tag = tagOf(p);
        var sold = soldOf(p);
        var top = p.kind === 'top';
        var horiz = cls.indexOf('--h') > -1;
        var attrs = ' data-nkc-up-add="' + h(vid) + '" data-pid="' + h(p.id) + '" data-price="' + h(p.price) + '"' + (added || off ? ' aria-disabled="true"' : '');
        var aria = h([tag, p.name, sizeOf(pick.variant), sold, label].filter(Boolean).join(' · '));
        var out = horiz ? '<div class="nkc-pop__card ' + cls + (added ? ' is-added' : '') + (off ? ' is-off' : '') + '">'
          : '<button type="button" class="nkc-pop__card ' + cls + (added ? ' is-added' : '') + (off ? ' is-off' : '') + '"' + attrs + ' data-nkc-up-key="a' + h(vid) + '" aria-label="' + aria + '">';
        var media = (p.img ? '<img src="' + h(p.img) + '" alt="" width="360" height="480" decoding="async">' : '');
        if (onPhoto && tag) media += '<span class="nkc-pop__tag' + (top ? ' nkc-pop__tag--top' : '') + '">' + h(tag) + '</span>';
        if (added) media += '<span class="nkc-pop__tick" aria-hidden="true">' + tick + '</span>';
        out += horiz ? '<button type="button" class="nkc-pop__media nkc-pop__hmedia"' + attrs + ' tabindex="-1" aria-hidden="true">' + media + '</button>' : '<span class="nkc-pop__media">' + media + '</span>';
        out += '<span class="nkc-pop__body">';
        if (horiz && tag) out += '<span class="nkc-pop__why' + (top ? ' nkc-pop__why--top' : '') + '">' + h(tag) + '</span>';
        out += '<span class="nkc-pop__name">' + h(p.name) + '</span>';
        if (extra) out += extra;
        out += '<span class="nkc-pop__size">' + h(sizeOf(pick.variant)) + '</span>';
        if (sold) out += '<span class="nkc-pop__sold"><i aria-hidden="true"></i>' + h(sold) + '</span>';
        var cta = (added ? tick : '') + '<span>' + h(label) + '</span>';
        out += horiz ? '<button type="button" class="nkc-pop__cta' + (added ? ' is-added' : '') + '"' + attrs + ' data-nkc-up-key="a' + h(vid) + '" aria-label="' + aria + '">' + cta + '</button>'
          : '<span class="nkc-pop__cta' + (added ? ' is-added' : '') + '">' + cta + '</span>';
        out += '</span>' + (horiz ? '</div>' : '</button>');
        if (up.failed[vid]) out += '<span class="nkc-pop__err" role="alert">' + h(t.error) + '</span>';
        return out;
      };
      var pieces = up.pieces || [];
      var html = '';
      if (dir === 'A') {
        html += '<div class="nkc-pop__row nkc-pop__row--duo" data-nkc-up-row role="list">';
        pieces.slice(0, 2).forEach(function (pick) {
          var tag = tagOf(pick.product);
          html += '<div class="nkc-pop__cell" role="listitem">' + (tag ? '<span class="nkc-pop__why' + (pick.product.kind === 'top' ? ' nkc-pop__why--top' : '') + '">' + h(tag) + '</span>' : '<span class="nkc-pop__why">&nbsp;</span>') + card(pick, 'nkc-pop__card--duo', false) + '</div>';
        });
        return html + '</div>';
      }
      if (dir === 'C') {
        var colours = up.colours && up.colours.length ? up.colours : [];
        var idx = Math.min(Math.max(0, Number(up.colourIdx) || 0), Math.max(0, colours.length - 1));
        var lead = colours[idx] || pieces[0];
        var topPick = pieces.filter(function (pick) { return pick.product.kind === 'top'; })[0] || pieces.filter(function (pick) { return !lead || pick.product.id !== lead.product.id; })[0];
        html += '<div class="nkc-pop__stack" data-nkc-up-row role="list">';
        if (lead) {
          var sw = '';
          if (colours.length > 1) {
            sw += '<span class="nkc-pop__swatches" role="group" aria-label="' + h(t.pcsColours || '') + '">';
            colours.forEach(function (pick, i) {
              sw += '<button type="button" class="nkc-pop__swatch' + (i === idx ? ' is-on' : '') + '" data-nkc-up-swatch="' + i + '" aria-pressed="' + (i === idx ? 'true' : 'false') + '" aria-label="' + h(pick.product.colour || pick.product.name) + '" style="--sw:' + h(pick.product.hex || '#888') + '"' + (anyAdded ? ' disabled' : '') + '></button>';
            });
            sw += '</span>';
          }
          html += '<div class="nkc-pop__hcell" role="listitem">' + card(lead, 'nkc-pop__card--h', false, sw) + '</div>';
        }
        if (topPick) html += '<div class="nkc-pop__hcell" role="listitem">' + card(topPick, 'nkc-pop__card--h nkc-pop__card--hsmall', false) + '</div>';
        return html + '</div>';
      }
      html += '<div class="nkc-pop__row" data-nkc-up-row role="list">';
      pieces.forEach(function (pick) { html += '<div class="nkc-pop__cell" role="listitem">' + card(pick, 'nkc-pop__card--tagged', true) + '</div>'; });
      return html + '</div>';
    }

    /**
     * Pack du jour (US, 25/10/2026) : le compteur de l'écran Emballage file jusqu'à minuit, et l'offre s'arrête vraiment à minuit pour la cliente
     * qui l'a vue (jour gardé dans le navigateur) : plus d'écran Emballage les 7 jours suivants, puis l'offre peut revenir.
     */
    _upPackDayOk(data) {
      if (data && data.daily) return true; // Offre renouvelée chaque jour, sans délai de plusieurs jours.
      if (!data || !data.midnight) return true;
      var seen = null;
      try { seen = root.localStorage.getItem('nkc_pk_day'); } catch (e) { seen = this._pkDayMem || null; }
      if (!seen || !/^\d{4}-\d\d-\d\d$/.test(seen)) return true;
      var gap = daysBetween(seen, dayKey());
      return !(gap >= 1 && gap < 8);
    }

    _upPackDayMark(data) {
      if (data && data.daily) return;
      if (!data || !data.midnight) return;
      var today = dayKey();
      var seen = null;
      try { seen = root.localStorage.getItem('nkc_pk_day'); } catch (e) { seen = this._pkDayMem || null; }
      if (seen && /^\d{4}-\d\d-\d\d$/.test(seen) && daysBetween(seen, today) < 8) return; // déjà daté (aujourd'hui ou dans les 7 jours)
      this._pkDayMem = today;
      try { root.localStorage.setItem('nkc_pk_day', today); } catch (e) { /* mémoire de la page */ }
    }

    /** Test de l'écran Pièces (US, 25/10/2026) : v1 (actuel) ou v2 (étiquettes), tiré une fois 50/50 et gardé ; null hors test. */
    _upPiecesGroup(data) {
      if (!data || !data.piecesAB) return null;
      var g = null;
      try { g = root.localStorage.getItem('nkc_pcs'); } catch (e) { g = this._pcsMem || null; }
      if (g !== 'v1' && g !== 'v2') {
        g = Math.random() < 0.5 ? 'v1' : 'v2';
        this._pcsMem = g;
        try { root.localStorage.setItem('nkc_pcs', g); } catch (e) { /* mémoire de la page */ }
      }
      return g;
    }

    /** Point d'entrée (_onSubmit) : true si la popup s'ouvre ou si le verdict des codes est attendu (le départ attend la cliente). */
    _upsellGo(event) {
      var self = this;
      if (this._upWaiting) { event.preventDefault(); return true; } // verdict des codes en cours : un 2e tap ne relance rien
      var state = this._upState();
      if (!state || !state.plan.show) return false;
      event.preventDefault();
      var box = this._protectBox();
      this._upFrozenVid = box ? Number(box.getAttribute('data-vid')) || null : null; // palier vu au clic (D10)
      var probe = state.plan.pack ? this._upProbe(state.data, state.layout === 'S' || state.layout === 'T' ? 3000 : 800) : Promise.resolve({ mode: 'normal' });
      if (state.layout === 'S' || state.layout === 'T') {
        this._upOpen(state, probe); // écran Pièces ou Palier tout de suite, le verdict des codes se lit derrière
        return true;
      }
      // Écran Emballage d'emblée (2 pièces ou plus) : le verdict d'abord (800 ms au plus, « One moment… » au tap), jamais une fausse
      // promesse. Aucune offre possible : départ normal, sans popup.
      this._payLabel(true);
      this._upWaiting = true;
      probe.then(function (verdict) {
        if (!self._upWaiting) return;
        self._upWaiting = false;
        if (verdict.mode === 'none') { self._upShown = true; self._payLabel(false); self._upDone = true; self._pay(); return; }
        self._payLabel(false);
        self._upOpen(state, Promise.resolve(verdict));
      });
      return true;
    }

    /** Pourcentage du code posé par la cliente (lu sur cart.js : remise de commande ou de ligne), hors GLOW et GIFTPACK ; 0 sinon. */
    _upCodePct(data, live) {
      var cart = live || this._cart || (this._config && this._config.cart) || {};
      var skip = {};
      skip[String((data && data.packCode) || 'GIFTPACK').toUpperCase()] = true;
      skip[String((data && data.packCodeB) || 'GIFTPACKB').toUpperCase()] = true;
      skip[String(((this._config && this._config.bxgy) || {}).code || 'GLOW').toUpperCase()] = true;
      var found = { pct: 0, code: '' };
      var seen = function (app) {
        if (!app || found.pct) return;
        var title = String(app.title || '').toUpperCase();
        if (app.value_type === 'percentage' && Number(app.value) > 0 && !skip[title] && (app.type === 'discount_code' || !app.type)) { found.pct = Number(app.value); found.code = title; }
      };
      (cart.cart_level_discount_applications || []).forEach(seen);
      (cart.items || []).forEach(function (it) { (it.line_level_discount_allocations || []).forEach(function (al) { seen(al.discount_application); }); });
      return found;
    }

    /**
     * Verdict du pack face aux codes du panier (lus sur cart.js, jamais devinés) : sans autre code que GLOW, prix normal ; sinon
     * 3 paniers jetables de l'API Storefront (sans jeton, même domaine), jamais le panier de la cliente : « ses lignes + ses codes »,
     * « + pack + ses codes + GIFTPACK », « + pack + ses codes ». 800 ms au plus quand la cliente attend (écran Emballage d'emblée),
     * 3 s quand elle lit d'abord l'écran Pièces ou Palier (banc du 17/10 : 3 appels en 0,7 à 0,9 s, l'écran Emballage sautait au-delà
     * de 800 ms). Échec ou délai : aucune offre (rien de faux).
     */
    _upProbe(data, limitMs) {
      var self = this;
      var known = this._cart || (this._config && this._config.cart) || {};
      if (!(limitMs > 800)) return this._upProbeOn(data, limitMs, known);
      // Derrière l'écran Pièces ou Palier (3 s) : ses codes relus sur cart.js d'abord, le panier du rendu peut être en retard (relecture 4,
      // VB : tiroir sans NIGHTMARE25, écran à « +$5 » au lieu de son prix VIP).
      var started = Date.now();
      return cartApi('cart.js').then(function (res) { return res && res.ok && res.data && res.data.items ? res.data : known; }, function () { return known; })
        .then(function (cart) { return self._upProbeOn(data, Math.max(300, limitMs - (Date.now() - started)), cart); });
    }

    _upProbeOn(data, limitMs, cart) {
      var self = this;
      var pack = data.pack || {};
      var pick = packPick(pack, data, !!(pack.box && pack.box.in), !!(pack.pouch && pack.pouch.in)); // GIFTPACK, ou GIFTPACKB en variante B (R7)
      var glow = String(((this._config && this._config.bxgy) || {}).code || 'GLOW').toUpperCase();
      var before = (cart.discount_codes || []).filter(function (c) { return pick.all.indexOf(String(c.code).toUpperCase()) === -1; });
      var others = before.filter(function (c) { return String(c.code).toUpperCase() !== glow; });
      var seen = others.map(function (c) { return String(c.code).toUpperCase(); }).sort(); // ses codes, lus par l'écran (comparés au départ)
      if (!others.length) return Promise.resolve({ mode: 'normal', seen: seen }); // GLOW et GIFTPACK se combinent avec tout (API, 17/10)
      var protect = (this._config && this._config.protect) || {};
      var gifts = {};
      (((this._config && this._config.cart) || {}).items || []).forEach(function (it) { if (it.gift) gifts[String(it.variant_id)] = true; });
      var lines = [];
      (cart.items || []).forEach(function (it) {
        if (String(it.product_id) === String(protect.pid) || gifts[String(it.variant_id)] || !(it.quantity > 0)) return;
        lines.push({ merchandiseId: 'gid://shopify/ProductVariant/' + it.variant_id, quantity: it.quantity });
      });
      var packLines = lines.slice();
      if (!pack.box.in) packLines.push({ merchandiseId: 'gid://shopify/ProductVariant/' + pack.box.vid, quantity: 1 });
      if (!pack.pouch.in) packLines.push({ merchandiseId: 'gid://shopify/ProductVariant/' + pack.pouch.vid, quantity: 1 });
      var codes = before.map(function (c) { return c.code; });
      var country = String(data.market || (this._config && this._config.country) || 'US');
      var make = function (ls, cs) {
        var query = 'mutation($l:[CartLineInput!],$d:[String!],$c:CountryCode){ cartCreate(input:{ lines:$l, discountCodes:$d, buyerIdentity:{ countryCode:$c } }){ cart{ discountCodes{ code applicable } discountAllocations{ discountedAmount{ amount } } lines(first:100){ nodes{ cost{ totalAmount{ amount } } } } } } }';
        return root.fetch('/api/2025-07/graphql.json', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query: query, variables: { l: ls, d: cs, c: country } }) })
          .then(function (res) { return res.json(); })
          .then(function (json) { var c = json && json.data && json.data.cartCreate && json.data.cartCreate.cart; if (!c) throw new Error('nkc-probe'); return c; });
      };
      var found = this._upCodePct(data, cart); // pourcentage de SON code, lu sur cart.js ; 0 si montant fixe
      var pct = found.pct;
      var pctCode = found.code;
      var full = pack.box.in || pack.pouch.in ? Number(pack.addFullCents) : Number(pack.fullCents);
      var work = Promise.all([make(lines, codes), make(packLines, codes.concat([pick.code])), make(packLines, codes)]).then(function (r) {
        var verdict = packVerdict(before, r[1].discountCodes, pick.code, storefrontTotal(r[0]), storefrontTotal(r[2]), full, pct);
        verdict.pct = pct;
        verdict.code = pctCode;
        verdict.seen = seen;
        return verdict;
      });
      var limit = new Promise(function (resolve) { setTimeout(function () { resolve({ mode: 'none', timeout: true }); }, limitMs || 800); });
      return Promise.race([work, limit]).catch(function () { return { mode: 'none', failed: true }; }).then(function (verdict) {
        fire('upsell_probe', { mode: verdict.mode, pct: verdict.pct || 0, cost: verdict.cost || null, timeout: !!verdict.timeout, failed: !!verdict.failed });
        return verdict;
      });
    }

    _upOpen(state, probe) {
      var self = this;
      var el = this._upEl;
      this._upMarkSeen();
      this._upWant = this._protectWant !== false; // choix de protection gardé pour le départ, par les deux boutons
      this._protectWant = true;
      // Deux écrans dans la même fenêtre (Mathis, 17/10/2026 : jamais 2 offres sur un écran) : « pieces » (1 ou 2 pièces : la 3e
      // offerte) puis « pack » (emballage : boîte et pochon), glissé à la place du premier. 3 pièces ou plus : directement « pack ».
      this._up = {
        plan: state.plan, pieces: state.pieces, group: state.group, gift: state.gift, p1: state.p1 || 'fallback', layout: state.layout, v2: !!state.v2, colours: state.colours || [], colourIdx: 0,
        screen: state.layout === 'S' ? 'pieces' : state.layout === 'T' ? 'tier' : 'pack', gap: state.gap || 0, shown: '', verdict: null, verdictPromise: probe || Promise.resolve({ mode: 'normal' }), expired: false,
        added: {}, extra: {}, pending: {}, failed: {}, packBusy: false, leaving: false, title: ''
      };
      this._upLogStep(this._up.screen, null, true); // nouvelle popup, nouveau journal
      if (!el) {
        el = document.createElement('div');
        el.className = 'nkc-pop';
        el.setAttribute('data-nkc-up', '');
        el.hidden = true;
        this._upEl = el;
      }
      (this.mode === 'page' ? this : this.panel).appendChild(el);
      var mine = this._up;
      mine.verdictPromise.then(function (verdict) { if (self._up === mine) mine.verdict = verdict; });
      // Photos de l'écran Emballage chargées dès l'ouverture : le passage d'écran n'attend jamais le réseau.
      var pack = state.data.pack;
      var single = pack && (pack.box.in || pack.pouch.in) ? [packBig(pack.box.in ? pack.pouch.img : pack.box.img)] : null;
      if (state.plan.pack && pack) (single || [pack.box.img, pack.pouch.img]).forEach(function (src) { if (src) { var img = new Image(); img.decoding = 'async'; img.src = src; } });
      this._upPaint(state.data);
      el.classList.remove('is-open');
      el.hidden = false;
      this.setAttribute('data-nkc-popped', ''); // bandeau « Reserved for » masqué derrière la popup : une seule horloge à l'écran (R2, R3)
      this._upFit(); // mesures possibles seulement fenêtre visible (D2)
      // Mesures refaites à l'image suivante et une fois les polices chargées (banc du 17/10 : pastille passée sur 2 lignes à 390 px).
      requestAnimationFrame(function () { if (self._upEl && !self._upEl.hidden) self._upFit(); });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { if (self._upEl && !self._upEl.hidden) self._upFit(); });
      if (this.mode === 'page') document.documentElement.classList.add('nkc-lock');
      this._payLabel(false);
      void el.offsetWidth; // l'état fermé est lu avant is-open : l'entrée s'anime
      // Hauteur de la fenêtre fixée à l'ouverture : rien ne saute après un ajout ni au passage d'écran (le contenu défile dedans).
      var first = el.querySelector('.nkc-pop__box');
      if (first && this._up) { this._up.lockH = first.offsetHeight; first.style.height = this._up.lockH + 'px'; }
      el.classList.add('is-open');
      var box = el.querySelector('.nkc-pop__box');
      if (box) box.focus({ preventScroll: true });
      fire('upsell_show', { group: state.group, layout: state.layout, pieces: state.plan.pieces, pack: state.plan.pack, offered: state.pieces.length, gift: state.gift ? state.gift.name : null });
    }

    /** Fermeture sans départ : le rappel cadeau prend la place (silent). Depuis le 19/10/2026, croix, voile et Échap partent au paiement (_upLeave). */
    _upClose(silent) {
      var self = this;
      var el = this._upEl;
      clearInterval(this._upTimer);
      if (!el || el.hidden) return;
      el.classList.remove('is-open');
      var done = function () {
        if (el.classList.contains('is-open')) return;
        el.hidden = true;
        self.removeAttribute('data-nkc-popped');
        if (self.mode === 'page') document.documentElement.classList.remove('nkc-lock');
      };
      if (silent) { done(); return; }
      setTimeout(done, 170);
      this._protectWant = true;
      fire('upsell_close', { group: this._up && this._up.group });
      var pay = this.root.querySelector('[data-nkc-checkout] [name="checkout"]');
      if (pay) pay.focus({ preventScroll: true });
    }

    /** Une pièce, dans sa taille, ajoutée tout de suite ; le 1er ajout pose GLOW s'il n'est pas rattaché (« No code to type »). */
    _upAddPiece(node) {
      var self = this;
      var up = this._up;
      if (!up || up.leaving) return;
      var vid = String(node.getAttribute('data-nkc-up-add'));
      if (up.added[vid] || Object.keys(up.added).length) return; // un seul ajout sur cet écran (copy du 17/10)
      var data = this._upData() || {};
      // Réponse au tap en moins de 100 ms (Mathis, 17/10 : « aucune friction ») : la carte est cochée, « Added to your cart » et
      // « Continue » s'affichent tout de suite ; l'appel réseau suit. Refus de Shopify : la carte revient, un message propre la suit.
      up.added[vid] = true;
      up.pending[vid] = true;
      this._upLogStep(up.screen, 'add');
      up.extra[vid] = Number(node.getAttribute('data-price')) || 0;
      delete up.failed[vid];
      fire('upsell_add', { kind: 'piece', variantId: Number(vid), productId: Number(node.getAttribute('data-pid')) || null, group: up.group, paid: Number(data.paid) });
      this._lastCause = 'add';
      this._upConfirm();
      // GLOW n'est plus posé ici (Mathis, 17/10/2026) : la cliente garde le code qu'elle avait.
      this._enqueue(function () {
        return cartApi('cart/add.js', { items: [{ id: Number(vid), quantity: 1, properties: { _nkc_up: '1' } }] }).then(function (res) {
          delete up.pending[vid];
          if (!res.ok) {
            delete up.added[vid];
            delete up.extra[vid];
            up.failed[vid] = true;
            up.toastUntil = 0;
            if (self._up === up && !up.leaving) self._upPaint(self._upData());
          }
          var notice = res.ok ? null : self._refusal(res, 'add');
          return self._fetchState().then(function (state) { return self._settle(state, 0, { notice: notice, error: !!notice }); });
        }, function (err) {
          delete up.pending[vid];
          delete up.added[vid];
          delete up.extra[vid];
          up.failed[vid] = true;
          if (self._up === up && !up.leaving) self._upPaint(self._upData());
          throw err;
        });
      }, 'add');
    }

    /** Pack cadeau coché : boîte et pochon manquants + code GIFTPACK (codes déjà posés repris) en UN cart/update.js, puis départ. */
    _upAddPack() {
      var up = this._up;
      if (!up || up.leaving) return;
      fire('upsell_add', { kind: 'pack', pack: up.plan.pack, mode: up.verdict ? up.verdict.mode : null, expired: !!up.expired, group: up.group });
      this._upLogStep('pack', 'yes');
      this._upLeave(true);
    }

    /**
     * Départ depuis la popup (QA du 17/10, B1 et B2 ; règle du coordinateur, 17/10 : ce que la cliente a choisi ne se perd JAMAIS en
     * silence). Les tâches pas encore parties s'annulent ; une écriture déjà en vol finit, puis le panier est RELU (sans écriture en vol,
     * le panier connu suffit : un aller-retour de moins). Puis UN cart/update.js calculé sur ce panier, en quantités absolues par
     * variante et codes listés (rejouable sans doublon) : pièces de la popup absentes, pack, GIFTPACK (mode normal, offre encore
     * valable), cadeaux que ces ajouts débloquent, protection (palier figé au clic, chaque variante par son id), _nkc_pop. Paiement
     * seulement à la réponse. Échec : upsell_send_fail, UNE nouvelle tentative, puis départ. Garde-fou absolu à 12 s du tap (nouvelle
     * tentative en keepalive, qui survit au départ). « One moment… » reste affiché jusque-là.
     */
    _upLeave(withPack) {
      var self = this;
      var up = this._up;
      if (!up || up.leaving) return;
      up.leaving = true;
      this._upDone = true;
      this._leaveAll = true;
      clearInterval(this._upTimer);
      var data = this._upData() || {};
      var t = data.t || {};
      fire(withPack ? 'upsell_go' : 'upsell_skip', { group: up.group, added: Object.keys(up.added).length, pack: !!withPack });
      var go = this._upEl && this._upEl.querySelector('[data-nkc-up-go]');
      var strings = (this._config && this._config.strings) || {};
      var start = Date.now();
      var gone = false;
      if (go) {
        go.setAttribute('data-nkc-paying', '1');
        go.setAttribute('aria-busy', 'true');
        var label = go.querySelector('[data-nkc-up-go-label]');
        var note = withPack ? t.pkAdded : strings.paying;
        if (label && note) label.textContent = note;
        // Coche comme sur « Added » des cartes (R11) ; « One moment… » la remplace si le départ traîne.
        if (label && note && withPack) label.insertAdjacentHTML('afterbegin', '<svg class="nkc-pop__gotick" aria-hidden="true" focusable="false" width="13" height="11" viewBox="0 0 12 10"><path d="M1 5l3.4 3.4L11 1.6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>');
        if (withPack) go.classList.add('is-added');
        // Pack : « Added. Let the tease begin. » répond au tap, puis « One moment… » jusqu'au départ si l'envoi traîne.
        if (withPack && label && strings.paying) setTimeout(function () { if (!gone) label.textContent = strings.paying; }, 1200);
      }
      this._payLabel(true);
      fire('checkout', { cart: this.getCart() });
      var leave = function () { if (gone) return; gone = true; root.location.assign(routeRoot() + 'checkout'); };
      var known = function () { return self._cart || (self._config && self._config.cart) || { items: [] }; };
      var body = null;
      var tries = 0;
      var send = function () {
        tries += 1;
        return timedFetch(mark(routeRoot() + 'cart/update.js'), {
          method: 'POST', credentials: 'same-origin', cache: 'no-store', keepalive: true,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body)
        }).then(function (res) { if (!res.ok) throw new Error('http-' + res.status); });
      };
      var fail = function (reason) { fire('upsell_send_fail', { reason: reason, tries: tries, ms: Date.now() - start, group: up.group }); };
      // Garde-fou : 12 s quand elle a choisi quelque chose (pack, pièce ajoutée : rien ne se perd) ; sinon 2,5 s, comme le départ du groupe A
      // (bouton de la protection) : l'envoi part en keepalive et survit au départ (19/10/2026).
      var quick = !withPack && !Object.keys(up.added).length;
      setTimeout(function () {
        if (gone) return;
        fail(body ? 'timeout' : 'timeout-before-send');
        if (!body) body = self._upFinalBody(known(), withPack, data);
        if (tries < 2) send().catch(function () {});
        leave();
      }, quick ? 2500 : 12000);
      var inFlight = writing();
      var settled = function () {
        return new Promise(function (resolve) {
          var tick = function () { if (!writing() || Date.now() - start > 5500) resolve(); else setTimeout(tick, 30); };
          tick();
        });
      };
      // Un code de pack à poser : panier relu d'abord. La liste envoyée REMPLACE les codes du panier : sur un panier du rendu en retard, son
      // code partait (relecture 4, VB : NIGHTMARE25 remplacé par GIFTPACKB). Sans code à poser ni écriture en vol : le panier connu suffit.
      var first = inFlight ? null : self._upFinalBody(known(), withPack, data);
      var mustRead = inFlight || !!(first && first.discount);
      var read = mustRead ? settled().then(function () { return cartApi('cart.js'); }).catch(function () { return null; }) : Promise.resolve(null);
      read.then(function (res) {
        if (gone) return;
        var fresh = res && res.ok && res.data && res.data.items ? res.data : known();
        body = mustRead ? self._upFinalBody(fresh, withPack, data) : first;
        // Ses codes ont changé depuis l'écran (autre onglet, panier en retard) : le prix affiché ne tient plus ; pas de pack, ses codes gardés.
        var seen = up.verdict && up.verdict.seen;
        if (withPack && seen && body.discount) {
          var glowCode = String(((self._config && self._config.bxgy) || {}).code || 'GLOW').toUpperCase();
          var packs = packPick(data.pack || {}, data, false, false).all;
          var now = (fresh.discount_codes || []).map(function (c) { return String(c.code).toUpperCase(); }).filter(function (c) { return c !== glowCode && packs.indexOf(c) === -1; }).sort();
          if (now.join(',') !== seen.join(',')) { fail('codes-changed'); body = self._upFinalBody(fresh, false, data); }
        }
        fire('upsell_send', { updates: Object.keys(body.updates || {}).length, discount: !!body.discount, read: inFlight, ms: Date.now() - start });
        return send().catch(function (err) {
          fail(String((err && err.message) || 'network'));
          if (gone) return null;
          return send().catch(function (err2) { fail('retry ' + String((err2 && err2.message) || 'network')); });
        });
      }).then(leave, leave);
    }

    /** L'envoi final du départ, calculé sur le panier RELU (ses calculs sont ceux du moteur, testés). */
    _upFinalBody(fresh, withPack, data) {
      var config = this._config || {};
      var protect = config.protect || {};
      var items = fresh.items || [];
      var plan = this._upPlanFinal(fresh, withPack, data);
      var updates = plan.updates;
      var discount = plan.discount;
      // 4. Protection : palier figé au clic, chaque variante par son id (jamais 2 lignes) ; 5. groupe de mesure.
      var box = this._protectBox();
      if (box) {
        var vid = protectChoice(this._upFrozenVid || Number(box.getAttribute('data-vid')) || null, fresh, protect, box.getAttribute('data-vid'));
        var lines = items.filter(function (it) { return String(it.product_id) === String(protect.pid); });
        var protectSet = protectUpdates(lines, vid, this._upWant !== false, protect.vids);
        Object.keys(protectSet).forEach(function (key) { updates[key] = protectSet[key]; });
      }
      var body = { updates: updates };
      if (discount) body.discount = discount;
      var group = this._upGroupNow();
      var attrs = {};
      var had = fresh.attributes || {};
      if (group && had._nkc_pop !== group) attrs._nkc_pop = group;
      var logA = this._upLogAttrs(); // écrans vus et actions (mesure du 18/10), dans le même envoi
      if (logA) Object.keys(logA).forEach(function (k) { if (had[k] !== logA[k]) attrs[k] = logA[k]; });
      if (Object.keys(attrs).length) body.attributes = attrs;
      return body;
    }

    /**
     * Pièces de la popup absentes du panier, pack (GIFTPACK, ou GIFTPACKB en variante B), cadeaux que ces ajouts débloquent : le calcul
     * de l'envoi final, repris tel quel par la ligne « Also unlocks your free [gift] » de l'écran Emballage (jamais une promesse que
     * l'envoi ne tient pas). Renvoie { updates, discount, unlocked: [palier] }.
     */
    _upPlanFinal(fresh, withPack, data) {
      var up = this._up;
      var config = this._config || {};
      var protect = config.protect || {};
      var pack = data.pack;
      var items = fresh.items || [];
      var inCart = {};
      items.forEach(function (it) { inCart[String(it.variant_id)] = (inCart[String(it.variant_id)] || 0) + it.quantity; });
      var gifts = {};
      (config.tiers || []).forEach(function (tier) { (tier.variants || []).forEach(function (v) { gifts[String(v.id)] = true; }); });
      var glowCode = String((config.bxgy || {}).code || 'GLOW').toUpperCase();
      var glowOn = (fresh.discount_codes || []).some(function (c) { return String(c.code).toUpperCase() === glowCode; }) || String(linkCode() || '').toUpperCase() === glowCode;
      var pct = this._upCodePct(data).pct;
      var keep = 1 - (pct > 0 ? pct / 100 : 0);
      var originals = [];
      items.forEach(function (it) {
        var vid = String(it.variant_id);
        if (gifts[vid] || String(it.product_id) === String(protect.pid) || (pack && (vid === String(pack.box.vid) || vid === String(pack.pouch.vid))) || !(Number(it.original_price) > 0)) return;
        for (var j = 0; j < it.quantity; j += 1) originals.push(Number(it.original_price));
      });
      var updates = {};
      var addedCents = 0;
      // 1. Pièces ajoutées depuis la popup que le panier relu n'a pas (tâche annulée par le départ) : posées par variante.
      Object.keys(up.added).forEach(function (vid) {
        if (inCart[vid]) return;
        var price = Number(up.extra[vid]) || 0;
        updates[vid] = 1;
        addedCents += baseWithPiece(0, originals, price, glowOn, pct);
        originals.push(price);
      });
      // 2. Pack (boîte et pochon manquants), seulement en mode normal tant que l'offre tient : GIFTPACK (pack complet, variante A),
      //    GIFTPACKB en variante B (pochon déjà pris : la boîte à −66,67 %, R7) ; l'autre code du pack retiré (jamais 2 remises).
      var discount = null;
      if (withPack && pack) {
        var boxMissing = !inCart[String(pack.box.vid)];
        var pouchMissing = !inCart[String(pack.pouch.vid)];
        if (boxMissing) updates[String(pack.box.vid)] = 1;
        if (pouchMissing) updates[String(pack.pouch.vid)] = 1;
        var full = (boxMissing ? Number(pack.boxCents) || 0 : 0) + (pouchMissing ? Number(pack.pouchCents) || 0 : 0);
        var mode = up.verdict ? up.verdict.mode : 'normal';
        var pick = packPick(pack, data, !boxMissing, !pouchMissing);
        if ((boxMissing || pouchMissing) && mode === 'normal' && !up.expired && pick.code) {
          var codes = (fresh.discount_codes || []).map(function (c) { return c.code; }).filter(function (c) { return pick.all.indexOf(String(c).toUpperCase()) === -1; });
          codes.push(pick.code);
          discount = codes.join(',');
          addedCents += Math.max(0, full - pick.off);
        } else if (mode === 'pct') addedCents += Number(up.verdict.cost) || 0;
        else addedCents += Math.round(full * keep);
      }
      // 3. Cadeaux que ces ajouts débloquent (QA B1) : base du panier relu (calcul de la jauge) + ce qui est ajouté ; palier atteint,
      //    cadeau absent et non refusé ; variante unique, ou celle de sa taille. Dans le MÊME envoi : rien ne part après la navigation.
      var unlocked = [];
      if (Object.keys(updates).length) {
        var after = planGifts(fresh, config, this._declined(), store.get('sizes', {})).base + addedCents;
        var size = this._giftSize();
        var declined = {};
        this._declined().forEach(function (id) { declined[String(id)] = true; });
        (config.tiers || []).forEach(function (tier) {
          if (!(tier.threshold > 0) || after < tier.threshold || declined[String(tier.id)] || tier.available === false) return;
          var vars = (tier.variants || []).filter(function (v) { return v.available !== false && !(Number(v.price) > 0); });
          if (vars.some(function (v) { return inCart[String(v.id)]; })) return;
          var chosen = store.get('sizes', {})[String(tier.id)];
          var pick = vars.length === 1 ? vars[0] : vars.filter(function (v) { return chosen ? String(v.id) === String(chosen) : size && String(v.title).toUpperCase() === String(size).toUpperCase(); })[0];
          if (pick) { updates[String(pick.id)] = 1; unlocked.push(tier); }
        });
      }
      return { updates: updates, discount: discount, unlocked: unlocked };
    }

    /** Cadeau que le PACK fait passer (et que l'envoi du départ pose vraiment) : ligne « Also unlocks your free [gift] » ; sinon null. */
    _upPackUnlock(data) {
      var fresh = this._cart || (this._config && this._config.cart) || { items: [] };
      var withPack = this._upPlanFinal(fresh, true, data).unlocked;
      var without = this._upPlanFinal(fresh, false, data).unlocked.map(function (tier) { return String(tier.id); });
      return withPack.filter(function (tier) { return without.indexOf(String(tier.id)) === -1 && tier.name; })[0] || null;
    }

    /** Minuteur du tiroir encore en marche (« Size reserved ») ; sinon la phrase sans réservation. */
    _upTimerLive() {
      return this._upTimerLeft() > 0;
    }

    /**
     * Retour arrière depuis le paiement (bfcache) ou départ de la page : la fenêtre est retirée sans animation, ses minuteries arrêtées,
     * le départ en cours oublié ; le panier redevient utilisable (QA du 17/10, lot 5 : popup restée sur « ONE MOMENT… », défilement
     * bloqué, plus rien ne partait). La popup ne se rouvre pas dans la session (drapeau de session), le paiement part au clic.
     */
    _upReset() {
      clearInterval(this._upTimer);
      clearTimeout(this._upToastTimer);
      var el = this._upEl;
      if (el) {
        el.classList.remove('is-open');
        el.hidden = true;
        el.innerHTML = '';
      }
      if (this._up) this._up.leaving = true;
      this._up = null;
      this._upDone = false;
      this.removeAttribute('data-nkc-popped');
      this._leaveAll = false;
      this._upFrozenVid = null;
      this._protectWant = true;
      if (this.mode === 'page' || !this.hasAttribute('open')) document.documentElement.classList.remove('nkc-lock');
    }

    /** Après chaque rendu (et en fin de file) : textes, prix et totaux à jour. */
    _upSync(idle) {
      var self = this;
      var el = this._upEl;
      var up = this._up;
      if (!el || el.hidden || !up || up.leaving) return;
      var data = this._upData();
      if (!data) { this._upClose(true); return; } // panier vidé ou hors US : plus rien à proposer
      if (!this._upPackOffer(data) && up.screen === 'pack') { this._upLeave(false); return; } // pack plus proposable (autre code posé ailleurs) : paiement
      this._upPaint(data);
    }

    /** « Added to your cart » 1,5 s ; repeint tout de suite (carte cochée), sauf si l'appelant repeint lui-même. */
    _upConfirm(later) {
      var self = this;
      if (!this._up) return;
      this._up.toastUntil = Date.now() + 1500;
      clearTimeout(this._upToastTimer);
      this._upToastTimer = setTimeout(function () {
        var toast = self._upEl && self._upEl.querySelector('[data-nkc-up-toast]');
        if (toast) toast.classList.remove('is-on');
        setTimeout(function () { if (toast && !toast.classList.contains('is-on')) toast.textContent = ''; }, 200); // texte vidé après le fondu
      }, 1500);
      if (!later) this._upPaint(this._upData());
    }

    /** Pièces payées : dernier rendu + ajouts que cart/add.js vient de confirmer (bouton « Continue » tout de suite, D6). */
    _upCount(data) {
      var up = this._up || {};
      var ctx = this._upContext(data);
      var known = {};
      (((this._config && this._config.cart) || {}).items || []).forEach(function (it) { known[String(it.variant_id)] = true; });
      var extras = Object.keys(up.extra || {}).filter(function (vid) { return !known[vid]; }).map(function (vid) { return up.extra[vid]; });
      return { paid: (Number(data.paid) || ctx.prices.length) + extras.length, added: extras.length };
    }

    /** Pack à proposer : boîte et pochon en vente et pas déjà tous les deux au panier. */
    _upPackOffer(data) {
      var pack = data && data.pack;
      return !!(this._up && this._up.plan.pack && pack && !(pack.box.in && pack.pouch.in));
    }

    /** Bouton de l'écran Pièces : écran Emballage s'il y a un pack à proposer (2e étape de la même fenêtre), sinon le paiement. */
    _upNext() {
      var self = this;
      var up = this._up;
      if (!up || up.leaving) return;
      if (up.screen !== 'pack' && !Object.keys(up.added).length && !up.waitingVerdict) this._upLogStep(up.screen, 'skip');
      // Rien d'ajouté sur l'écran Pièces ou Palier : départ direct, sans écran Emballage (19/10/2026, « ça ralentit la vitesse ») ; après
      // un refus, l'Emballage n'a pris qu'1 pack sur 57 (commandes US du 17 au 19/10). Après un ajout, l'Emballage suit comme avant.
      var declined = up.screen !== 'pack' && !Object.keys(up.added).length;
      if (up.screen === 'pack' || declined || !this._upPackOffer(this._upData())) { this._upLeave(false); return; }
      var step = function (verdict) {
        if (self._up !== up || up.leaving) return;
        up.verdict = verdict;
        if (verdict.mode === 'none') self._upLeave(false); else self._upScreen('pack', 'exit');
      };
      if (up.verdict) { step(up.verdict); return; } // verdict lu pendant l'écran Pièces : passage immédiat (≤ 250 ms)
      if (up.waitingVerdict) return; // 2e tap pendant l'attente : rien de plus
      up.waitingVerdict = true;
      // Verdict de son code encore en route (tap rapide) : « One moment… » sur le bouton, l'écran suit à la réponse (3 s au plus).
      var go = this._upEl && this._upEl.querySelector('[data-nkc-up-go]');
      var wait = (this._config && this._config.strings && this._config.strings.paying) || '';
      if (go && wait) { go.setAttribute('aria-busy', 'true'); (go.querySelector('[data-nkc-up-go-label]') || go).textContent = wait; }
      up.verdictPromise.then(step);
    }

    _upScreen(screen, via) {
      var up = this._up;
      if (!up || up.screen === screen) return;
      var before = this._upEl && this._upEl.querySelector('.nkc-pop__box');
      up.lockH = before ? before.offsetHeight : 0; // rien ne saute : même hauteur que l'écran quitté
      up.screen = screen;
      this._upLogStep(screen);
      fire('upsell_step', { screen: screen, via: via || null, group: up.group });
      this._upPaint(this._upData());
      var box = this._upEl && this._upEl.querySelector('.nkc-pop__box');
      if (box) box.focus({ preventScroll: true });
    }

    /**
     * Secondes restantes de la VRAIE minuterie de réservation du tiroir (« Reserved for 09:55 », même échéance), 0 si elle est finie,
     * coupée ou absente : l'écran Emballage n'affiche alors aucun compteur (jamais un compte à rebours inventé à part).
     */
    _upTimerLeft() {
      var node = this.root.querySelector('[data-nkc-timer]');
      var config = this._config;
      if (!node || node.hidden || node.hasAttribute('data-done') || !config || !config.timer || !config.timer.enabled) return 0;
      var start = this._timerStart || store.get('timer', null);
      if (!start) return 0;
      return Math.max(0, config.timer.minutes * 60 - Math.floor((Date.now() - start) / 1000));
    }

    /** Construit (ou reconstruit) la fenêtre depuis les données du rendu courant ; défilements et focus gardés. */
    _upPaint(data) {
      var el = this._upEl;
      var up = this._up;
      if (!el || !up || !data) return;
      var t = data.t || {};
      var h = function (value) {
        return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
      };
      // Texte à jetons : [jeton] remplacé, en gras si demandé (« [gift] worth [gift_value] », « $0 », le prix du pack). Le segment
      // « [price] … [full] » reste d'un seul bloc (« $20 instead of $30 » ne se coupe jamais, D1).
      var fill = function (text, tokens, bold) {
        var out = h(text);
        var i = out.indexOf('[price]');
        var j = out.indexOf('[full]');
        if (i > -1 && j > i) out = out.slice(0, i) + '<span class="nkc-pop__nw">' + out.slice(i, j + 6) + '</span>' + out.slice(j + 6);
        Object.keys(tokens).forEach(function (key) {
          var value = h(tokens[key]);
          out = out.split(h('[' + key + ']')).join(bold && bold.indexOf(key) > -1 ? '<strong>' + value + '</strong>' : value);
        });
        return out;
      };
      var self = this;
      var oldScroll = el.querySelector('.nkc-pop__scroll');
      var oldRow = el.querySelector('[data-nkc-up-row]');
      var same = up.shown === up.screen;
      var scrollTop = oldScroll && same ? oldScroll.scrollTop : 0;
      var rowLeft = oldRow && same ? oldRow.scrollLeft : 0;
      var active = document.activeElement && el.contains(document.activeElement) ? document.activeElement : null;
      var focusKey = active && same ? (active.getAttribute('data-nkc-up-key') || '') : '';
      var enter = up.shown && !same ? ' is-enter' : ''; // l'écran Emballage glisse à la place de l'écran Pièces (≤ 250 ms)
      up.shown = up.screen;

      var pack = data.pack;
      var complete = !!(pack && (pack.box.in || pack.pouch.in));
      var missing = complete ? (pack.box.in ? 'pouch' : 'box') : '';
      // Toast : texte présent seulement pendant son affichage (QA B9 : un lecteur d'écran ne lit jamais « Added » sans ajout).
      var toastOn = !!(up.toastUntil && Date.now() < up.toastUntil);
      var toast = '<p class="nkc-pop__toast' + (toastOn ? ' is-on' : '') + '" data-nkc-up-toast role="status" aria-live="polite">' + (toastOn ? '<svg aria-hidden="true" width="12" height="10" viewBox="0 0 12 10"><path d="M1 5l3.4 3.4L11 1.6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg><span>' + h(t.toast) + '</span>' : '') + '</p>';
      var timerRow = function (text, rest, midnight) {
        if (data.daily) return '<p class="nkc-pop__pktimer nkc-pop__pktimer--day" data-nkc-daily-row><svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M8 4.4V8l2.4 1.6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg><span>' + h(text).split('[timer]').join('<b class="nkc-num" data-nkc-daily-time>' + hms(rest) + '</b>') + '</span></p>';
        // Pack du jour (25/10/2026) : sa propre horloge jusqu'à minuit (HH:MM:SS), tenue par _upMidTick, jamais celle de la réservation.
        if (midnight) return '<p class="nkc-pop__pktimer nkc-pop__pktimer--day" data-nkc-up-timer-row data-nkc-up-midrow><svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M8 4.4V8l2.4 1.6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg><span>' + h(text).split('[timer]').join('<b class="nkc-num" data-nkc-up-mid>' + hms(rest) + '</b>') + '</span></p>';
        var mm = Math.floor(rest / 60);
        var ss = rest % 60;
        // Une seule horloge (R2 de la relecture 2) : la valeur affichée par le tiroir à cet instant, puis écrite par son paint().
        var shown = self.root.querySelector('[data-nkc-time]');
        var clock = shown && /^\d\d:\d\d$/.test(shown.textContent) ? shown.textContent : (mm < 10 ? '0' : '') + mm + ':' + (ss < 10 ? '0' : '') + ss;
        return '<p class="nkc-pop__pktimer" data-nkc-up-timer-row><svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M8 4.4V8l2.4 1.6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg><span>' + h(text).split('[timer]').join('<b class="nkc-num" data-nkc-up-timer>' + clock + '</b>') + '</span></p>';
      };
      var lock = '<svg class="nkc-lock" aria-hidden="true" focusable="false" width="12" height="13" viewBox="0 0 12 13"><rect x="1" y="5.5" width="10" height="7" rx="1.4" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M3.5 5.5V3.8a2.5 2.5 0 0 1 5 0v1.7" fill="none" stroke="currentColor" stroke-width="1.3"/></svg>';
      var html = '<div class="nkc-pop__veil" data-nkc-up-close aria-hidden="true"></div>';
      html += '<div class="nkc-pop__box is-' + (up.screen === 'tier' ? 'pieces is-tier' : up.screen) + '" role="dialog" aria-modal="true" aria-labelledby="nkc-up-title" tabindex="-1"' + (up.lockH ? ' style="height:' + up.lockH + 'px"' : '') + '>';
      html += '<div class="nkc-pop__head"><span class="nkc-pop__badge">' + h(up.screen === 'pieces' ? (t.p1Eyebrow || t.badge) : up.screen === 'tier' ? (t.tierEyebrow || t.badge) : t.badge) + '</span>';
      html += '<button type="button" class="nkc-pop__x" data-nkc-up-close data-nkc-up-key="x" aria-label="' + h(t.close) + '"><svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 14 14"><path d="M1 1l12 12M13 1L1 13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button></div>';
      html += '<div class="nkc-pop__screen nkc-pop__screen--' + up.screen + enter + '">';
      var left = 0;

      if (up.screen === 'pieces' || up.screen === 'tier') {
        // Écran Pièces (1 pièce payée, copy du 17/10) ou écran Palier (2 pièces ou plus : la pièce qui débloque le cadeau suivant) : surtitre, titre de perte (version principale) ou de repli, sous-titre, compteur
        // de la vraie réservation, preuve, consigne, cartes « Add it · [price] », un seul ajout, puis « Continue with both ».
        var tier = up.screen === 'tier' && up.gift;
        var main = (up.p1 === 'main' || tier) && up.gift;
        var anyAdded = Object.keys(up.added).length > 0;
        var counter = data.timer !== false;
        var tok = function (text) {
          var out = h(text);
          if (up.gift) out = out.split(h('[gift_value]')).join(h(up.gift.worth)).split(h('[gift]')).join(h(up.gift.name));
          return out.split(h('[gap]')).join(h(centsMoney(up.gap, (self._config && self._config.moneyUnit) || '$1')));
        };
        // Palier après l'ajout (M3, relecture 4) : le cadeau est acquis, le titre ne dit plus « $30 away ».
        var done = tier && anyAdded && t.tierDoneTitle;
        var p1Title = tier ? tok(done ? t.tierDoneTitle : t.tierTitle) : main ? tok(t.p1Title) : h(t.p1FbTitle);
        var p1Sub = tier ? tok(done && t.tierDoneSub ? t.tierDoneSub : t.tierSub) : main ? tok(t.p1Sub) : h(t.p1FbSub);
        html += '<div class="nkc-pop__scroll">';
        html += '<h2 class="nkc-pop__title nkc-pop__p1title" id="nkc-up-title">' + p1Title + '</h2>';
        // Écran Pièces principal (coordinateur, 18/10/2026) : « Add $60 · your $110 BELLORE harness is free » = le prix de la 1re carte (la
        // marche la plus petite), jamais l'écart ; photo du cadeau en petit à côté, la hauteur ne bouge pas (le texte se range à droite).
        var firstPick = !tier && main && up.pieces[0] && up.pieces[0].product;
        if (firstPick) p1Sub = p1Sub.split(h('[price]')).join(h(firstPick.money));
        var giftImg = !tier && main && up.gift && up.gift.img ? '<img class="nkc-pop__giftimg" src="' + h(up.gift.img) + '" alt="" width="40" height="40" decoding="async">' : '';
        html += giftImg ? '<p class="nkc-pop__sub nkc-pop__sub--gift">' + giftImg + '<span>' + p1Sub + '</span></p>' : '<p class="nkc-pop__sub">' + p1Sub + '</p>';
        var timerKey = data.daily ? t.dailyGifts : tier ? t.tierTimer : t.p1Timer;
        left = counter && timerKey ? (data.daily ? untilMidnight() : this._upTimerLeft()) : 0;
        if (left > 0 || (data.daily && counter && timerKey)) html += timerRow(timerKey, left);
        if (!tier && t.p1Proof) html += '<p class="nkc-pop__proof">' + h(t.p1Proof) + '</p>';
        // Palier acquis (M3) : « Each one unlocks your gift » ne vaut plus ; sa place revient aux cartes (le titre passe sur 3 lignes).
        if (!done) html += '<p class="nkc-pop__label">' + h(tier ? t.tierLabel : t.p1Label) + '</p>';
        if (up.v2 && !tier) html += self._upCardsV2(up, t, data, anyAdded, h); // écran Pièces v2 (25/10/2026)
        else {
        html += '<div class="nkc-pop__row" data-nkc-up-row role="list">';
        up.pieces.forEach(function (pick) {
          var p = pick.product;
          var vid = String(pick.variant.id);
          var added = !!up.added[vid]; // coché dès le tap (ajout optimiste), décoché si Shopify refuse
          var off = anyAdded && !added; // un seul ajout : les autres cartes s'effacent
          var size = String(pick.variant.t || '').replace(/\s*\/\s*/g, '/');
          // Ensemble haut + bas à la même taille (« M / M ») : une seule taille affichée, « Size M » (audit du 25/10/2026 : « M/M » se lisait mal).
          var sizeParts = size.split('/').filter(function (part) { return SIZE_LIKE.test(part); }); // « M / M / Green » : la couleur n'est pas une taille (25/10/2026)
          if (sizeParts.length && sizeParts.every(function (part) { return part === sizeParts[0]; })) size = sizeParts[0];
          else if (sizeParts.length) size = sizeParts.join('/');
          var label = added ? t.added : String((tier ? t.tierAdd : t.p1Add) || '').replace('[price]', p.money);
          html += '<div class="nkc-pop__cell" role="listitem">';
          html += '<button type="button" class="nkc-pop__card' + (added ? ' is-added' : '') + (off ? ' is-off' : '') + '" data-nkc-up-add="' + h(vid) + '" data-pid="' + h(p.id) + '" data-price="' + h(p.price) + '" data-nkc-up-key="a' + h(vid) + '"' + (added || off ? ' aria-disabled="true"' : '') + ' aria-label="' + h(p.name + ' · ' + String(t.size || '').replace('[size]', size) + ' · ' + label) + '">';
          html += '<span class="nkc-pop__media">' + (p.img ? '<img src="' + h(p.img) + '" alt="" width="360" height="480" decoding="async">' : '') + (added ? '<span class="nkc-pop__tick" aria-hidden="true"><svg width="12" height="10" viewBox="0 0 12 10"><path d="M1 5l3.4 3.4L11 1.6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' : '') + '</span>';
          html += '<span class="nkc-pop__name">' + h(p.name) + '</span>';
          html += '<span class="nkc-pop__size">' + h(String(t.size || '').replace('[size]', size)) + '</span>';
          html += '<span class="nkc-pop__cta' + (added ? ' is-added' : '') + '">' + (added ? '<svg aria-hidden="true" width="11" height="9" viewBox="0 0 12 10"><path d="M1 5l3.4 3.4L11 1.6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' : '') + '<span>' + h(label) + '</span></span>';
          html += '</button>';
          if (up.failed[vid]) html += '<span class="nkc-pop__err" role="alert">' + h(t.error) + '</span>';
          html += '</div>';
        });
        html += '</div>';
        }
        html += '</div>';
        html += '<div class="nkc-pop__foot">';
        // Avant l'ajout : « Continue with 1 piece » ; après : « Continue with both » (bouton plein). Les deux mènent à l'écran Emballage.
        // Écran Palier (R4) : « Continue » quand l'écran Emballage suit, « Check out now » seulement s'il est le dernier.
        var goLabel = tier ? (this._upPackOffer(data) && t.tierNext ? t.tierNext : t.tierContinue) : t.p1Continue;
        if (anyAdded) html += '<button type="button" class="nkc-pop__go" data-nkc-up-go data-nkc-up-key="go"><span data-nkc-up-go-label>' + h(goLabel) + '</span><svg aria-hidden="true" focusable="false" width="14" height="12" viewBox="0 0 14 12"><path d="M1 6h11M8 1.5L12.5 6 8 10.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></button>';
        else html += '<button type="button" class="nkc-pop__skip nkc-pop__skip--alone" data-nkc-up-go data-nkc-up-key="go">' + (tier ? tok(t.tierSkip) : h(t.p1Skip)) + '</button>';
        html += toast.replace(h(t.toast), h(tier ? tok(t.tierToast) : main ? t.p1Toast : t.p1FbToast)) + '</div>';
      } else {
        // Écran Emballage (copy du 17/10) : photo réelle pleine largeur, surtitre, titre, sous-titre, compteur de la vraie réservation,
        // prix, bouton qui ajoute et part au paiement, lien qui part sans. Pack complet, complément A (elle a la boîte : le pochon) ou
        // B (elle a le pochon : la boîte), ou son code en pourcentage qui couvre le pack (mode « pct »). Jamais « $20 » en A et B.
        var verdict = up.verdict || { mode: 'normal' };
        var v = !complete ? '' : (missing === 'pouch' ? 'A' : 'B');
        var vip = verdict.mode === 'pct';
        var strike = data.strike !== false;
        var counter = data.timer !== false;
        var amounts = complete ? { price: pack.add, full: pack.addFull, box: pack.boxMoney, pouch: pack.pouchMoney } : { price: pack.price, full: pack.full, box: pack.boxMoney, pouch: pack.pouchMoney };
        if (vip) amounts.price = centsMoney(verdict.cost, (this._config && this._config.moneyUnit) || pack.price);
        var fillPrice = function (text) {
          var out = fill(text, { price: amounts.price, box: amounts.box, pouch: amounts.pouch, pct: String(verdict.pct || 0) }, ['price']);
          return out.split(h('[full]')).join(strike ? '<s>' + h(amounts.full) + '</s>' : h(amounts.full));
        };
        var imgs = !complete ? [pack.box.img, pack.pouch.img] : [packBig(missing === 'box' ? pack.box.img : pack.pouch.img)];
        // Un seul emballage proposé (A, B) : son nom, et son prix plein (N1, N2 de la relecture 3).
        var item = v ? String((missing === 'box' ? t.pkItemBox : t.pkItemPouch) || '') : '';
        var itemLower = item ? item.charAt(0).toLowerCase() + item.slice(1) : '';
        var oneFull = missing === 'box' ? pack.boxMoney : pack.pouchMoney;
        var eyebrow = vip ? fill(t.pkVipEyebrow, { pct: String(verdict.pct || 0) }) : h(v ? t['pk' + v + 'Eyebrow'] : t.pkEyebrow); // HTML échappé une fois
        if (data.midnight && t.pkDayEyebrow && !vip) eyebrow = h(t.pkDayEyebrow); // pack du jour (25/10/2026) : « TODAY'S EXCLUSIVE »
        var title = v ? t['pk' + v + 'Title'] : t.pkTitle;
        var sub = v ? t['pk' + v + 'Sub'] : t.pkSub;
        var priceLine = '';
        if (vip && v && t.pkVipOne) priceLine = fill(t.pkVipOne, { item: item, price: amounts.price, pct: String(verdict.pct || 0) }, ['price']); // « Gift box: $11.25 with your 25% off » (N1)
        else if (vip) priceLine = fillPrice(t.pkVipPrice);
        else if (!up.expired) {
          if (v) priceLine = fillPrice(t['pk' + v + 'Price']);
          else {
            // « Gift box $15 + pouch $15. » en regular, « Both $20. » en gras et plus grand (règle de la copy).
            var parts = String(t.pkPrice || '').split(/\.\s+/);
            priceLine = parts.length > 1 ? fillPrice(parts[0] + '.') + ' <span class="nkc-pop__pkboth">' + fillPrice(parts.slice(1).join('. ')) + '</span>' : fillPrice(t.pkPrice);
          }
        }
        else if (!v) priceLine = fillPrice(t.pkPriceOver); // offre finie à 00:00 : prix seuls, jamais « Both $20 »
        else if (t.pkOneOver) priceLine = fill(t.pkOneOver, { item: item, price: oneFull }, ['price']); // A ou B à 00:00 : « Satin pouch $15. » (N2)
        var timerText = data.daily ? t.dailyPack : data.midnight && t.pkMidnight ? t.pkMidnight : vip ? t.pkVipTimer : v ? t.pkAbTimer : t.pkTimer; // pack du jour (25/10/2026)
        // Bouton au montant affiché, jamais un autre au clic : A ou B en VIP (son prix) ou après 00:00 (prix plein) → « Yes, add the gift box +$15 ».
        var cta = v && (vip || up.expired) && t.pkOneCta ? fill(t.pkOneCta, { item: itemLower, price: vip ? amounts.price : oneFull }) : up.expired || vip || !v ? h(t.pkCta) : fill(t['pk' + v + 'Cta'], { price: pack.add }); // HTML échappé une fois
        left = counter && !up.expired ? (data.daily || data.midnight ? untilMidnight() : this._upTimerLeft()) : 0;
        if (data.midnight && !up.expired) this._upPackDayMark(data); // vu aujourd'hui : l'offre finit à minuit pour elle
        html += '<div class="nkc-pop__scroll">';
        html += '<div class="nkc-pop__pkphotos' + (imgs.length === 1 ? ' is-one' + (missing === 'pouch' ? ' is-pouch' : '') : '') + '">' + imgs.filter(Boolean).map(function (src) { return '<img src="' + h(src) + '" alt="" width="640" height="640" decoding="async">'; }).join('') + '</div>';
        if (!(up.expired && !vip)) html += '<p class="nkc-pop__eyebrow">' + eyebrow + '</p>'; // surtitre de l'offre faux après 00:00 (R13, N2)
        html += '<h2 class="nkc-pop__title nkc-pop__pktitle" id="nkc-up-title">' + h(title) + '</h2>';
        // Sous-titre en text-wrap: pretty (R10 : balance le serrait en colonne étroite sur 3 lignes).
        // Les 3 derniers mots liés (espaces insécables) : jamais « new. » ou « lingerie new. » seul en bas (N6), texte inchangé. Pack
        // complet : retour à la ligne entre les 2 phrases (N6, relecture 4), la 1re tenue sur une ligne par _upFit (plancher 12,5 px).
        var bind = function (x) { return String(x || '').replace(/ (\S+) (\S+)$/, '\u00a0$1\u00a0$2'); };
        var cut = !v ? String(sub || '').search(/\. \S/) : -1;
        if (cut > 0) html += '<p class="nkc-pop__sub nkc-pop__pksub is-split"><span class="nkc-pop__s1" data-nkc-s1>' + h(bind(String(sub).slice(0, cut + 1))) + '</span> <span class="nkc-pop__s2">' + h(bind(String(sub).slice(cut + 2))) + '</span></p>';
        else html += '<p class="nkc-pop__sub nkc-pop__pksub">' + h(bind(sub)) + '</p>';
        if (left > 0 || (data.daily && counter && !up.expired)) html += timerRow(timerText.replace('[price]', pack.add), left, !!data.midnight);
        // Ligne de prix libre de passer à la ligne ; seuls les montants restent d'un bloc (« Both $20. », « $5 more, not $15 ») : R1.
        if (priceLine) html += '<p class="nkc-pop__pkprice">' + priceLine + '</p>';
        // Le pack fait passer un palier et l'envoi du départ pose le cadeau : « Also unlocks your free BELLORE harness » (R6, tous écrans).
        var unlock = t.pkUnlocks ? this._upPackUnlock(data) : null;
        if (unlock) html = html.replace('class="nkc-pop__box is-pack"', 'class="nkc-pop__box is-pack has-unlock"'); // N3 : photos plus basses sur petit écran
        if (unlock) html += '<p class="nkc-pop__pkunlock"><svg aria-hidden="true" focusable="false" width="13" height="13" viewBox="0 0 16 16"><path d="M2 7h12v8H2zM1 4h14v3H1zM8 4v11M8 4C6.5 1.5 3.5 1.8 4.2 3.6 4.6 4.4 8 4 8 4zm0 0c1.5-2.5 4.5-2.2 3.8-.4C11.4 4.4 8 4 8 4z" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg><span>' + fill(t.pkUnlocks, { gift: unlock.name }, ['gift']) + '</span></p>';
        html += '</div>';
        html += '<div class="nkc-pop__foot">';
        html += '<button type="button" class="nkc-pop__go" data-nkc-up-go data-pack data-nkc-up-key="go"><span data-nkc-up-go-label>' + cta + '</span></button>';
        html += '<button type="button" class="nkc-pop__skip" data-nkc-up-skip data-nkc-up-key="skip">' + h(v ? t.pkAbNo : t.pkNo) + '</button>';
        html += toast + '</div>';
      }
      html += '</div></div>';

      el.innerHTML = html;
      this._dailyPaint();
      var scroll = el.querySelector('.nkc-pop__scroll');
      var row = el.querySelector('[data-nkc-up-row]');
      if (scroll) scroll.scrollTop = scrollTop;
      if (row) row.scrollLeft = rowLeft;
      var back = focusKey ? el.querySelector('[data-nkc-up-key="' + esc(focusKey) + '"]') : null;
      if (active) (back || el.querySelector('.nkc-pop__box')).focus({ preventScroll: true });
      if (!el.hidden) this._upFit();
      // Compteur : écrit par le paint() de la minuterie du tiroir, à la même seconde que « Reserved for … » (R2) ; à 0, _upTimerEnd.
      clearInterval(this._upTimer);
      clearInterval(this._upMidTimer);
      if (el.querySelector('[data-nkc-up-mid]')) {
        var selfMid = this;
        this._upMidTimer = setInterval(function () {
          var nodes = el.querySelectorAll('[data-nkc-up-mid]');
          if (!nodes.length || el.hidden) { clearInterval(selfMid._upMidTimer); return; }
          var rest = untilMidnight();
          nodes.forEach(function (n) { n.textContent = hms(rest); });
          if (rest <= 0) { clearInterval(selfMid._upMidTimer); selfMid._upTimerEnd(true); }
        }, 1000);
      }
    }

    /** Minuterie du tiroir arrivée à 00:00 : la ligne du compteur disparaît ; écran Emballage ouvert, l'offre à prix promis s'arrête. */
    _upTimerEnd(midnight) {
      var daily = this._upData();
      if (daily && daily.daily) return; // Le compteur quotidien se renouvelle à minuit ; aucun changement de prix.
      var el = this._upEl;
      var up = this._up;
      if (!el || el.hidden || !up) return;
      // Pack du jour (25/10/2026) : la fin de la réservation du tiroir n'arrête pas l'offre du jour ; seul minuit l'arrête.
      if (!midnight && up.screen === 'pack' && el.querySelector('[data-nkc-up-midrow]')) return;
      var rowNode = el.querySelector('[data-nkc-up-timer-row]');
      if (rowNode) rowNode.hidden = true;
      if (up.screen === 'pack' && !up.leaving && !up.expired && !(up.verdict && up.verdict.mode === 'pct')) {
        up.expired = true; // « Your size and this price: 00:00 left » : le prix promis ne tient plus, il disparaît
        fire('upsell_expired', { group: up.group });
        this._upPaint(this._upData());
      }
    }

    /**
     * Libellés des cartes ajustés à leur largeur réelle : la mesure porte sur le texte (le span), plancher 9,5 px (D2). Pastille du
     * compteur sur UNE ligne (coordinateur, 17/10 : « Last chance to add. » passait sur 2 lignes à 390 px), plancher 11 px ; au
     * plancher, 2 lignes plutôt qu'une phrase coupée.
     */
    _upFit() {
      var self = this;
      var el = this._upEl;
      if (!el || el.hidden) return;
      el.querySelectorAll('.nkc-pop__cta > span').forEach(function (label) { self._fit(label, 9.5); });
      // Libellé du bouton (« Yes, add the satin pouch +$11.25 » coupé à 390 px, relecture 3) : une ligne, plancher 11 px, puis 2 lignes.
      el.querySelectorAll('.nkc-pop__go > [data-nkc-up-go-label], .nkc-pop__pktimer > span').forEach(function (label) {
        label.style.removeProperty('white-space');
        self._fit(label, 11);
        if (label.clientWidth > 0 && label.scrollWidth > label.clientWidth + 1) label.style.setProperty('white-space', 'normal'); // jamais sur une mesure à 0
      });
      // Sous-titre du pack (N6) : la 1re phrase sur une ligne, tout le sous-titre à la même taille ; au plancher, elle passe à la ligne.
      var s1 = el.querySelector('[data-nkc-s1]');
      if (s1) {
        var para = s1.parentNode;
        para.style.removeProperty('font-size');
        s1.style.removeProperty('white-space');
        self._fit(s1, 12.5);
        var size = s1.style.getPropertyValue('font-size');
        if (size) { para.style.setProperty('font-size', size, 'important'); s1.style.removeProperty('font-size'); }
        if (s1.clientWidth > 0 && s1.scrollWidth > s1.clientWidth + 1) s1.style.setProperty('white-space', 'normal');
      }
    }

    /** Cadeaux débloqués et absents, hors cadeaux que la cliente a elle-même retirés. */
    _missing() {
      var declined = {};
      this._declined().forEach(function (id) { declined[String(id)] = true; });
      var placing = this._placing || {};
      var count = 0;
      this.root.querySelectorAll('[data-nkc-claim]').forEach(function (claim) {
        var tier = claim.getAttribute('data-tier');
        if (!declined[tier] && !placing[tier]) count += 1;
      });
      return count;
    }

    /** Cadeaux débloqués et absents que la cliente a retirés elle-même (jamais ceux refusés par Shopify). */
    _declinedMissing() {
      var declined = {};
      store.get('declined', []).forEach(function (id) { declined[String(id)] = true; });
      var failed = this._failed;
      var count = 0;
      this.root.querySelectorAll('[data-nkc-claim]').forEach(function (claim) {
        var tier = String(claim.getAttribute('data-tier'));
        if (declined[tier] && !failed[tier]) count += 1;
      });
      return count;
    }

    /**
     * Départ différé (clic retenu le temps des écritures) : la logique du rappel cadeau est rejouée, puis
     * navigation DIRECTE vers /checkout. Jamais requestSubmit : sur FR (Candy Rack), un envoi programmatique
     * du formulaire est détourné — le formulaire produit part à sa place (POST /cart/add) et la cliente
     * atterrit sur /cart (mesure WebKit du 07/10, étapes 2 et 3).
     */
    _pay() {
      var form = this.root.querySelector('[data-nkc-checkout]');
      if (!form) return;
      var pseudo = { target: form, prevented: false, preventDefault: function () { this.prevented = true; } };
      this._onSubmit(pseudo);
      if (pseudo.prevented) return; // rappel cadeau affiché : la cliente décide
      root.location.assign(routeRoot() + 'checkout');
    }

    /**
     * Le paiement est retenu tant qu'une ÉCRITURE est en vol ou décidée : ajout du thème (openEarly, pas encore
     * répondu), plan de cadeaux en cours de décision sur /cart.js, requête cart/update.js ou cart/change.js
     * partie, tâche d'écriture en file. Jamais pour un rechargement de section ni des recommandations.
     */
    _holding() {
      return this._inflight > 0 || !!this._refreshDue || (this._deciding || 0) > 0 || writing() || (this._queuedWrites || 0) > 0;
    }

    /** Libellé « Un instant… » sur le bouton de paiement (état pressé) ; false : libellé d'origine. */
    _payLabel(on) {
      var pay = this.root.querySelector('[data-nkc-checkout] [name="checkout"]');
      if (!pay) return;
      var text = (this._config && this._config.strings && this._config.strings.paying) || '';
      var spans = pay.querySelectorAll('span');
      if (on) {
        this.setAttribute('paying', '');
        if (!text || pay.getAttribute('data-nkc-paying') === '1') return;
        pay.setAttribute('data-nkc-paying', '1');
        pay.setAttribute('aria-live', 'polite');
        spans.forEach(function (span) { span.setAttribute('data-nkc-label', span.textContent); span.textContent = text; });
      } else {
        this.removeAttribute('paying');
        if (pay.getAttribute('data-nkc-paying') !== '1') return;
        pay.removeAttribute('data-nkc-paying');
        spans.forEach(function (span) { if (span.hasAttribute('data-nkc-label')) { span.textContent = span.getAttribute('data-nkc-label'); span.removeAttribute('data-nkc-label'); } });
        this.removeAttribute('paying');
      }
    }

    _payWhenIdle() {
      var self = this;
      if (this._payWaiting) return;
      this._payWaiting = true;
      this._payLabel(true);
      var go = function () {
        if (!self._payWaiting) return;
        self._payWaiting = false;
        self._payNow = true;
        self._pay();
      };
      // 1 200 ms au plus, quoi qu'il arrive (décision 07/10 : le clic part toujours en moins de 1,5 s). Une
      // écriture encore en vol à ce moment est traitée par Shopify avant ou pendant la création du paiement
      // (mesures DE du 07/10 : départ à 1 229-1 319 ms avec un ajout du thème en vol → page de paiement au bon total).
      var limit = setTimeout(go, Math.min(PAY_HOLD_MS, timeout()));
      var check = function () {
        if (!self._payWaiting) return;
        if (self._holding()) { setTimeout(check, 30); return; }
        clearTimeout(limit);
        go();
      };
      check();
    }

    /** Réponse de cart/change.js ou cart/update.js : panier complet + section rendue. */
    _mutate(path, body, options) {
      var self = this;
      return this._enqueue(function () { return self._mutation(path, body, options); }, undefined, true);
    }

    _mutation(path, body, options) {
      var self = this;
      body.sections = [SECTION];
      body.sections_url = this._sectionUrl();
      var prepare = options && options.unit ? this._freshUnit(body, options.unit)
        : (options && options.undoKey ? this._freshLine(body, options.undoKey) : Promise.resolve(body));
      return prepare.then(function (ready) {
        if (!ready) return self._fetchState().then(function (state) { return self._settle(state, 0, null); }); // plus rien à retirer
        return cartApi(path, ready);
      }).then(function (res) {
        if (!res) return null;
        if (!res.ok) {
          // Ligne disparue entre-temps (clé regénérée par une remise, retrait dans un autre onglet) : l'état
          // réel est réaffiché, sans le message technique de Shopify. Autre refus : l'action échouée est nommée
          // (jamais le texte anglais de Shopify sur cart/change.js ni cart/update.js).
          var gone = path === 'cart/change.js' && (res.status === 404 || res.status === 400);
          var message = gone ? null : self._errorText(options && options.context);
          return self._fetchState().then(function (state) {
            return self._settle(state, 0, message ? { notice: message, error: true } : null);
          });
        }
        var after = options && options.after ? options.after(res.data) : null;
        return Promise.resolve(after).then(function (extra) {
          var html = res.data.sections && res.data.sections[SECTION];
          if (!html || (extra && extra.refetch)) return self._fetchState().then(function (state) { return self._settle(state, 0, extra); });
          return self._settle({ cart: res.data, html: html }, 0, extra);
        });
      });
    }

    /**
     * Retrait d'UNE unité (lignes par exemplaire, décision 50) : la ligne est relue sur un panier frais au
     * moment de l'envoi. Clé encore là : sa quantité − 1. Clé disparue (Shopify regénère les clés quand une
     * remise de ligne se réalloue, ex. GLOW) : la ligne fraîche de même variante et mêmes propriétés (la plus
     * fournie) prend le relais. Aucune ligne : rien n'est envoyé. La quantité affichée au clic ne fait pas foi.
     */
    _freshUnit(body, unit) {
      var self = this;
      return cartApi('cart.js').then(function (res) {
        var fresh = res.ok && res.data && res.data.items ? res.data : null;
        if (!fresh) return body;
        var line = fresh.items.filter(function (it) { return it.key === body.id; })[0];
        if (!line) {
          var known = ((self._cart && self._cart.items) || []).filter(function (it) { return it.key === body.id; })[0];
          var vid = String(known ? known.variant_id : unit.variantId);
          var props = known ? JSON.stringify(known.properties || {}) : null;
          var same = fresh.items.filter(function (it) {
            return String(it.variant_id) === vid && (props === null || JSON.stringify(it.properties || {}) === props);
          }).sort(function (a, b) { return b.quantity - a.quantity; });
          line = same[0];
        }
        if (!line) return null;
        self._fillUndo(line, 1);
        return Object.assign({}, body, { id: line.key, quantity: Math.max(0, line.quantity - 1) });
      }).catch(function () { return body; });
    }

    /**
     * Retrait d'une ligne entière avec « Annuler » : la ligne est relue sur un panier frais pour connaître ce qu'il
     * faudra remettre (variante, quantité, propriétés : le JSON du Liquid ne porte pas les propriétés). La requête
     * part ensuite telle quelle.
     */
    _freshLine(body, key) {
      var self = this;
      return cartApi('cart.js').then(function (res) {
        var fresh = res.ok && res.data && res.data.items ? res.data : null;
        var line = fresh ? fresh.items.filter(function (it) { return it.key === key; })[0] : null;
        if (line) self._fillUndo(line, line.quantity);
        return body;
      }).catch(function () { return body; });
    }

    /** Complète le contenu à remettre par « Annuler » depuis la ligne Shopify fraîche. */
    _fillUndo(line, quantity) {
      var undo = this._undo;
      if (!undo || undo.key !== line.key || undo.gift) return;
      undo.id = line.variant_id;
      undo.quantity = quantity;
      undo.properties = line.properties && Object.keys(line.properties).length ? line.properties : undefined;
    }

    _changeLine(key, quantity, unit, context) {
      var self = this;
      context = context || 'remove';
      // Ligne entière retirée avec « Annuler » à remettre : relue avant l'envoi (propriétés, quantité).
      var undoKey = quantity === 0 && !unit && this._undo && this._undo.key === key && !this._undo.gift ? key : null;
      return this._enqueue(function () { return self._mutation('cart/change.js', { id: key, quantity: quantity }, { unit: unit || null, context: context, undoKey: undoKey }); }, context);
    }

    /**
     * Message d'un ajout refusé : la raison de Shopify seulement en 422 (stock, localisée par Shopify) ;
     * un 404 (« Cannot find variant », toujours en anglais) ou autre : l'action échouée, dans la langue de la page.
     */
    _refusal(res, context) {
      var strings = (this._config && this._config.strings) || {};
      var raw = res.data && (res.data.description || res.data.message) ? String(res.data.description || res.data.message) : '';
      // « Cannot find variant » (toujours en anglais, 404 ou 422) : la taille n'existe plus, dit dans la langue de la page.
      if (context !== 'gift' && (res.status === 404 || /variant/i.test(raw)) && strings.errorVariant) return strings.errorVariant;
      if (res.status === 422 && res.data && res.data.description) return res.data.description;
      return this._errorText(context);
    }

    _add(items, context) {
      var self = this;
      context = context || 'add';
      return this._enqueue(function () {
        return cartApi('cart/add.js', { items: items }).then(function (res) {
          var notice = res.ok ? null : self._refusal(res, context);
          return self._fetchState().then(function (state) { return self._settle(state, 0, { notice: notice, error: !!notice }); });
        });
      }, context);
    }

    /** La cliente prend (ou reprend) son cadeau : le refus est oublié, la taille choisie est retenue. */
    _claimGift(tier, variantId) {
      var self = this;
      this._undecline([tier]);
      delete this._failed[tier];
      var sizes = store.get('sizes', {});
      sizes[String(tier)] = String(variantId);
      store.set('sizes', sizes);
      this._justClaimed = tier;
      this.ghost(true); // le cadeau arrive en fin de liste : le fantôme aussi
      return this._enqueue(function () {
        return self._giftIn(variantId, tier).then(function (res) {
          var notice = res.ok ? null : self._refusal(res, 'gift');
          return self._fetchState().then(function (state) { return self._settle(state, 0, { notice: notice, error: !!notice }); });
        });
      }, 'gift');
    }

    /**
     * « Changer de taille » sur un cadeau à taille (tour 4 du jury, UX, décision 223) : une rangée de pastilles S M L XL s'ouvre dans la
     * ligne (tailles lues sur le bloc de rappel du palier, taille actuelle aria-pressed), sans retrait ni défilement. Renvoie false
     * s'il n'y a pas de bloc de rappel (réglage coupé) : l'ancien chemin prend le relais.
     */
    _giftPicks(line, tier) {
      // Tailles : JSON rendu par le Liquid sur le lien (data-nkc-gift-sizes : variantes disponibles à 0 du cadeau) ; à défaut, le bloc de
      // rappel du palier (il n'existe que tant que le cadeau n'est pas au panier).
      var edit = line.querySelector('[data-nkc-gift-resize]');
      var sizes = [];
      try { sizes = JSON.parse((edit && edit.getAttribute('data-nkc-gift-sizes')) || '[]'); } catch (e) { sizes = []; }
      if (!Array.isArray(sizes) || sizes.length < 2) {
        var source = this.root.querySelector('[data-nkc-reminder-tier="' + esc(String(tier)) + '"]') || this.querySelector('[data-nkc-reminder-tier="' + esc(String(tier)) + '"]');
        sizes = source ? Array.prototype.map.call(source.querySelectorAll('[data-nkc-reminder-add]'), function (button) {
          return { id: button.getAttribute('data-nkc-reminder-add'), t: (button.textContent || '').trim() };
        }) : [];
      }
      if (sizes.length < 2) return false;
      if (line.querySelector('[data-nkc-gift-picks]')) { this._giftUnpick(line); return true; }
      var strings = (this._config && this._config.strings) || {};
      var current = String(line.getAttribute('data-vid') || '');
      var box = document.createElement('div');
      box.className = 'nkc-picks nkc-picks--gift';
      box.setAttribute('data-nkc-gift-picks', '');
      var row = document.createElement('div');
      row.className = 'nkc-picks__row';
      row.setAttribute('role', 'group');
      row.setAttribute('aria-label', strings.size || '');
      sizes.forEach(function (size) {
        var vid = String(size.id);
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'nkc-pick';
        chip.setAttribute('data-nkc-gift-pick', vid);
        chip.setAttribute('data-nkc-gift-pick-tier', String(tier));
        chip.setAttribute('aria-pressed', vid === current ? 'true' : 'false');
        chip.textContent = String(size.t || '').trim();
        row.appendChild(chip);
      });
      box.appendChild(row);
      (line.querySelector('.nkc-line__main') || line).appendChild(box);
      line.classList.add('is-picking');
      if (edit) edit.setAttribute('aria-expanded', 'true');
      fire('gift_resize_open', { tier: tier });
      return true;
    }

    _giftUnpick(line) {
      var box = line.querySelector('[data-nkc-gift-picks]');
      if (box) box.remove();
      line.classList.remove('is-picking');
      var edit = line.querySelector('[data-nkc-gift-resize]');
      if (edit) { edit.setAttribute('aria-expanded', 'false'); edit.focus({ preventScroll: true }); }
    }

    /** Échange de taille d'un cadeau en UNE requête cart/update.js (ancienne clé → 0, nouvelle variante → 1) : jamais un refus. */
    _resizeGift(line, tier, variantId) {
      if (line.hasAttribute('data-removing') || !variantId) return null;
      var key = line.getAttribute('data-key');
      this._undecline([tier]);
      delete this._failed[tier];
      var sizes = store.get('sizes', {});
      sizes[String(tier)] = String(variantId);
      store.set('sizes', sizes);
      this._justClaimed = tier;
      this._lastKey = key;
      line.setAttribute('data-removing', '');
      var updates = {};
      updates[key] = 0;
      updates[String(variantId)] = 1;
      fire('gift_resize', { tier: tier, variantId: variantId });
      return this._mutate('cart/update.js', { updates: updates }, { context: 'gift' });
    }

    /* ---------- Code promo ---------- */

    /**
     * Les codes déjà posés sont toujours repris, sans cela Shopify les retire. Ils sont relus sur un
     * panier frais DANS la tâche mise en file, juste avant l'écriture (un code posé entre-temps par un
     * autre script, un autre onglet ou un lien /discount/… n'est pas perdu).
     */
    _writeCodes(edit, after) {
      var self = this;
      return this._enqueue(function () {
        return cartApi('cart.js').then(function (res) {
          if (!res.ok || !res.data) throw new Error('nkc-cart');
          var codes = (res.data.discount_codes || []).map(function (entry) { return entry.code; });
          return self._mutation('cart/update.js', { discount: edit(codes).join(',') }, { after: after, context: 'code' });
        });
      }, 'code');
    }

    /**
     * Code de l'offre « N pièces, la moins chère offerte » (réglage bxgy) saisi sous le minimum : une consigne
     * (« Code GLOW enregistré. Ajoutez encore 1 pièce… »), pas une erreur. Sinon null.
     */
    _bxgyHint(code) {
      var bxgy = this._config && this._config.bxgy;
      var strings = (this._config && this._config.strings) || {};
      if (!bxgy || !bxgy.code || String(code).toUpperCase() !== String(bxgy.code).toUpperCase()) return null;
      var missing = Number(bxgy.min) - Number(bxgy.qty || 0);
      if (!(missing > 0)) return null;
      var text = missing === 1 ? strings.codeWaitOne : strings.codeWaitOther;
      if (!text) return null;
      return text.replace('[code]', String(code).toUpperCase()).replace('[min]', bxgy.min).replace('[count]', missing);
    }

    _applyCode(code) {
      var self = this;
      var wanted = code.toUpperCase();
      this._codeError(null);
      this._lastCause = 'code';
      this._lastCode = wanted;
      return this._writeCodes(function (codes) {
        return codes.filter(function (existing) { return existing.toUpperCase() !== wanted; }).concat([code]);
      }, function (cart) {
        var entry = (cart.discount_codes || []).filter(function (item) { return item.code.toUpperCase() === wanted; })[0];
        fire('code_apply', { code: wanted, applicable: !!(entry && entry.applicable) });
        if (entry && entry.applicable) { self._forgetRefused([code]); return { codeApplied: wanted }; }
        // Code de l'offre « N pièces » sous le minimum : gardé (il s'appliquera à la pièce suivante), dit en consigne.
        var hint = self._bxgyHint(code);
        if (hint) return { codeInfo: hint, codeRefused: code };
        // Tour 1 du jury (décision 160) : un code refusé ne reste pas attaché au panier. Il est retiré tout de suite
        // (les autres codes sont repris), le panier est relu, une seule ligne le dit sous le champ.
        var kept = (cart.discount_codes || []).map(function (item) { return item.code; })
          .filter(function (existing) { return String(existing).toUpperCase() !== wanted; });
        var link = linkCode();
        if (link && link.toUpperCase() === wanted) dropLinkCode();
        self._linked[wanted] = true;
        return cartApi('cart/update.js', { discount: kept.join(',') }).then(function () {
          return { codeError: self._config.strings.codeInvalid.replace('[code]', wanted), codeRefused: code, refetch: true };
        });
      });
    }

    _removeCode(code) {
      var gone = String(code).toUpperCase();
      this._codeError(null);
      this._forgetRefused([code]);
      fire('code_remove', { code: gone });
      // Code posé tout seul (levier 2) retiré par la cliente : il n'est pas reposé pour ce panier.
      var bxgy = this._config && this._config.bxgy;
      if (this._config && this._config.autoCode && bxgy && String(bxgy.code).toUpperCase() === gone) store.set('autoOff', true);
      // Retiré pour de bon : aussi du cookie du lien /discount/…, sinon le paiement le réappliquerait.
      var link = linkCode();
      if (link && link.toUpperCase() === gone) dropLinkCode();
      this._linked[gone] = true;
      return this._writeCodes(function (codes) {
        return codes.filter(function (existing) { return existing.toUpperCase() !== gone; });
      });
    }

    _forgetRefused(codes) {
      var drop = {};
      codes.forEach(function (code) { drop[String(code).toUpperCase()] = true; });
      var list = store.get('refused', []);
      var kept = list.filter(function (code) { return !drop[String(code).toUpperCase()]; });
      if (kept.length !== list.length) store.set('refused', kept);
    }

    /**
     * Un code saisi et refusé reste attaché au panier tant que celui-ci vit (il peut s'appliquer plus
     * tard : GLOW au troisième article). Shopify ne dit pas si un code est inexistant ou seulement non
     * applicable. Quand le panier est vidé, les codes refusés à la saisie sont retirés : ils ne suivent
     * pas la cliente dans un panier suivant.
     */
    _sweepRefused() {
      var cart = this._cart;
      var config = this._config;
      if (!cart || !config || !config.cart || config.cart.item_count !== 0) return;
      var refused = store.get('refused', []);
      if (!refused.length) return;
      store.set('refused', []);
      var drop = {};
      refused.forEach(function (code) { drop[String(code).toUpperCase()] = true; });
      var stale = (cart.discount_codes || []).filter(function (entry) { return !entry.applicable && drop[String(entry.code).toUpperCase()]; });
      if (!stale.length) return;
      stale.forEach(function (entry) {
        var link = linkCode();
        if (link && link.toUpperCase() === String(entry.code).toUpperCase()) dropLinkCode();
      });
      this._writeCodes(function (codes) {
        return codes.filter(function (existing) { return !drop[existing.toUpperCase()]; });
      });
    }

    /** Codes posés mais non appliqués (ex. GLOW avec deux articles) : dits sous le champ, retirables. */
    _pendingCodes() {
      var self = this;
      var holder = this.root.querySelector('[data-nkc-chips]');
      var cart = this._cart;
      if (!holder || !cart || !cart.discount_codes) return;
      var strings = this._config.strings;
      holder.querySelectorAll('.nkc-chip.is-pending').forEach(function (node) { node.remove(); }); // rejouable sur le même rendu (F2)
      cart.discount_codes.forEach(function (entry) {
        if (entry.applicable) return;
        var code = String(entry.code).toUpperCase();
        var known = Array.prototype.some.call(holder.querySelectorAll('[data-nkc-chip]'), function (node) { return node.getAttribute('data-nkc-chip') === code; });
        if (known) return;
        var chip = document.createElement('span');
        chip.className = 'nkc-chip is-pending';
        chip.setAttribute('data-nkc-chip', code);
        chip.title = strings.codePendingTitle || '';
        // Tour 5 du jury (UX, décision 248) : une puce sans effet ne ressemble plus à une puce active : data-inert (bord pointillé,
        // estompée) et, pour le code de l'offre, la condition écrite : « GLOW · dès 3 pièces ».
        chip.setAttribute('data-inert', '');
        var label = document.createElement('span');
        label.setAttribute('data-nkc-chip-code', '');
        var bxgyInert = self._config && self._config.bxgy;
        if (bxgyInert && strings.chipInert && String(bxgyInert.code || '').toUpperCase() === code && bxgyInert.min > 1) label.textContent = strings.chipInert.replace('[code]', code).replace('[min]', bxgyInert.min);
        else label.textContent = code;
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'nkc-chip__x';
        button.setAttribute('data-nkc-code-remove', entry.code);
        button.setAttribute('aria-label', (strings.codeRemove || '').replace('[code]', code));
        button.innerHTML = '<span class="nkc-chip__xt" aria-hidden="true"></span><svg aria-hidden="true" focusable="false" width="8" height="8" viewBox="0 0 14 14"><path d="M1 1l12 12M13 1L1 13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
        button.firstChild.textContent = strings.codeDrop || ''; // clarté (07/10) : « Retirer » en toutes lettres, comme la puce du Liquid
        chip.appendChild(label);
        chip.appendChild(button);
        holder.appendChild(chip);
      });
      // Récap 3 (09/10, décision 303) : plus de bouton « + » ; la rangée « Ajouter un code promo » reste telle quelle à côté d'une puce sans effet.
    }

    /* ---------- Recommandations ---------- */

    _loadReco() {
      var self = this;
      var box = this.root.querySelector('[data-nkc-reco]');
      if (!box) return;
      var productId = box.getAttribute('data-product-id');
      var cached = this._recoCache[productId];
      if (typeof cached === 'string') { this._showReco(box, cached); return; }
      if (cached) return; // requête en cours
      this._recoCache[productId] = root.fetch(box.getAttribute('data-url'), { credentials: 'same-origin' })
        .then(function (res) { return res.ok ? res.text() : ''; })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cards = doc.querySelector('[data-nkc-reco-cards]');
          self._recoCache[productId] = cards ? cards.innerHTML : '';
          var current = self.root.querySelector('[data-nkc-reco]');
          if (current && current.getAttribute('data-product-id') === productId) self._showReco(current, self._recoCache[productId]);
        })
        .catch(function () { self._recoCache[productId] = ''; });
    }

    /**
     * Cartes de recommandation. Articles du panier retirés ; aucune taille présélectionnée, sauf celle du panier quand toutes
     * les lignes payées portent la même (cro-ARBITRAGE n° 2). Écart au palier connu (data-gap, centimes) : les cartes dont le
     * prix couvre l'écart passent en tête, de la moins chère à la plus chère, le titre devient « Pour atteindre votre cadeau »
     * (n° 12, partout) ; la moins chère d'entre elles devient la carte « combler l'écart » (levier 1, hors France) et quitte la
     * piste. Sans écart : les cartes venues de la collection de repli (data-gap-card) sont retirées. Aucun montant écrit ici.
     */
    _showReco(box, html) {
      var self = this;
      var track = box.querySelector('[data-nkc-reco-track]');
      track.innerHTML = html;
      box.removeAttribute('data-loading');
      var inCart = {};
      var cartModels = {};
      // Mots trop communs pour désigner un modèle (« Body … », « Coffret … ») : jamais pris pour un nom de modèle.
      var COMMON = { body: 1, coffret: 1, ensemble: 1, set: 1, nuisette: 1, kit: 1, pack: 1, the: 1, les: 1 };
      var modelOf = function (text) {
        var word = String(text || '').trim().toLowerCase().split(/\s+/)[0] || '';
        return word.length >= 3 && !COMMON[word] ? word : '';
      };
      // data-opt = tailles de la ligne (« S », ou « S/M » pour un ensemble) : une seule taille distincte dans tout le
      // panier → reprise dans chaque sélecteur de taille ; deux ou plus → aucune présélection (décisions 93 et 121).
      this.root.querySelectorAll('[data-nkc-line]').forEach(function (line) {
        inCart[line.getAttribute('data-pid')] = true;
        if (line.hasAttribute('data-gift-tier')) return;
        var model = modelOf(line.getAttribute('data-title'));
        if (model) cartModels[model] = true;
      });
      var cartSize = this._cartSize();
      // Modèles des cadeaux de palier (« Harnais BELLORE » → bellore) : jamais proposés à l'achat pour débloquer… le même.
      var giftWords = [];
      ((this._config && this._config.tiers) || []).forEach(function (tier) {
        (String(tier.name || '').match(/[A-ZÀ-Ý]{3,}/g) || []).forEach(function (word) { giftWords.push(word.toLowerCase()); });
      });
      var isGiftModel = function (card) {
        var words = String(card.getAttribute('data-full') || card.getAttribute('data-model') || '').split(/[^a-zà-ÿ0-9]+/);
        return giftWords.some(function (word) { return words.indexOf(word) > -1; });
      };
      var gap = box.hasAttribute('data-gap') ? Number(box.getAttribute('data-gap')) : null;
      // Tour 5 du jury (clientes, décision 240) : la carte d'écart peut viser un autre écart que la rangée (écart après GLOW quand la
      // remise coûterait un cadeau acquis : data-gap du slot, rendu par le Liquid) ; la rangée et son titre gardent l'écart du palier suivant.
      var slot = this.root.querySelector('[data-nkc-filler]');
      var fgap = gap;
      if (slot && gap !== null && slot.getAttribute('data-gap') !== null && slot.getAttribute('data-gap') !== '') fgap = Number(slot.getAttribute('data-gap'));
      var cards = Array.prototype.slice.call(track.querySelectorAll('[data-nkc-reco-card]'));
      var fitting = [];
      var allCards = [];
      var gapAll = [];
      var spare = [];
      var seenModels = {};
      var byPrice = function (a, b) { return a.price - b.price; };
      // Prix admissible pour combler un écart : au moins l'écart, jamais plus de 50 % (ou 15 unités de devise) au-dessus (décision 170).
      var within = function (price, target) { return target !== null && price >= target && price <= Math.max(target * 1.5, target + 1500); };
      var cartModelOf = function (card) { var m = modelOf(card.getAttribute('data-model')); return !!(m && cartModels[m]); };
      cards.forEach(function (card) {
        if (inCart[card.getAttribute('data-pid')]) { card.remove(); return; }
        // Best-sellers en réserve : hors de la piste, repris seulement pour compléter une rangée trop courte.
        if (card.hasAttribute('data-spare-card')) { card.remove(); spare.push(card); return; }
        // Sans écart à combler : les cartes de la collection de repli ne servent qu'à compléter une rangée trop courte.
        if (gap === null && card.hasAttribute('data-gap-card')) { card.remove(); spare.push(card); return; }
        // Tour 4 du jury (CRO, clientes, décision 224) : les collections de repli (pièces à moins de 50 €, coffrets) sortent de la piste ;
        // seules celles qui comblent l'écart au juste prix reviennent : la moins chère en carte d'écart, deux au plus en tête de rangée.
        if (card.hasAttribute('data-gap-card')) {
          card.remove();
          // Tour 6 du jury (UX) : les pièces de repli qui ne comblent pas l'écart restent en réserve pour compléter une rangée trop courte.
          if (!isGiftModel(card) && !cartModelOf(card)) gapAll.push({ card: card, price: Number(card.getAttribute('data-price')) });
          return;
        }
        // Au plus UNE autre couleur d'un modèle déjà dans le panier (tour 1 du jury, décision 170).
        var model = modelOf(card.getAttribute('data-model'));
        if (model && cartModels[model]) {
          if (seenModels[model]) { card.remove(); return; }
          seenModels[model] = true;
        }
        var select = card.querySelector('select[data-nkc-reco-variant]');
        var multi = card.querySelectorAll('select[data-nkc-reco-opt]');
        // Taille déjà choisie par la cliente sur cette carte : reprise si l'option existe encore ; sinon la taille du panier.
        var pick = self._recoPicks[card.getAttribute('data-pid')];
        var has = function (box, value) { return Array.prototype.some.call(box.options, function (o) { return o.value === value; }); };
        if (multi.length) {
          // Ensemble : un choix par option (mémoire par option), la taille du panier dans les sélecteurs de TAILLE seulement.
          var picks = pick && typeof pick === 'object' ? pick : {};
          Array.prototype.forEach.call(multi, function (s) {
            var want = picks[s.getAttribute('data-nkc-reco-opt')] || (s.hasAttribute('data-nkc-reco-size') ? cartSize : null);
            if (want && has(s, want)) s.value = want;
          });
          self._recoResolve(card);
        } else if (select) {
          var byText = function (text) { return Array.prototype.filter.call(select.options, function (o) { return o.value && o.textContent.trim() === text; })[0]; };
          if (typeof pick === 'string' && pick && has(select, pick)) select.value = pick;
          else if (cartSize && byText(cartSize)) select.value = byText(cartSize).value;
        }
        var price = Number(card.getAttribute('data-price'));
        if (gap !== null && price >= gap) fitting.push({ card: card, price: price });
        allCards.push({ card: card, price: price });
      });
      var title = box.querySelector('[data-nkc-reco-title]');
      if (title) title.textContent = title.getAttribute('data-title') || title.textContent;
      fitting.sort(function (a, b) { return a.price - b.price; });
      // Carte « combler l'écart » : la moins chère qui couvre l'écart, sortie de la piste, texte du Liquid dessous.
      // Tour 1 du jury (décision 170) : jamais le modèle d'un cadeau de palier (acheter un « Body Gia » pour recevoir le
      // body GIA), jamais un modèle déjà dans le panier, jamais une pièce très au-dessus de l'écart (plus de 50 % ou de
      // 15 unités de la devise au-delà) ; aucune pièce éligible : pas de carte.
      // Tour 4 du jury (décision 224) : les recommandations d'abord, puis les collections de repli (gapPool) ; les pièces de repli
      // qui restent (deux au plus, les moins chères) ouvrent la rangée, pour que le titre « Complétez pour… » soit tenu.
      var fitFor = function (target) { return target === null ? [] : allCards.filter(function (entry) { return within(entry.price, target) && !isGiftModel(entry.card) && !cartModelOf(entry.card); }); };
      var gapFor = function (target) { return target === null ? [] : gapAll.filter(function (entry) { return within(entry.price, target); }); };
      var pool = fitFor(fgap).concat(gapFor(fgap)).sort(byPrice);
      var fillerGap = fgap;
      // Tour 6 du jury (CRO, majeur) : à N pièces sans code, l'écart APRÈS GLOW (data-keep, décision 240) peut ne laisser aucune candidate
      // (20 € d'écart, pièce la moins chère 40 €) : la carte retombe sur l'écart du palier (« Avec cette pièce : Body GIA (60€) en cadeau »,
      // textes data-text-tier*), data-keep retiré. Puis, toujours sans candidate (écart sous ~27 € après GLOW) : la pièce la moins chère
      // au-dessus de l'écart est retenue si son prix ne dépasse pas la valeur du cadeau visé (40 € pour 59,90 € de cadeau : gain net).
      if (slot && !pool.length && gap !== null && fgap !== gap) {
        pool = fitFor(gap).concat(gapFor(gap)).sort(byPrice);
        fillerGap = gap;
        if (pool.length) {
          ['', '-short', '-set', '-set-short'].forEach(function (suffix) {
            var tierText = slot.getAttribute('data-text-tier' + suffix);
            if (tierText !== null) slot.setAttribute('data-text' + suffix, tierText);
          });
          // Décision 299 : la ligne 1 du palier revient aussi (« WILD Noir : les 60€ qui manquent », data-title-tier).
          var tierTitle = slot.getAttribute('data-title-tier');
          if (tierTitle !== null) slot.setAttribute('data-title', tierTitle);
          slot.removeAttribute('data-keep');
        }
      }
      if (slot && !pool.length && fillerGap !== null) {
        var worthAttr = slot.hasAttribute('data-keep') && fillerGap === fgap ? 'data-keep-worth' : 'data-worth';
        var worth = Number(slot.getAttribute(worthAttr)) || 0;
        var cheapest = allCards.filter(function (entry) { return entry.price >= fillerGap && !isGiftModel(entry.card) && !cartModelOf(entry.card); })
          .concat(gapAll.filter(function (entry) { return entry.price >= fillerGap; })).sort(byPrice)[0];
        if (cheapest && worth > 0 && cheapest.price <= worth) pool = [cheapest];
      }
      // La rangée et son titre visent l'écart du palier : deux pièces de repli au juste prix au plus (décision 224).
      var gapPool = gapFor(gap);
      if (slot) {
        slot.innerHTML = '';
        slot.removeAttribute('data-loading');
        var best = pool.length ? pool[0].card : null;
        gapPool = gapPool.filter(function (entry) { return entry.card !== best; });
        if (best) {
          var kicker = best.querySelector('[data-nkc-filler-text]');
          // Tour 5 du jury (clientes, décision 241) : devant un coffret (data-set), « Avec ce coffret : … » (data-text-set).
          var kickerText = (best.hasAttribute('data-set') && slot.getAttribute('data-text-set')) || slot.getAttribute('data-text') || '';
          if (kicker) { kicker.textContent = kickerText; kicker.hidden = !kicker.textContent; }
          // Carte d'écart v2 (09/10, décision 299) : ligne 1 = data-title du slot, [model] = nom de la carte (« WILD Noir : les 60€ qui manquent ») ;
          // le nom est mémorisé (data-nkc-model) pour que _fitFiller reste idempotent (repli 12 px, puis « WILD Noir · 60,00€ »).
          var bestTitle = best.querySelector('.nkc-rc__title');
          if (bestTitle && !best.dataset.nkcModel) best.dataset.nkcModel = (bestTitle.textContent || '').trim() || best.getAttribute('data-model') || '';
          var titleTpl = slot.getAttribute('data-title');
          if (bestTitle && titleTpl && best.dataset.nkcModel) bestTitle.textContent = titleTpl.replace('[model]', best.dataset.nkcModel);
          // Itération 4 : le bouton de la carte d'écart porte le prix rendu par le Liquid (« Ajouter · 50,00€ »).
          var fillerBtn = best.querySelector('[data-nkc-reco-add]');
          var fillerPrice = best.querySelector('.nkc-rc__amount > span');
          if (fillerBtn && fillerPrice && !fillerBtn.hasAttribute('data-priced')) {
            var priced = (fillerBtn.getAttribute('data-label') || fillerBtn.textContent || '').trim() + ' · ' + (fillerPrice.textContent || '').trim();
            fillerBtn.setAttribute('data-priced', '');
            fillerBtn.setAttribute('data-label', priced);
            if (!fillerBtn.disabled) fillerBtn.textContent = priced;
          }
          var fillerImg = best.querySelector('img');
          if (fillerImg) { fillerImg.setAttribute('loading', 'eager'); }
          slot.appendChild(best);
          slot.hidden = false;
          this._sizeNote(best);
          this._fitKicker(slot, kicker);
          this._fitFiller();
        } else slot.hidden = true;
      }
      // En tête de rangée, de la moins chère à la plus chère : les recommandations qui couvrent l'écart et, au juste prix, deux pièces
      // de repli au plus (hors carte d'écart) ; la carte de l'écart n'est jamais comptée deux fois.
      var front = fitting.filter(function (entry) { return !best || entry.card !== best; }).concat(gapPool.slice(0, 2)).sort(function (a, b) { return a.price - b.price; });
      front.forEach(function (entry) { entry.card.setAttribute('data-gap-first', ''); });
      for (var f = front.length - 1; f >= 0; f -= 1) track.insertBefore(front[f].card, track.firstChild);
      gapAll.forEach(function (entry) {
        if (entry.card === best || entry.card.isConnected) return;
        if (front.some(function (one) { return one.card === entry.card; })) return;
        spare.push(entry.card);
      });
      // Rangée trop courte (moins de trois cartes) : complétée par les best-sellers de la collection de repli.
      while (track.children.length < 3 && spare.length) {
        var extra = spare.shift();
        var extraModel = modelOf(extra.getAttribute('data-model'));
        if (extraModel && cartModels[extraModel]) continue;
        track.appendChild(extra);
      }
      // Tour 5 du jury (quatre juges, décision 238) : la piste s'ouvre sur sa PREMIÈRE carte. Les insertions en tête (insertBefore) et les
      // retraits sur une piste en scroll-snap laissaient la position de défilement sur l'ancienne première carte (scrollLeft 233-933
      // mesurés : lamelles coupées des deux côtés, pièces les moins chères hors écran, flèche « suivant » grisée dès l'ouverture sur de.).
      track.scrollLeft = 0;
      this._fitRecoTitles(track);
      // Titre de la rangée (tour 4 du jury, CRO et clientes, décision 225 ; révise 205) : avec une carte d'écart, « Ou choisissez une
      // autre pièce » ; sinon « Complétez pour le body GIA » seulement si une carte de la rangée couvre l'écart à elle seule, et
      // « Plus que 85€ pour le body GIA » (montant rendu par le Liquid) quand aucune ne le couvre : jamais une promesse qu'une pièce ne tient pas.
      if (title && gap !== null && gap > 0) {
        var fillerShown = !!(slot && !slot.hidden && slot.querySelector('[data-nkc-reco-card]'));
        var covers = Array.prototype.some.call(track.children, function (card) { return Number(card.getAttribute('data-price')) >= gap; });
        var gapTitle = covers ? box.getAttribute('data-title-gap') : (box.getAttribute('data-title-gap-amount') || box.getAttribute('data-title-gap'));
        if (fillerShown) title.textContent = box.getAttribute('data-title-or') || title.getAttribute('data-title') || title.textContent;
        else if (gapTitle) title.textContent = gapTitle;
      }
      Array.prototype.slice.call(track.children).slice(0, 3).forEach(function (card) {
        var img = card.querySelector('img');
        if (img) img.setAttribute('loading', 'eager');
      });
      track.setAttribute('data-count', String(track.children.length));
      box.hidden = track.children.length === 0;
      this._arrows(box);
      if (!track._nkcArrows) {
        track._nkcArrows = true;
        track.addEventListener('scroll', function () { self._arrows(box); }, { passive: true });
      }
    }

    /** Flèches du carrousel : éteintes en bout de piste (aria-disabled, jamais disabled : le focus clavier resterait). */
    _arrows(box) {
      var track = box.querySelector('[data-nkc-reco-track]');
      var prev = box.querySelector('[data-nkc-slide="-1"]');
      var next = box.querySelector('[data-nkc-slide="1"]');
      if (!track || !prev || !next) return;
      // Rien à faire défiler (trois cartes ou moins) : pas de flèches grisées inutiles.
      var still = track.clientWidth > 0 && track.scrollWidth <= track.clientWidth + 1;
      prev.hidden = still;
      next.hidden = still;
      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      next.setAttribute('aria-disabled', atEnd && track.scrollWidth > 0 && track.clientWidth > 0 ? 'true' : 'false');
    }

    /* ---------------------------------------------------------------- */
    /* 6. Minuteur, confettis                                             */
    /* ---------------------------------------------------------------- */

    _dailyPaint() {
      var config = this._config;
      var enabled = !!(config && config.daily !== false && config.country && config.country !== 'FR' && config.cart && config.cart.item_count > 0);
      var value = hms(untilMidnight());
      var roots = [this.root, this._upEl].filter(Boolean);
      roots.forEach(function (scope) {
        scope.querySelectorAll('[data-nkc-daily-row]').forEach(function (row) { row.hidden = !enabled; });
        if (enabled) scope.querySelectorAll('[data-nkc-daily-time]').forEach(function (node) { node.textContent = value; });
      });
    }

    _dailyTimer() {
      clearInterval(this._dailyTick);
      this._dailyPaint();
      var config = this._config;
      if (!config || config.daily === false || !config.country || config.country === 'FR' || !config.cart || !config.cart.item_count) return;
      var self = this;
      this._dailyTick = setInterval(function () { self._dailyPaint(); }, 1000);
    }

    _timer() {
      this._dailyTimer();
      var self = this;
      clearInterval(this._tick);
      var node = this.root.querySelector('[data-nkc-timer]');
      var config = this._config;
      if (!node || !config || !config.timer.enabled) {
        // Panier vidé : le prochain panier repart avec un minuteur neuf.
        if (config && config.cart && config.cart.item_count === 0 && store.get('timer', null)) store.set('timer', null);
        return;
      }
      // Commun aux onglets (lié au jeton du panier). Après six heures (ancienne visite), le bandeau est masqué
      // (décision 83) : il ne repart que pour un panier vidé puis rempli.
      var start = store.get('timer', null);
      if (start && Date.now() - start > TIMER_RESET) { node.hidden = true; return; }
      if (!start) { start = Date.now(); store.set('timer', start); }
      this._timerStart = start; // la popup lit la MÊME échéance que ce bandeau (R2 : une seule horloge)
      var total = config.timer.minutes * 60;
      var running = node.querySelector('[data-nkc-timer-run]');
      var done = node.querySelector('[data-nkc-timer-done]');
      var display = node.querySelector('[data-nkc-time]');
      var paint = function () {
        var left = total - Math.floor((Date.now() - start) / 1000);
        if (left <= 0) {
          if (self._upTimerEnd) self._upTimerEnd(); // popup avant paiement : même échéance, même seconde (R2)
          running.hidden = true;
          done.hidden = false;
          node.setAttribute('data-done', '');
          self._fit(done, 10.5);
          clearInterval(self._tick);
          if (!self._timerFired) { self._timerFired = true; fire('timer_expired'); } // mesure : une fois par page
          // Tour 6 du jury (clientes) : « Temps écoulé · Validez vite… » ne reste que 20 s ; ensuite un libellé neutre et vrai
          // (« Panier conservé · Validez pour garder votre taille », clé timer_after) : le panier n'a pas bougé, le texte le dit.
          var after = node.querySelector('[data-nkc-timer-after]');
          var showAfter = function () {
            if (!after || !node.isConnected) return;
            done.hidden = true;
            after.hidden = false;
            node.setAttribute('data-after', '');
            self._fit(after, 10.5);
          };
          clearTimeout(self._afterTimer);
          if (left <= -20) showAfter(); else self._afterTimer = setTimeout(showAfter, (20 + left) * 1000);
          return;
        }
        var minutes = Math.floor(left / 60);
        var seconds = left % 60;
        display.textContent = (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
        // Une seule horloge (R2 de la relecture 2) : le compteur de la popup reçoit le même texte, au même instant.
        if (self._upEl && !self._upEl.hidden) self._upEl.querySelectorAll('[data-nkc-up-timer]').forEach(function (n) { n.textContent = display.textContent; });
      };
      paint();
      node.hidden = false;
      this._tick = setInterval(paint, 1000);
    }

    _confetti() {
      var canvas = this.querySelector('[data-nkc-confetti]');
      if (!canvas || !canvas.getContext) return;
      if (root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      var box = (this.mode === 'page' ? this : this.panel).getBoundingClientRect();
      var ratio = Math.min(root.devicePixelRatio || 1, 2);
      var width = this.mode === 'page' ? root.innerWidth : box.width;
      var height = this.mode === 'page' ? root.innerHeight : Math.min(box.height, root.innerHeight);
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.style.display = 'block';
      var ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(ratio, ratio);
      var colors = ['#1a1a1a', '#c5a880', '#e6cfc8', '#8d6f4c', '#f0e6d8'];
      var pieces = [];
      for (var i = 0; i < 90; i += 1) {
        pieces.push({
          x: Math.random() * width,
          y: -20 - Math.random() * height * 0.4,
          w: 5 + Math.random() * 5,
          h: 8 + Math.random() * 6,
          vx: -1 + Math.random() * 2,
          vy: 2.2 + Math.random() * 3,
          spin: Math.random() * Math.PI,
          vs: -0.2 + Math.random() * 0.4,
          color: colors[i % colors.length]
        });
      }
      var started = performance.now();
      var duration = 2400;
      var frame = function (now) {
        var elapsed = now - started;
        ctx.clearRect(0, 0, width, height);
        ctx.globalAlpha = elapsed > duration - 500 ? Math.max(0, (duration - elapsed) / 500) : 1;
        pieces.forEach(function (piece) {
          piece.x += piece.vx;
          piece.y += piece.vy;
          piece.spin += piece.vs;
          ctx.save();
          ctx.translate(piece.x, piece.y);
          ctx.rotate(piece.spin);
          ctx.fillStyle = piece.color;
          ctx.fillRect(-piece.w / 2, -piece.h / 2, piece.w, piece.h * Math.abs(Math.cos(piece.spin)));
          ctx.restore();
        });
        if (elapsed < duration) requestAnimationFrame(frame);
        else { ctx.clearRect(0, 0, width, height); canvas.style.display = 'none'; }
      };
      requestAnimationFrame(frame);
    }
  }

  customElements.define('naked-cart', NakedCartElement);

  /* ------------------------------------------------------------------ */
  /* 4. Détection des changements de panier                               */
  /* ------------------------------------------------------------------ */

  function element() { return document.querySelector('naked-cart'); }

  var debounce = null;
  var lastNetwork = 0;

  /**
   * Regroupe les signaux. Un ajout du thème déclenche fetch, puis variant:add et cart:change :
   * la requête réseau suffit, les évènements qui la suivent ne relancent pas un rendu.
   */
  function signal(kind, fromNetwork) {
    var el = element();
    if (!el) return;
    if (fromNetwork) lastNetwork = Date.now();
    else if (Date.now() - lastNetwork < 2500) return;
    if (kind === 'add') {
      // Fin d'un ajout extérieur (réponse réseau reçue, ou évènement du thème sans réseau intercepté).
      if (fromNetwork) el._inflight = Math.max(0, (el._inflight || 0) - 1);
      // Le tiroir ne rouvre pas si la cliente l'a fermé pendant la requête ; le rafraîchissement a lieu quand même.
      if (!el._closedSince()) el.open();
      el.setAttribute('busy', '');
      el.ghost();
    }
    // Rechargement à venir (regroupé 60 ms) : le paiement attend qu'il ait décidé des cadeaux (1 200 ms au plus).
    el._refreshDue = true;
    clearTimeout(debounce);
    debounce = setTimeout(function () { el.refresh(); }, 60);
  }

  /**
   * Ouverture dès le départ d'une requête d'ajout (sans attendre la réponse). L'ajout en vol est compté
   * (_inflight) : une tâche de file qui finit entre-temps ne retire ni l'état de chargement ni le fantôme.
   */
  function openEarly() {
    var el = element();
    if (!el) return false;
    el._earlyAt = Date.now();
    el._inflight = (el._inflight || 0) + 1;
    el.setAttribute('busy', '');
    el.ghost();
    // Requête d'ajout sans réponse : l'état de chargement ne reste pas affiché sans fin.
    clearTimeout(el._earlyTimer);
    el._earlyTimer = setTimeout(function () { el._inflight = 0; if (!el._pending) el._idle(); }, timeout());
    var wasOpen = el.mode === 'page' || el.hasAttribute('open');
    el.open(); // déjà ouvert : remonte seulement la liste en haut (la ligne fantôme est visible)
    return !wasOpen;
  }

  /**
   * Ajout extérieur refusé (article épuisé, variante invalide, réseau) : le tiroir reste ouvert et DIT ce qui
   * s'est passé (message de Shopify, sinon le texte d'erreur), au lieu de se refermer en clignotant.
   */
  function failEarly(description) {
    var el = element();
    if (!el) return;
    el._inflight = Math.max(0, (el._inflight || 0) - 1);
    if (!el._pending) el._idle();
    el._notice(description || (el._config && el._config.strings.error));
  }

  /** Corps d'une réponse refusée : « description » de Shopify, lue sans consommer la réponse du thème. */
  function describe(res) {
    try {
      return res.clone().json().then(function (data) { return data && (data.description || data.message); }, function () { return null; });
    } catch (e) { return Promise.resolve(null); }
  }

  /** Une requête qui ne pose que des attributs (tests de prix, CRO) ne change rien à l'affichage. */
  function attributesOnly(body) {
    try {
      if (typeof body === 'string') return body.indexOf('attributes') > -1 && !/updates|discount|quantity|note|"id"|(^|&)id=/.test(body);
      if (body && typeof body.forEach === 'function' && typeof body.append === 'function') {
        var other = false;
        var seen = false;
        body.forEach(function (value, key) { seen = true; if (String(key).indexOf('attributes') !== 0) other = true; });
        return seen && !other;
      }
    } catch (e) { /* corps illisible : on recharge */ }
    return false;
  }

  function inspect(url, body) {
    if (!url || url.indexOf(SELF_MARK) > -1) return null;
    if (/[?&]nkc=silent(&|$)/.test(url)) return null; // opt-out public : un script qui ajoute puis navigue aussitôt
    var match = CART_RE.exec(url.split('#')[0]);
    if (!match) return null;
    var kind = match[1].toLowerCase();
    if (kind === 'update' && attributesOnly(body)) return null;
    return kind;
  }

  var nativeFetch = root.fetch;
  if (typeof nativeFetch === 'function') {
    root.fetch = function (input, init) {
      var kind = null;
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || String(input);
        kind = inspect(url, init && init.body);
      } catch (e) { kind = null; }
      var early = kind === 'add' ? { opened: openEarly() } : null;
      var promise = nativeFetch.apply(this, arguments);
      if (kind) {
        promise.then(function (res) {
          if (res && res.ok) signal(kind, true);
          else if (early) describe(res).then(failEarly);
        }, function () { if (early) failEarly(null); });
      }
      return promise;
    };
  }

  if (root.XMLHttpRequest) {
    var open = XMLHttpRequest.prototype.open;
    var send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._nkcUrl = String(url || '');
      return open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      var kind = null;
      try { kind = inspect(this._nkcUrl, body); } catch (e) { kind = null; }
      if (kind) {
        var early = kind === 'add' ? { opened: openEarly() } : null;
        this.addEventListener('loadend', function () {
          if (this.status >= 200 && this.status < 300) { signal(kind, true); return; }
          if (!early) return;
          var description = null;
          try { description = JSON.parse(this.responseText).description; } catch (e) { description = null; }
          failEarly(description);
        });
      }
      return send.apply(this, arguments);
    };
  }

  // Évènements du thème. Capture sur window : le tiroir et la fenêtre « ajouté » natifs restent fermés.
  root.addEventListener('variant:add', function (event) {
    if (!element()) return;
    if (event.detail) event.detail.blockCartDrawerOpening = true;
    signal('add');
  }, true);
  document.addEventListener('cart:change', function (event) {
    if (event.detail && event.detail.baseEvent === 'naked-cart') return;
    signal('change');
  });
  document.addEventListener('cart:refresh', function () { signal('change'); });

  // Icône panier et liens /cart : ouvrent le tiroir (sauf sur la page panier). L'en-tête du thème n'est
  // pas retouché : le clic est capté ici avant le gestionnaire natif (aria-controls="cart-drawer").
  document.addEventListener('click', function (event) {
    var el = element();
    if (!el || el.mode === 'page' || event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
    var link = event.target.closest && event.target.closest('[data-naked-cart-open], [aria-controls="cart-drawer"], a[href$="/cart"]');
    if (!link || el.contains(link) || link.hasAttribute('data-naked-cart-ignore')) return;
    event.preventDefault();
    event.stopPropagation();
    el.open();
  }, true);

  // Liens « /checkout » posés hors du panier (landings) : si une écriture est en vol (ajout du thème,
  // cadeau en route), le départ attend sa réponse, 1 200 ms au plus, comme le bouton de paiement.
  document.addEventListener('click', function (event) {
    var el = element();
    if (!el || event.defaultPrevented || !el._holding()) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    var link = event.target.closest && event.target.closest('a[href]');
    if (!link || el.contains(link) || !/\/checkout\/?(\?|#|$)/.test(link.getAttribute('href') || '')) return;
    event.preventDefault();
    var started = Date.now();
    var go = function () {
      if (el._holding() && Date.now() - started < Math.min(PAY_HOLD_MS, timeout())) { setTimeout(go, 30); return; }
      root.location.href = link.href;
    };
    go();
  }, true);

  // Bouton d'achat d'une fiche : retour visuel immédiat (aria-busy, que le thème sait dessiner) quand une
  // app retarde le clic (Candy Rack sur FR : 0,5 à 1,4 s de silence). Le thème le retire à la réponse ;
  // retrait de secours à l'ouverture du tiroir, en cas d'erreur, et au plus tard après 3 s.
  document.addEventListener('click', function (event) {
    var el = element();
    if (!el || !event.isTrusted) return;
    var button = event.target.closest && event.target.closest('form[action*="/cart/add"] [type="submit"]');
    if (!button || button.disabled || button.hasAttribute('aria-busy') || button.classList.contains('awaiting-size')) return;
    var form = button.closest('form');
    if (!form || (typeof form.checkValidity === 'function' && !form.checkValidity())) return;
    button.setAttribute('aria-busy', 'true');
    var release = function () { if (button.getAttribute('aria-busy') === 'true') button.removeAttribute('aria-busy'); };
    document.addEventListener('naked-cart:open', release, { once: true });
    document.addEventListener('cart:error', release, { once: true });
    setTimeout(release, 3000);
  }, true);

  // Focus sorti du tiroir (élément remplacé pendant un rendu) : Échap ferme, Tab y ramène.
  document.addEventListener('keydown', function (event) {
    // Popup avant paiement ouverte (tiroir ou page /cart), focus parti dehors (page, corps du document) : Tab revient dedans (QA B8).
    var popped = Array.prototype.filter.call(document.querySelectorAll('naked-cart'), function (cart) { return cart._upEl && !cart._upEl.hidden; })[0];
    if (popped && event.key === 'Tab' && !popped.contains(event.target)) { popped._onKeydown(event); return; }
    var el = element();
    if (!el || el.mode === 'page' || !el.hasAttribute('open') || el.contains(event.target)) return;
    if (event.key === 'Escape') el.close(true);
    if (event.key === 'Tab') {
      event.preventDefault();
      (el.querySelector('[data-nkc-close]') || el.panel).focus();
    }
  });

  // Navigation (clic sur un titre, paiement, retour arrière) : le tiroir est fermé avant que la page parte,
  // sinon le cache de retour (bfcache) restitue une page avec le tiroir ouvert et le défilement verrouillé.
  root.addEventListener('pagehide', function () {
    var el = element();
    if (el) el.close(false);
    document.querySelectorAll('naked-cart').forEach(function (cart) { if (cart._upReset) cart._upReset(); }); // popup avant paiement
  });
  // Retour par l'historique du navigateur, ou retour sur l'onglet : l'état affiché peut être périmé
  // (panier modifié dans un autre onglet).
  root.addEventListener('pageshow', function (event) {
    var el = element();
    if (!event.persisted || !el) return;
    document.querySelectorAll('naked-cart').forEach(function (cart) {
      if (cart._upReset) cart._upReset();
      cart._payWaiting = false;
      cart._payNow = false;
      if (cart._payLabel) cart._payLabel(false);
    });
    el._payWaiting = false;
    el._payNow = false;
    el._payLabel(false); // retour depuis la page de paiement (bfcache) : le bouton reprend son libellé
    if (el.hasAttribute('open')) el.close(false); // secours si pagehide n'a pas joué
    el.refresh();
  });
  document.addEventListener('visibilitychange', function () {
    var el = element();
    if (!el || document.visibilityState !== 'visible') return;
    if (Date.now() - el._lastSync < 3000 || el._pending || el._inflight) return;
    // La cliente est en train de taper un code (copié depuis un SMS, un mail) : la relecture attend qu'elle
    // quitte le champ, elle ne lui efface pas sa saisie.
    var active = document.activeElement;
    if (active && el.contains(active) && active.matches('[data-nkc-code-form] input') && active.value) {
      if (!active._nkcBlur) {
        active._nkcBlur = true;
        active.addEventListener('blur', function () { el._lastSync = Date.now(); el.refresh(); }, { once: true });
      }
      return;
    }
    el._lastSync = Date.now();
    el.refresh();
  });

  /* ------------------------------------------------------------------ */
  /* 7. Compatibilité                                                     */
  /* ------------------------------------------------------------------ */

  root.NakedCart = {
    open: function () { var el = element(); if (el) el.open(); },
    close: function () { var el = element(); if (el) el.close(true); },
    refresh: function () { var el = element(); return el ? el.refresh() : Promise.resolve(); },
    getCart: function () { var el = element(); return el ? el.getCart() : null; },
    on: function (name, handler) { document.addEventListener('naked-cart:' + name, handler); }
  };
})(typeof globalThis !== 'undefined' ? globalThis : this);
